<script setup lang="ts">
import { ref, computed, onMounted, watch, watchEffect, nextTick, type ComputedRef, type Ref } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useDateField, formatDocDate } from '@/shared/composables/dateUtils';
import { formatDateToDDMMYYYY } from '@/shared/composables/useDatePicker';
import { useAddressFormatter } from '@/shared/composables/useAddressFormatter';
import { useOfficerAppointmentStore } from '../stores/officerAppointmentStore';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { entityApi } from '@/features/Entity/api/entityApi';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import { apiClient } from '@/shared/api/client';
import { getOfficersByEntity } from '../api/officerAppointmentApi';
import { getControllersByEntity, getControllerDetail } from '../api/controllerCessationApi';
import ContactListPage from '@/features/Contact/views/ContactListPage.vue';
import ContactDetailPage from '@/features/Contact/views/ContactDetailPage.vue';
import TaskFlowSection from './TaskFlowSection.vue';
import type { TaskConfig } from '../api/taskConfigApi';

// Props 상속 설정 (여러 루트 노드에서 props 상속 경고 방지)
defineOptions({
  inheritAttrs: false
});

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// 주소 포맷팅 함수
const { formatAddress } = useAddressFormatter();

// Props: CreateTaskForm에서 선택한 taskConfig 전달받기
interface Props {
  taskConfig?: TaskConfig | null;
  entityId?: string;
  initialData?: any | null; // E-Filing 페이지에서 TASK_DATA 전달용
  readOnly?: boolean; // 읽기 전용 모드 (E-Filing 검증용)
  showTaskFlow?: boolean; // Task Flow 섹션 표시 여부 (E-Filing에서는 false)
  eFilingMode?: boolean; // E-Filing 모드
  validationErrors?: Record<string, string>; // 필드별 validation 에러 메시지
}

const props = withDefaults(defineProps<Props>(), {
  taskConfig: null,
  entityId: undefined,
  initialData: null,
  readOnly: false,
  showTaskFlow: true,
  eFilingMode: false,
  validationErrors: () => ({})
});

// Store
const officerAppointmentStore = useOfficerAppointmentStore();
const sessionStore = useSessionStore();

// Emits
const emit = defineEmits<{
  'auto-select-templates': [config: { categoryName: string; templateIds: string[] }],
  'filter-select-templates': [config: { categoryName: string; templateIds: string[]; filterValue: string }],
  'reset-templates': [],
  'update:entityId': [value: string | undefined],
  'update:taskFlow': [value: { all: boolean; createDocument: boolean; requestTSign: boolean; proceedWithEFiling: boolean }]
}>();

// 공통 날짜 필드 composable 사용 (초기값: 오늘 날짜)
const {
  dateValue: transactionDateValue,
  dateDisplay: transactionDate,
  datePickerValue: transactionDatePickerValue,
  datePlaceholder,
  datePickerLocale,
  firstDayOfWeek,
  dateMenu: transactionDateMenu
} = useDateField(new Date().toISOString().slice(0, 10));

// Shareholder Transfer Section 인터페이스 (From & To 쌍)
interface ShareholderTransferSection {
  id: string;
  // From (Transferor) Section
  fromShareholder: string;
  fromNumberOfShares: string;
  fromPaidUpShareCapital: string;
  fromShareholderID: string;
  fromShareholderAddress: string;
  // To (Transferee) Section
  toShareholder: string;
  toNumberOfShares: string;
  toIssuedShareCapital: string;
  toPaidUpShareCapital: string;
  totalConsiderationPaid: string;
  toShareholderID: string;
  toShareholderAddress: string;
  toShareholderNationality: string;
  toShareholderBirth: string;
  // Certificate Details (섹션별 Transferor / Transferee)
  fromShareholderCertType: string;
  fromShareholderCertNo: string;
  toShareholderCertType: string;
  toShareholderCertNo: string;
  // 공통 필드
  shareType: string;
  currency: string;
  // Trust 관련 필드
  showTrustSection: boolean;
  sharesHeldInTrust: boolean | null;
  trustName: string;
  // Subclass 관련 필드
  hasSubClass: boolean;
  subClassErrorMessage: string;
  // 에러 메시지
  toNumberOfSharesError: string;
  // ENTERED MEMBER 필드
  enteredMember: string;
}

const createEmptyTransferSection = (id: string): ShareholderTransferSection => ({
  id,
  fromShareholder: '',
  fromNumberOfShares: '',
  fromPaidUpShareCapital: '',
  fromShareholderID: '',
  fromShareholderAddress: '',
  toShareholder: '',
  toNumberOfShares: '',
  toIssuedShareCapital: '',
  toPaidUpShareCapital: '',
  totalConsiderationPaid: '',
  toShareholderID: '',
  toShareholderAddress: '',
  toShareholderNationality: '',
  toShareholderBirth: '',
  fromShareholderCertType: '',
  fromShareholderCertNo: '',
  toShareholderCertType: '',
  toShareholderCertNo: '',
  shareType: '',
  currency: '',
  showTrustSection: false,
  sharesHeldInTrust: null,
  trustName: '',
  hasSubClass: false,
  subClassErrorMessage: '',
  toNumberOfSharesError: '',
  enteredMember: '',
});

// Form fields
const entity = ref('');

// Shareholder Transfer Sections (여러 Shareholder 지원)
const shareholderTransferSections = ref<ShareholderTransferSection[]>([
  createEmptyTransferSection('1'),
]);

// 기존 필드들 (첫 번째 섹션과 호환성을 위해 유지)
const fromShareholder = computed({
  get: () => shareholderTransferSections.value[0]?.fromShareholder || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].fromShareholder = val;
    }
  }
});
const fromNumberOfShares = computed({
  get: () => shareholderTransferSections.value[0]?.fromNumberOfShares || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].fromNumberOfShares = val;
    }
  }
});
const fromPaidUpShareCapital = computed({
  get: () => shareholderTransferSections.value[0]?.fromPaidUpShareCapital || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].fromPaidUpShareCapital = val;
    }
  }
});
const fromShareholderID = computed({
  get: () => shareholderTransferSections.value[0]?.fromShareholderID || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].fromShareholderID = val;
    }
  }
});
const fromShareholderAddress = computed({
  get: () => shareholderTransferSections.value[0]?.fromShareholderAddress || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].fromShareholderAddress = val;
    }
  }
});

const toShareholder = computed({
  get: () => shareholderTransferSections.value[0]?.toShareholder || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].toShareholder = val;
    }
  }
});
const toNumberOfShares = computed({
  get: () => shareholderTransferSections.value[0]?.toNumberOfShares || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].toNumberOfShares = val;
    }
  }
});
const toIssuedShareCapital = computed({
  get: () => shareholderTransferSections.value[0]?.toIssuedShareCapital || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].toIssuedShareCapital = val;
    }
  }
});
const toPaidUpShareCapital = computed({
  get: () => shareholderTransferSections.value[0]?.toPaidUpShareCapital || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].toPaidUpShareCapital = val;
    }
  }
});
const totalConsiderationPaid = computed({
  get: () => shareholderTransferSections.value[0]?.totalConsiderationPaid || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].totalConsiderationPaid = val;
    }
  }
});
const toShareholderID = computed({
  get: () => shareholderTransferSections.value[0]?.toShareholderID || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].toShareholderID = val;
    }
  }
});
const toShareholderAddress = computed({
  get: () => shareholderTransferSections.value[0]?.toShareholderAddress || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].toShareholderAddress = val;
    }
  }
});
const toShareholderNationality = computed({
  get: () => shareholderTransferSections.value[0]?.toShareholderNationality || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].toShareholderNationality = val;
    }
  }
});
const toShareholderBirth = computed({
  get: () => shareholderTransferSections.value[0]?.toShareholderBirth || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].toShareholderBirth = val;
    }
  }
});

const shareType = computed({
  get: () => shareholderTransferSections.value[0]?.shareType || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].shareType = val;
    }
  }
});

const currency = computed({
  get: () => shareholderTransferSections.value[0]?.currency || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].currency = val;
    }
  }
});

const showTrustSection = computed({
  get: () => shareholderTransferSections.value[0]?.showTrustSection || false,
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].showTrustSection = val;
    }
  }
});
const sharesHeldInTrust = computed({
  get: () => shareholderTransferSections.value[0]?.sharesHeldInTrust || null,
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].sharesHeldInTrust = val;
    }
  }
});
const trustName = computed({
  get: () => shareholderTransferSections.value[0]?.trustName || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].trustName = val;
    }
  }
});

const hasSubClass = computed({
  get: () => shareholderTransferSections.value[0]?.hasSubClass || false,
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].hasSubClass = val;
    }
  }
});
const subClassErrorMessage = computed({
  get: () => shareholderTransferSections.value[0]?.subClassErrorMessage || '',
  set: (val) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].subClassErrorMessage = val;
    }
  }
});

const isResettingToFields = ref(false); // 초기화 중 플래그
const isRestoringData = ref(false); // 데이터 복원 중 플래그 (Allotment와 동일)

// ========== Task Flow ==========
const taskFlow = ref({
  all: false,
  createDocument: false,
  requestTSign: false,
  proceedWithEFiling: false
});

const isCreateDocumentSupported = computed(() => {
  const steps = props.taskConfig?.processSteps;
  if (!steps || !Array.isArray(steps)) return false;
  return steps.some(s => s.toLowerCase().includes('doc'));
});

const isRequestTSignSupported = computed(() => {
  const steps = props.taskConfig?.processSteps;
  if (!steps || !Array.isArray(steps)) return false;
  return steps.some(s => s.toLowerCase().includes('sign'));
});

const isProceedWithEFilingSupported = computed(() => {
  const steps = props.taskConfig?.processSteps;
  if (!steps || !Array.isArray(steps)) return false;
  return steps.some(s => s.toLowerCase().includes('filing'));
});

const isAllSupported = computed(() =>
  isCreateDocumentSupported.value &&
  isRequestTSignSupported.value &&
  isProceedWithEFilingSupported.value
);

const updateTaskFlowState = () => {
  taskFlow.value.createDocument = isCreateDocumentSupported.value;
  taskFlow.value.requestTSign = isRequestTSignSupported.value;
  taskFlow.value.proceedWithEFiling = isProceedWithEFilingSupported.value;
  taskFlow.value.all = isAllSupported.value;
};

watch(() => props.taskConfig, updateTaskFlowState, { immediate: true });
watch(taskFlow, (v) => emit('update:taskFlow', { ...v }), { deep: true });

// 에러 메시지는 각 섹션 객체의 toNumberOfSharesError 속성에 직접 저장됨

// 각 섹션별 v-text-field ref 저장 (validation 강제 실행용)
const toNumberOfSharesFieldRefs = ref<Map<number, any>>(new Map());
const toShareholderFieldRefs = ref<Map<number, any>>(new Map());
const toIssuedShareCapitalFieldRefs = ref<Map<number, any>>(new Map());
const totalConsiderationPaidFieldRefs = ref<Map<number, any>>(new Map());
const toPaidUpShareCapitalFieldRefs = ref<Map<number, any>>(new Map());
const currencyFieldRefs = ref<Map<number, any>>(new Map());
const shareTypeFieldRefs = ref<Map<number, any>>(new Map());

// 섹션별 validation 에러 관리 (Currency, Share Type 등)
const sectionValidationErrors = ref<Map<number, Record<string, string>>>(new Map());

// Certificate Details — 첫 섹션 alias (레거시 watch / expose 호환). 실제 값은 섹션별 필드.
const transferorCertificateType = computed({
  get: () => shareholderTransferSections.value[0]?.fromShareholderCertType || '',
  set: (val: string) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].fromShareholderCertType = val ?? '';
    }
  },
});
const transferorCertificateNo = computed({
  get: () => shareholderTransferSections.value[0]?.fromShareholderCertNo || '',
  set: (val: string) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].fromShareholderCertNo = val ?? '';
    }
  },
});
const transfereeCertificateType = computed({
  get: () => shareholderTransferSections.value[0]?.toShareholderCertType || '',
  set: (val: string) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].toShareholderCertType = val ?? '';
    }
  },
});
const transfereeCertificateNo = computed({
  get: () => shareholderTransferSections.value[0]?.toShareholderCertNo || '',
  set: (val: string) => {
    if (shareholderTransferSections.value[0]) {
      shareholderTransferSections.value[0].toShareholderCertNo = val ?? '';
    }
  },
});
const fromShareCertificateNo = transferorCertificateNo;
const toShareCertificateNo = transfereeCertificateNo;

const getSectionTransferorLabel = (section: ShareholderTransferSection, index: number) => {
  const name =
    fromShareholderOptions.value.find((opt) => opt.value === section.fromShareholder)?.title ||
    section.fromShareholderID ||
    '';
  return name
    ? `Transfer #${index + 1} — Transferor (${name})`
    : `Transfer #${index + 1} — Transferor`;
};
const getSectionTransfereeLabel = (section: ShareholderTransferSection, index: number) => {
  const name =
    toShareholderOptions.value.find((opt) => opt.value === section.toShareholder)?.title ||
    section.toShareholderID ||
    '';
  return name
    ? `Transfer #${index + 1} — Transferee (${name})`
    : `Transfer #${index + 1} — Transferee`;
};

// EGM 정보
const egmPlace = ref('');
const egmDate = ref('');
const egmTime = ref('');

// RORC Information Setting 필드
const chairPerson = ref('');
const representativeDirector = ref('');
const rorc = ref('');
const representative = ref('');

// RORC 옵션들
const chairPersonOptions = ref<Array<{ title: string; value: string }>>([]);
const representativeDirectorOptions = ref<Array<{ title: string; value: string }>>([]);
const rorcOptions = ref<Array<{ title: string; value: string }>>([]);
const representativeOptions = ref<Array<{ title: string; value: string }>>([]);

// Contact 상세 화면 모달
const showContactDetailModal = ref(false);
const currentContactDetailType = ref<'from' | 'to' | null>(null);
const currentContactDetailIndex = ref<number>(0);

// Contact 목록 모달 상태
const showContactListModal = ref(false);
const contactListInitialSearch = ref('');
const contactListType = ref<'from' | 'to' | null>(null);

// Shareholder별 Contact 데이터 저장 (섹션별로 관리)
const shareholderTransferContactData = ref<Map<string, { from?: any; to?: any }>>(new Map());

// Contact 데이터 가져오기 함수
const getFromShareholderContactData = (shareholderIndex: number) => {
  const section = shareholderTransferSections.value[shareholderIndex];
  if (!section) return null;
  return shareholderTransferContactData.value.get(section.id)?.from || null;
};

const getToShareholderContactData = (shareholderIndex: number) => {
  const section = shareholderTransferSections.value[shareholderIndex];
  if (!section) return null;
  return shareholderTransferContactData.value.get(section.id)?.to || null;
};

// From Shareholder의 CONTACT_CD 가져오기 (섹션별)
const getFromShareholderContactCd = (shareholderIndex: number): string => {
  const section = shareholderTransferSections.value[shareholderIndex];
  if (!section || !section.fromShareholder) return '';
  
  // 1. Contact 데이터에서 먼저 확인
  const contactData = getFromShareholderContactData(shareholderIndex);
  if (contactData?.CONTACT_CD) {
    return contactData.CONTACT_CD;
  }
  
  // 2. 옵션에서 찾기
  const selectedOption = fromShareholderOptions.value.find(opt => opt.value === section.fromShareholder);
  if (selectedOption?.shareData?.contactCd) {
    return selectedOption.shareData.contactCd;
  }
  if (selectedOption?.contactCd) {
    return selectedOption.contactCd;
  }
  
  // 3. entitySharesData에서 찾기
  if (selectedOption?.shareData?.shareCapitalIdx && selectedOption?.shareData?.shareholderIdx) {
    const matchingEntityShare = entitySharesData.value.find(
      (es: any) => es.shareCapitalIdx === selectedOption.shareData.shareCapitalIdx && 
                   es.shareholderIdx === selectedOption.shareData.shareholderIdx
    );
    if (matchingEntityShare?.contactCd) {
      return matchingEntityShare.contactCd;
    }
  }
  
  return '';
};

// 기존 코드 호환성을 위해 (첫 번째 섹션용)
const fromShareholderContactData = computed(() => getFromShareholderContactData(0));
const toShareholderContactData = computed(() => getToShareholderContactData(0));

// BaseCode 옵션 데이터 (Contact 정보 표시용)
const identificationTypeOptions = ref<Array<{ title: string; value: string; bigo1?: string }>>([]);
const nationalityOptions = ref<Array<{ title: string; value: string; bigo1?: string }>>([]);
const countryOptions = ref<Array<{ title: string; value: string; bigo3?: string }>>([]);
const addressTypeOptions = ref<Array<{ title: string; value: string }>>([]);

// 아코디언 확장/축소 상태 (Shareholder 1, Shareholder 2, Certificate Details, RORC)
const allExpandedPanels = ref<string[]>([]);

// From Shareholder의 Contact CD 가져오기 (Entity Shares에서 contactCd 추출)
const fromShareholderContactCd = computed(() => {
  if (!fromShareholder.value) return '';
  
  // 1. fromShareholderContactData에서 먼저 확인
  if (fromShareholderContactData.value?.CONTACT_CD) {
    return fromShareholderContactData.value.CONTACT_CD;
  }
  
  // 2. shareData에서 확인
  const selectedOption = fromShareholderOptions.value.find(opt => opt.value === fromShareholder.value);
  if (selectedOption?.shareData?.contactCd) {
    return selectedOption.shareData.contactCd;
  }
  
  return '';
});

// Options
const fromShareholderOptions = ref<Array<{ title: string; value: string; contactCd?: string; shareData?: any }>>([]);
const toShareholderOptions = ref<Array<{ title: string; value: string }>>([]);
const originalToShareholderOptions = ref<Array<{ title: string; value: string }>>([]); // 전체 연락처 목록 (원본)
const allShareTypeOptions = ref<Array<{ title: string; value: string }>>([]); // 전체 Share Type 옵션 (BaseCode A20)
const certificateTypeOptions = ref<Array<{ title: string; value: string }>>([]);
const isLoadingShareholders = ref(false);

// Currency Options (computed - 선택된 From Shareholder의 모든 currency)
const currencyOptions = computed(() => {
  if (!fromShareholder.value) {
    return [];
  }
  
  // fromShareholderOptions가 배열이 아니면 빈 배열 반환
  if (!Array.isArray(fromShareholderOptions.value)) {
    return [];
  }
  
  const selectedOption = fromShareholderOptions.value.find(opt => opt.value === fromShareholder.value);
  if (!selectedOption?.shareData) {
    return [];
  }
  
  const shareData = selectedOption.shareData;
  const currencies = shareData.currencies || [];
  
  // currencies가 배열이 아니면 빈 배열 반환
  if (!Array.isArray(currencies)) {
    return [];
  }
  
  // currencies 배열에서 옵션 생성
  return currencies.map((curr: any) => ({
    title: curr.currency || curr.currencyCode || '',
    value: `${curr.currency || curr.currencyCode || ''}_${curr.shareCapitalIdx || 0}`,
    // BIGO3(통화 축약 코드)가 있으면 함께 저장 (fallback 용도)
    bigo3: curr.bigo3 || curr.BIGO3 || ''
  })).filter((opt: any) => opt.title); // 빈 값 제거
});

// Debug 로그 함수 (개발 환경에서만 실행)
const isDev = process.env.NODE_ENV === 'development';
const debugLog = isDev ? console.log : () => {};
const debugWarn = isDev ? console.warn : () => {};

// Share Type Options 캐시 (메모이제이션용)
let shareTypeOptionsCache: {
  key: string;
  value: Array<{ title: string; value: string }>;
} | null = null;

// Share Type Options (computed - 선택된 From Shareholder의 shares 값이 0이 아닌 것만)
// Share Type Options (computed - 선택된 From Shareholder의 share type, Currency와 동일한 패턴)
// 저장된 shareType이 항상 포함되도록 보장
const shareTypeOptions = computed(() => {
  // 1. 모든 의존성을 한 번만 읽기 (의존성 최적화)
  const sections = shareholderTransferSections.value;
  const allOptions = allShareTypeOptions.value;
  const fromOptions = fromShareholderOptions.value;
  const initialShareholders = props.initialData?.formData?.shareholders;
  
  // 2. Early return으로 불필요한 계산 방지
  if (!Array.isArray(allOptions) || allOptions.length === 0) {
    return [];
  }
  
  if (!Array.isArray(fromOptions)) {
    return [];
  }
  
  // 3. 캐시 키 생성 (모든 의존성 포함)
  const cacheKey = [
    sections.map(s => `${s.fromShareholder || ''}_${s.shareType || ''}`).join(','),
    allOptions.length,
    fromOptions.length,
    JSON.stringify(initialShareholders || [])
  ].join('|');
  
  // 4. 캐시 확인 (메모이제이션)
  if (shareTypeOptionsCache && shareTypeOptionsCache.key === cacheKey) {
    return shareTypeOptionsCache.value;
  }
  
  // 5. 저장된 shareType 값들 수집
  const savedShareTypes = new Set<string>();
  
  // sections에서 shareType 수집
  sections.forEach((section) => {
    if (section.shareType) {
      savedShareTypes.add(String(section.shareType).trim());
    }
  });
  
  // initialData에서 shareType 수집 (한 번만 체크)
  if (Array.isArray(initialShareholders)) {
    initialShareholders.forEach((shareholder: any) => {
      if (shareholder.shareType) {
        savedShareTypes.add(String(shareholder.shareType).trim());
      }
    });
  }
  
  // 6. 모든 섹션의 fromShareholder를 고려하여 필터링
  const allFilteredOptions = new Set<string>();
  
  // 각 섹션의 fromShareholder를 기준으로 필터링
  for (const section of sections) {
    if (!section.fromShareholder) continue;
    
    const selectedOption = fromOptions.find(opt => opt.value === section.fromShareholder);
    if (!selectedOption?.shareData) {
      continue;
    }
    
    const shareData = selectedOption.shareData;
    const sharesOrdinary = Number(shareData.sharesOrdinary || 0);
    const sharesPreference = Number(shareData.sharesPreference || 0);
    const sharesOthers = Number(shareData.sharesOthers || 0);
    
    // A20001 = Ordinary, A20002 = Preference, A20003 = Others
    allOptions.forEach(option => {
      const optValue = String(option.value).trim();
      let shouldInclude = false;
      
      if (optValue === 'A20001' || optValue === 'A20001000') {
        shouldInclude = sharesOrdinary > 0;
      } else if (optValue === 'A20002' || optValue === 'A20002000') {
        shouldInclude = sharesPreference > 0;
      } else if (optValue === 'A20003' || optValue === 'A20003000') {
        shouldInclude = sharesOthers > 0;
      } else {
        // 기타 옵션은 그대로 표시
        shouldInclude = true;
      }
      
      if (shouldInclude) {
        allFilteredOptions.add(optValue);
      }
    });
  }
  
  // 7. 필터링된 옵션 생성 (저장된 shareType도 포함)
  const filteredOptions = allOptions.filter(option => {
    const optValue = String(option.value).trim();
    return allFilteredOptions.has(optValue) || savedShareTypes.has(optValue);
  });
  
  // 8. 저장된 shareType이 필터링된 옵션에 없으면 추가 (맨 앞에)
  const finalOptions = [...filteredOptions];
  for (const savedShareType of savedShareTypes) {
    const exists = finalOptions.some(opt => String(opt.value).trim() === savedShareType);
    if (!exists) {
      const savedOption = allOptions.find(opt => String(opt.value).trim() === savedShareType);
      if (savedOption) {
        finalOptions.unshift(savedOption); // 맨 앞에 추가
      }
    }
  }
  
  // 9. 캐시 저장
  shareTypeOptionsCache = { key: cacheKey, value: finalOptions };
  
  return finalOptions;
});

// shareTypeOptions가 변경되고 section.shareType이 옵션에 있으면 강제로 재설정
watch(shareTypeOptions, async (newOptions, oldOptions) => {
  // 데이터 복원 중이면 건너뜀 (setFormData의 finally에서 처리)
  if (isRestoringData.value) {
    return;
  }
  
  if (!Array.isArray(newOptions) || newOptions.length === 0) {
    return;
  }
  
  // 옵션이 변경되었고, 이전에 옵션이 없었거나 변경된 경우에만 처리
  const optionsChanged = !oldOptions || 
    (Array.isArray(oldOptions) && oldOptions.length === 0 && newOptions.length > 0);
  
  if (!optionsChanged) {
    return;
  }
  
  // 각 섹션의 shareType이 옵션에 포함되어 있는지 확인하고 강제로 재설정
  if (Array.isArray(shareholderTransferSections.value)) {
    for (let i = 0; i < shareholderTransferSections.value.length; i++) {
      // 비동기 루프 중간에 데이터 복원 모드가 켜질 수 있으므로 재확인
      if (isRestoringData.value) {
        console.log('🔍 [shareTypeOptions watch] 루프 중 isRestoringData가 true가 되어 중단');
        return;
      }

      const section = shareholderTransferSections.value[i];
      if (section.shareType) {
        const shareTypeStr = String(section.shareType).trim();
        const existsInOptions = newOptions.some(opt => String(opt.value).trim() === shareTypeStr);
        
        if (existsInOptions) {
          // 재설정 직전에도 재확인
          if (isRestoringData.value) {
            console.log('🔍 [shareTypeOptions watch] 재설정 직전 isRestoringData가 true가 되어 스킵');
            continue;
          }

          // 값을 다시 설정하여 v-autocomplete가 반응하도록
          const temp = section.shareType;
          section.shareType = '';
          await nextTick();
          await new Promise(resolve => setTimeout(resolve, 50));

          // await 이후 데이터 복원 모드가 켜졌으면 원복하지 않고 중단 (setFormData에서 처리)
          if (isRestoringData.value) {
            return;
          }

          section.shareType = temp;
          await nextTick();
        }
      }
    }
  }
}, { deep: true });

// Share Type 바인딩 상태 확인을 위한 watch
watch(() => shareholderTransferSections.value.map(s => ({ shareType: s.shareType, fromShareholder: s.fromShareholder })), (newValues, oldValues) => {
  // 각 섹션의 shareType이 옵션에 포함되어 있는지 확인
  newValues.forEach((value, index) => {
    if (value.shareType) {
      const shareTypeStr = String(value.shareType).trim();
      const existsInOptions = shareTypeOptions.value.some(opt => String(opt.value).trim() === shareTypeStr);
      console.log(`🔍 [Share Type Watch] 섹션 ${index}:`, {
        shareType: value.shareType,
        shareTypeStr,
        fromShareholder: value.fromShareholder,
        existsInOptions,
        matchingOption: shareTypeOptions.value.find(opt => String(opt.value).trim() === shareTypeStr)
      });
    }
  });
}, { deep: true });

// Entity Shares 데이터 저장용 (From Shareholder용)
const entitySharesData = ref<Array<{
  name: string;
  idNumber: string;
  numberOfShares: number;
  paidUpCapital: number;
  shareType: string;
  shareholderIdx: number;
  shareCapitalIdx: number;
  currencyCode: string;
  contactCd?: string | null;
}>>([]);

const placeholderLabel = computed(() => t('startTask.Please Select') || 'Please Select');

const certificateTypeOptionItems = computed(() => {
  const options = certificateTypeOptions.value;
  // 배열 체크 추가
  if (!Array.isArray(options)) {
    return [];
  }
  return options
    .map((option) => ({
      title: option.title || option.value || '',
      value: option.value || ''
    }))
    .filter((option) => option.title);
});

// Expand All / Collapse All 함수
const expandAll = () => {
  const panels = ['entity'];
  // shareholderTransferSections가 배열인지 확인
  if (Array.isArray(shareholderTransferSections.value)) {
    panels.push(...shareholderTransferSections.value.map(s => s.id));
  }
  panels.push('certificate-details', 'rorc-info');
  allExpandedPanels.value = panels;
};

const collapseAll = () => {
  allExpandedPanels.value = [];
};

// Shareholder Transfer Section 추가 함수
const addShareholderTransferSection = () => {
  const newId = String(shareholderTransferSections.value.length + 1);
  shareholderTransferSections.value.push(createEmptyTransferSection(newId));
};

// Shareholder Transfer Section 삭제 함수
const deleteShareholderTransferSection = (index: number) => {
  if (shareholderTransferSections.value.length > 1) {
    shareholderTransferSections.value.splice(index, 1);
  }
};

// BaseCode 옵션 로드 함수 (ID Type, Nationality, Country, Address Type)
const loadBaseCodeOptions = async () => {
  try {
    // ID Type (B03) - BIGO1에 E-Filing용 name 저장
    const idTypeResponse = await baseCodeApi.getCodesByMainCode('CM', 'B03', undefined);
    const idTypeCodes = (idTypeResponse as any).codes || idTypeResponse.data?.codes || [];
    identificationTypeOptions.value = idTypeCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo1: code.bigo1 || code.BIGO1 || ''
    })).filter((opt: any) => opt.title && opt.value);

    // Nationality (B04) - BIGO1에 E-Filing용 name 저장
    const nationalityResponse = await baseCodeApi.getCodesByMainCode('CM', 'B04', undefined);
    const nationalityCodes = (nationalityResponse as any).codes || nationalityResponse.data?.codes || [];
    nationalityOptions.value = nationalityCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo1: code.bigo1 || code.BIGO1 || ''
    })).filter((opt: any) => opt.title && opt.value);

    // Country (B05)
    const countryResponse = await baseCodeApi.getCodesByMainCode('CM', 'B05', undefined);
    const countryCodes = (countryResponse as any).codes || countryResponse.data?.codes || [];
    countryOptions.value = countryCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo3: code.bigo3 || code.BIGO3 || ''
    })).filter((opt: any) => opt.title && opt.value);

    // Address Type (B07)
    const addressTypeResponse = await baseCodeApi.getCodesByMainCode('CM', 'B07', undefined);
    const addressTypeCodes = (addressTypeResponse as any).codes || addressTypeResponse.data?.codes || [];
    addressTypeOptions.value = addressTypeCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || ''
    })).filter((opt: any) => opt.title && opt.value);
  } catch (error: any) {
    console.error('❌ Failed to load BaseCode options:', error);
  }
};

// Helper 함수들 (Contact 정보 표시용)
const getIdentificationTypeDisplay = (code: string): string => {
  if (!code) return '';
  const option = identificationTypeOptions.value.find(opt => opt.value === code);
  return option?.title || code;
};

const getNationalityDisplay = (code: string): string => {
  if (!code) return '';
  const option = nationalityOptions.value.find(opt => opt.value === code);
  return option?.title || code;
};

const getCountryCodeDisplay = (code: string): string => {
  if (!code) return '';
  const option = countryOptions.value.find(opt => opt.value === code);
  return option?.title || code;
};

const getAddressTypeDisplay = (code: string): string => {
  if (!code) return '';
  const option = addressTypeOptions.value.find(opt => opt.value === code);
  return option?.title || code;
};

// Date of Birth 표시용 포맷
const formatDateOfBirth = (dob: string): string => {
  return formatDateToDDMMYYYY(dob);
};

// Contact가 Corporate인지 확인
const isCorporateContact = (contactData: any): boolean => {
  return contactData?.CATEGORY_CD === 'B09001';
};

// 이미 자동 선택 이벤트를 발생시켰는지 추적
let hasEmittedAutoSelect = false;

// Shareholder별로 이미 문서 선택 이벤트를 발생시켰는지 추적 (복수 Shareholder 지원)
const hasEmittedAutoSelectByCategory = ref<Map<string, Set<string>>>(new Map()); // Shareholder ID -> Category Set

// 카테고리/파일 자동 선택 함수 (초기 로드 시 전체 문서 선택)
const autoSelectCategoryAndFiles = () => {
  const taskConfig = props.taskConfig;

  if (!taskConfig) {
    console.warn('⚠️ Task Config not available. Please select a Task Type first.');
    return;
  }

  if (hasEmittedAutoSelect) {
    console.log('⏭️ Auto-select already emitted for this config, skipping...');
    return;
  }

  try {
    if (taskConfig.documents && Array.isArray(taskConfig.documents)) {
      // FILTER가 없는 문서만 선택 (초기 로드용)
      const documentsWithoutFilter = taskConfig.documents.filter(
        (doc: any) => !doc.filter || doc.filter === ''
      );

      if (documentsWithoutFilter.length > 0) {
        const templateIds = documentsWithoutFilter
          .map((doc: any) => doc.TMP_ID || doc.DOC_TYPE)
          .filter((id: any): id is string | number => id != null)
          .map((id: any) => String(id));

        const categoryName = documentsWithoutFilter[0]?.category?.trim() || null;

        console.log('📋 Auto-selecting category and templates (no filter):', {
          categoryName,
          templateIds,
          templateCount: templateIds.length
        });

        emit('auto-select-templates', {
          categoryName: categoryName || '',
          templateIds
        });
      }

      hasEmittedAutoSelect = true;
    }
  } catch (error) {
    console.error('❌ Failed to auto-select templates:', error);
  }
};

// 모든 To Shareholder를 확인하고 필요한 문서 자동 선택 함수 (복수 Shareholder 지원)
const autoSelectTemplatesByShareholders = async () => {
  const taskConfig = props.taskConfig;

  if (!taskConfig) {
    console.warn('⚠️ Task Config not available. Please select a Task Type first.');
    return;
  }

  try {
    // 모든 To Shareholder의 CATEGORY_CD 수집
    const categoryCounts = {
      individual: 0, // B09002
      corporate: 0   // B09001
    };

    // To Shareholder가 선택된 섹션들을 확인
    const selectedToShareholders: Array<{ sectionId: string; contactCd: string }> = [];
    if (Array.isArray(shareholderTransferSections.value)) {
      for (const section of shareholderTransferSections.value) {
        if (section.toShareholder) {
          selectedToShareholders.push({
            sectionId: section.id,
            contactCd: section.toShareholder
          });
        }
      }
    }

    // 각 To Shareholder의 CATEGORY_CD 조회
    for (const { sectionId, contactCd } of selectedToShareholders) {
      // 이미 로드된 Contact 정보가 있으면 사용
      let contactData = shareholderTransferContactData.value.get(sectionId)?.to;
      
      // Contact 정보가 없으면 API로 조회
      if (!contactData) {
        try {
          const response = await contactsApi.getContactById(contactCd);
          const contact = (response as any).contact || response.data?.contact;
          if (contact) {
            const categoryCd = contact.CATEGORY_CD || contact.categoryCd || '';
            if (categoryCd === 'B09001') {
              categoryCounts.corporate++;
            } else if (categoryCd === 'B09002') {
              categoryCounts.individual++;
            }
          }
        } catch (error: any) {
          console.warn(`⚠️ Failed to fetch contact ${contactCd}:`, error);
        }
      } else {
        // 이미 로드된 Contact 정보 사용
        const categoryCd = contactData.CATEGORY_CD || '';
        if (categoryCd === 'B09001') {
          categoryCounts.corporate++;
        } else if (categoryCd === 'B09002') {
          categoryCounts.individual++;
        }
      }
    }

    console.log('📊 To Shareholder Category Counts:', categoryCounts);

    // 문서 선택
    if (taskConfig.documents && Array.isArray(taskConfig.documents)) {
      // 1. 필터 없는 문서는 항상 선택
      const documentsWithoutFilter = taskConfig.documents.filter(
        (doc: any) => !doc.filter || doc.filter === ''
      );
      
      // 2. 개인이 1명 이상이면 → 필터가 'B09002'인 문서 선택
      const individualDocuments = categoryCounts.individual > 0 
        ? taskConfig.documents.filter((doc: any) => doc.filter === 'B09002')
        : [];

      // 3. 법인이 1명 이상이면 → 필터가 'B09001'인 문서 선택
      const corporateDocuments = categoryCounts.corporate > 0
        ? taskConfig.documents.filter((doc: any) => doc.filter === 'B09001')
        : [];

      // 모든 선택할 문서 ID 수집 (필터 없는 문서 + 필터 걸린 문서)
      const allTemplateIds: string[] = [];
      
      // 필터 없는 문서 ID 추가
      if (documentsWithoutFilter.length > 0) {
        const noFilterTemplateIds = documentsWithoutFilter
          .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
          .filter((id: any) => id != null)
          .map((id: any) => String(id));
        allTemplateIds.push(...noFilterTemplateIds);
      }

      // 개인 문서 ID 추가
      if (individualDocuments.length > 0) {
        const individualTemplateIds = individualDocuments
          .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
          .filter((id: any) => id != null)
          .map((id: any) => String(id));
        allTemplateIds.push(...individualTemplateIds);
      }

      // 법인 문서 ID 추가
      if (corporateDocuments.length > 0) {
        const corporateTemplateIds = corporateDocuments
          .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
          .filter((id: any) => id != null)
          .map((id: any) => String(id));
        allTemplateIds.push(...corporateTemplateIds);
      }

      // 중복 제거
      const uniqueTemplateIds = Array.from(new Set(allTemplateIds));

      console.log('📋 To Shareholder 기반 문서 선택:', {
        individualShareholderCount: categoryCounts.individual,
        corporateShareholderCount: categoryCounts.corporate,
        noFilterCount: documentsWithoutFilter.length,
        individualDocumentCount: individualDocuments.length,
        corporateDocumentCount: corporateDocuments.length,
        totalSelected: uniqueTemplateIds.length,
        templateIds: uniqueTemplateIds
      });

      // 필터 없는 문서 ID 추출
      const noFilterTemplateIds = documentsWithoutFilter.length > 0
        ? documentsWithoutFilter
            .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
            .filter((id: any) => id != null)
            .map((id: any) => String(id))
        : [];

      // 개인 문서 ID 추출
      const individualTemplateIds = individualDocuments.length > 0
        ? individualDocuments
            .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
            .filter((id: any) => id != null)
            .map((id: any) => String(id))
        : [];

      // 법인 문서 ID 추출
      const corporateTemplateIds = corporateDocuments.length > 0
        ? corporateDocuments
            .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
            .filter((id: any) => id != null)
            .map((id: any) => String(id))
        : [];

      // 필터 걸린 문서가 있으면 filter-select-templates로 emit (필터 없는 문서 포함)
      if (individualTemplateIds.length > 0 || corporateTemplateIds.length > 0) {
        // 모든 선택할 문서 ID (필터 없는 + 필터 걸린)
        const allSelectedIds = Array.from(new Set([
          ...noFilterTemplateIds,
          ...individualTemplateIds,
          ...corporateTemplateIds
        ]));

        // 개인 문서가 있으면 개인 문서로 emit
        if (individualTemplateIds.length > 0) {
          const categoryName = individualDocuments[0]?.category?.trim() || '';
          
          console.log('📋 개인 To Shareholder 문서 선택 (필터 없는 문서 포함):', {
            noFilterCount: noFilterTemplateIds.length,
            individualCount: individualTemplateIds.length,
            totalSelected: allSelectedIds.length,
            templateIds: allSelectedIds
          });

          emit('filter-select-templates', {
            categoryName: categoryName || '',
            templateIds: allSelectedIds,
            filterValue: 'B09002'
          });
        }

        // 법인 문서가 있으면 법인 문서로 emit
        if (corporateTemplateIds.length > 0) {
          const categoryName = corporateDocuments[0]?.category?.trim() || '';
          
          console.log('📋 법인 To Shareholder 문서 선택 (필터 없는 문서 포함):', {
            noFilterCount: noFilterTemplateIds.length,
            corporateCount: corporateTemplateIds.length,
            totalSelected: allSelectedIds.length,
            templateIds: allSelectedIds
          });

          if (individualTemplateIds.length === 0) {
            emit('filter-select-templates', {
              categoryName: categoryName || '',
              templateIds: allSelectedIds,
              filterValue: 'B09001'
            });
          }
        }
      } else {
        // 필터 걸린 문서가 없으면 필터 없는 문서만 선택
        if (noFilterTemplateIds.length > 0) {
          const categoryName = documentsWithoutFilter[0]?.category?.trim() || '';
          
          console.log('📋 필터 없는 문서만 선택:', {
            templateIds: noFilterTemplateIds,
            count: noFilterTemplateIds.length
          });

          emit('auto-select-templates', {
            categoryName: categoryName || '',
            templateIds: noFilterTemplateIds
          });
        }
      }

      console.log('📋 [SharesTransferForm] To Shareholder 기반 문서 자동 선택 완료:', {
        individualCount: categoryCounts.individual,
        corporateCount: categoryCounts.corporate,
        noFilterDocuments: documentsWithoutFilter.length
      });
    }
  } catch (error) {
    console.error('❌ Failed to auto-select templates by shareholders:', error);
  }
};

// taskConfig가 변경될 때 카테고리/파일 자동 선택
watch(() => props.taskConfig, (newConfig, oldConfig) => {
  if (newConfig) {
    if (newConfig !== oldConfig) {
      hasEmittedAutoSelect = false;
    }
    console.log('📌 TaskConfig changed, auto-selecting category/files');
    autoSelectCategoryAndFiles();
  }
}, { immediate: true });

// Entity 변경 시 로깅 및 shareholder 목록 로드
watch(entity, async (newEntityCd, oldEntityCd) => {
  console.log('📌 Entity changed:', newEntityCd);
  
  // 데이터 복원 중이면 watch 실행 안 함 (Allotment와 동일)
  if (isRestoringData.value) {
    console.log('📋 [SharesTransferForm] 데이터 복원 중 - Entity watch 건너뜀');
    return;
  }
  
  // Entity 변경 시 emit으로 부모에게 알림
  emit('update:entityId', newEntityCd);
  
  // Entity가 변경되면 From Shareholder 관련 데이터 초기화
  if (oldEntityCd && newEntityCd !== oldEntityCd) {
    entitySharesData.value = [];
    fromShareholderOptions.value = [];
    fromShareholder.value = '';
    fromNumberOfShares.value = '';
    fromPaidUpShareCapital.value = '';
    fromShareholderID.value = '';
    fromShareholderAddress.value = '';
    // Contact 데이터 제거 (첫 번째 섹션)
    if (shareholderTransferSections.value[0]) {
      const contactData = shareholderTransferContactData.value.get(shareholderTransferSections.value[0].id) || {};
      delete contactData.from;
      if (Object.keys(contactData).length === 0) {
        shareholderTransferContactData.value.delete(shareholderTransferSections.value[0].id);
      } else {
        shareholderTransferContactData.value.set(shareholderTransferSections.value[0].id, contactData);
      }
    }
    transferorCertificateType.value = '';
    transferorCertificateNo.value = '';
  }
  
  if (newEntityCd) {
    // 첫 번째 Shareholder 섹션 펼치기
    if (shareholderTransferSections.value.length > 0) {
      allExpandedPanels.value = [shareholderTransferSections.value[0].id];
    }
    
    // 데이터 로드는 비동기로 처리
    Promise.all([
      loadEntityShares(newEntityCd), // From Shareholder용 (Entity Shares)
      loadShareholderContacts(newEntityCd), // To Shareholder용 (새로운 API 사용)
      loadChairPersonOptions(newEntityCd),
      loadCertificateTypes(newEntityCd),
      loadRepresentativeDirectorOptions(newEntityCd),
      loadRorcOptions(newEntityCd)
    ]).catch(error => {
      console.error('Failed to load entity data:', error);
    });
  } else {
    // 엔티티가 해제되면 전체 연락처로 복원
    entitySharesData.value = [];
    fromShareholderOptions.value = [];
    fromShareholder.value = '';
    fromNumberOfShares.value = '';
    fromPaidUpShareCapital.value = '';
    fromShareholderID.value = '';
    fromShareholderAddress.value = '';
    // Contact 데이터 제거 (첫 번째 섹션)
    if (shareholderTransferSections.value[0]) {
      const contactData = shareholderTransferContactData.value.get(shareholderTransferSections.value[0].id) || {};
      delete contactData.from;
      if (Object.keys(contactData).length === 0) {
        shareholderTransferContactData.value.delete(shareholderTransferSections.value[0].id);
      } else {
        shareholderTransferContactData.value.set(shareholderTransferSections.value[0].id, contactData);
      }
    }
    transferorCertificateType.value = '';
    transferorCertificateNo.value = '';
    certificateTypeOptions.value = [];
    chairPersonOptions.value = [];
    representativeDirectorOptions.value = [];
    rorcOptions.value = [];
    // 엔티티가 해제되면 To Shareholder 목록은 로드하지 않음 (엔티티가 필요함)
    toShareholderOptions.value = [];
    originalToShareholderOptions.value = [];
    allExpandedPanels.value = [];
  }
});

// props.entityId 변경 시 entity ref 동기화 및 shareholder 목록 로드
watch(
  () => props.entityId,
  async (newEntityId) => {
    if (newEntityId && newEntityId !== entity.value) {
      entity.value = newEntityId;
    }
    if (newEntityId) {
      // From Shareholder에서 선택된 CONTACT_CD 추출
      const selectedFromOption = fromShareholderOptions.value.find(opt => opt.value === fromShareholder.value);
      const excludeContactCd = selectedFromOption?.shareData?.contactCd;
      
      await Promise.all([
        loadEntityShares(newEntityId),
        loadShareholderContacts(newEntityId, excludeContactCd),
        loadChairPersonOptions(newEntityId),
        loadCertificateTypes(newEntityId),
        loadRepresentativeDirectorOptions(newEntityId),
        loadRorcOptions(newEntityId)
      ]);
      // 첫 번째 Shareholder 섹션 펼치기
      if (shareholderTransferSections.value.length > 0) {
        allExpandedPanels.value = [shareholderTransferSections.value[0].id];
      }
    }
  },
  { immediate: true }
);

// From Shareholder 선택 시 자동 바인딩 및 Contact 정보 로드
watch(fromShareholder, async (selectedValue, oldValue) => {
  // 데이터 복원 중이면 watch 실행 안 함 (Allotment와 동일)
  if (isRestoringData.value) {
    console.log('📋 [SharesTransferForm] 데이터 복원 중 - From Shareholder watch 건너뜀');
    return;
  }
  
  console.log('🔔 [watch fromShareholder] 트리거됨:', {
    selectedValue,
    oldValue,
    entityId: props.entityId,
    timestamp: Date.now()
  });
  
  if (!selectedValue) {
    console.log('🔔 [watch fromShareholder] 선택 해제됨 - To (Transferee) 목록 재로드');
    fromNumberOfShares.value = '';
    fromPaidUpShareCapital.value = '';
    fromShareholderID.value = '';
    fromShareholderAddress.value = '';
    currency.value = '';
    shareType.value = '';
    transferorCertificateType.value = '';
    transferorCertificateNo.value = '';
    // Contact 데이터 제거 (첫 번째 섹션)
    if (shareholderTransferSections.value[0]) {
      const contactData = shareholderTransferContactData.value.get(shareholderTransferSections.value[0].id) || {};
      delete contactData.from;
      if (Object.keys(contactData).length === 0) {
        shareholderTransferContactData.value.delete(shareholderTransferSections.value[0].id);
      } else {
        shareholderTransferContactData.value.set(shareholderTransferSections.value[0].id, contactData);
      }
    }
    
    // From Shareholder가 선택 해제되면 To (Transferee) 목록 재로드 (excludeContactCd 없이)
    const targetEntityId = props.entityId || entity.value;
    if (targetEntityId) {
      console.log('🔄 [watch fromShareholder] 선택 해제 - To (Transferee) 목록 재조회 시작');
      await loadShareholderContacts(targetEntityId);
      console.log('✅ [watch fromShareholder] 선택 해제 - To (Transferee) 목록 재조회 완료');
    }
    return;
  }
  
  console.log('🔔 [watch fromShareholder] 선택됨 - contactCd 추출 및 재조회 시작:', {
    selectedValue,
    entityId: props.entityId
  });
  
  // 선택된 주주 데이터 찾기
  const selectedOption = fromShareholderOptions.value.find(opt => opt.value === selectedValue);
  let contactCd: string | undefined;
  
  console.log('🔍 [watch fromShareholder] 선택된 옵션 찾기:', {
    selectedValue,
    selectedOption,
    allOptions: fromShareholderOptions.value
  });
  
  if (selectedOption?.shareData) {
    const shareData = selectedOption.shareData;
    fromShareholderID.value = shareData.idNumber || '';
    
    // Currency 설정 (currencies 배열의 첫 번째 항목을 기본값으로)
    const currencies = shareData.currencies || [];
    if (currencies.length > 0) {
      const firstCurrency = currencies[0];
      currency.value = `${firstCurrency.currency || firstCurrency.currencyCode || ''}_${firstCurrency.shareCapitalIdx || 0}`;
    } else {
      // currencies 배열이 없으면 기존 방식 사용 (하위 호환성)
      currency.value = shareData.currency || shareData.currencyCode || '';
    }
    
    // Share Type 초기화 (새로운 옵션에 맞게)
    shareType.value = '';
    
    // Number of Shares와 Paid up share capital은 Currency와 Share Type 선택 시 바인딩
    fromNumberOfShares.value = '';
    fromPaidUpShareCapital.value = '';
    
    // Contact 정보 로드 (shareData에 contactCd가 있으면)
    // contactCd 우선순위: shareData.contactCd > entitySharesData에서 찾기
    contactCd = shareData.contactCd;
    
    // entitySharesData에서도 확인 (shareCapitalIdx와 shareholderIdx로 매칭)
    if (!contactCd && shareData.shareCapitalIdx && shareData.shareholderIdx) {
      const matchingEntityShare = entitySharesData.value.find(
        (es: any) => es.shareCapitalIdx === shareData.shareCapitalIdx && 
                     es.shareholderIdx === shareData.shareholderIdx
      );
      if (matchingEntityShare && (matchingEntityShare as any).contactCd) {
        contactCd = (matchingEntityShare as any).contactCd;
        (shareData as any).contactCd = contactCd; // shareData에도 저장
      }
    }
    
    console.log('🔍 [watch fromShareholder] CONTACT_CD 추출:', {
      shareDataContactCd: shareData.contactCd,
      extractedContactCd: contactCd,
      shareCapitalIdx: shareData.shareCapitalIdx,
      shareholderIdx: shareData.shareholderIdx
    });
    
    if (contactCd) {
      await loadFromShareholderContactData(contactCd);
    }
    
    // Certificate Type과 Certificate No는 Shareholder Detail API에서 가져오기
    if (shareData.shareCapitalIdx && shareData.shareholderIdx && props.entityId) {
      try {
        const detailResponse = await entityApi.getEntityShareholderDetail(
          props.entityId,
          shareData.shareCapitalIdx,
          shareData.shareholderIdx
        );
        const detailData = detailResponse.data?.data || detailResponse.data || {};
        
        // PreSet 등에서 이미 cert가 있으면 API 값으로 덮어쓰지 않음
        if (!String(transferorCertificateType.value || '').trim()) {
          transferorCertificateType.value = detailData.certificateType || '';
        }
        if (!String(transferorCertificateNo.value || '').trim()) {
          transferorCertificateNo.value = detailData.certificateNo || '';
        }
        
        // Entity Shareholder Detail에서 contactCd가 있으면 Contact 정보 로드
        const detailContactCd = detailData.contactCd || detailData.CONTACT_CD;
        if (detailContactCd) {
          if (!contactCd) {
            contactCd = detailContactCd;
            (shareData as any).contactCd = contactCd;
          }
          if (!fromShareholderContactData.value && contactCd) {
            await loadFromShareholderContactData(contactCd);
          }
        }
        
        console.log('✅ From Shareholder 자동 바인딩:', {
          name: shareData.name,
          numberOfShares: shareData.numberOfShares,
          paidUpCapital: shareData.paidUpCapital,
          certificateType: detailData.certificateType,
          certificateNo: detailData.certificateNo,
          contactCd: contactCd,
          detailContactCd: detailContactCd
        });
      } catch (error) {
        console.error('❌ Failed to load shareholder detail:', error);
        // Detail 로드 실패해도 기본 정보는 바인딩
        transferorCertificateType.value = '';
        transferorCertificateNo.value = '';
      }
    } else {
      // shareCapitalIdx나 shareholderIdx가 없으면 기본 정보만 바인딩
      transferorCertificateType.value = '';
      transferorCertificateNo.value = '';
      console.log('✅ From Shareholder 자동 바인딩 (기본 정보만):', {
        name: shareData.name,
        numberOfShares: shareData.numberOfShares,
        paidUpCapital: shareData.paidUpCapital,
        contactCd: contactCd
      });
    }
  }
  
  // From Shareholder가 선택되면 To (Transferee) 목록 재로드 (excludeContactCd 포함)
  // contactCd 추출 우선순위:
  // 1. watch 내부에서 추출한 contactCd (가장 확실함)
  // 2. selectedOption.contactCd (옵션 자체에 추가한 contactCd)
  // 3. selectedOption.shareData.contactCd
  // 4. entitySharesData에서 찾기 (shareCapitalIdx와 shareholderIdx로 매칭)
  let finalContactCd = contactCd;
  
  if (!finalContactCd && selectedOption) {
    // 옵션 자체의 contactCd (loadEntityShares에서 추가한 것)
    finalContactCd = selectedOption.contactCd;
    
    // 없으면 shareData에서 찾기
    if (!finalContactCd) {
      finalContactCd = selectedOption.shareData?.contactCd;
    }
    
    // 여전히 없으면 entitySharesData에서 찾기
    if (!finalContactCd && selectedOption.shareData?.shareCapitalIdx && selectedOption.shareData?.shareholderIdx) {
      const matchingEntityShare = entitySharesData.value.find(
        (es: any) => es.shareCapitalIdx === selectedOption.shareData.shareCapitalIdx && 
                     es.shareholderIdx === selectedOption.shareData.shareholderIdx
      );
      if (matchingEntityShare && matchingEntityShare.contactCd) {
        finalContactCd = matchingEntityShare.contactCd;
      }
    }
  }
  
  console.log('🔍 [watch fromShareholder] 최종 CONTACT_CD 추출 결과:', {
    selectedValue,
    contactCd,
    selectedOptionContactCd: selectedOption?.contactCd,
    selectedOptionShareDataContactCd: selectedOption?.shareData?.contactCd,
    finalContactCd,
    selectedOption: selectedOption ? {
      title: selectedOption.title,
      value: selectedOption.value,
      contactCd: selectedOption.contactCd,
      shareData: selectedOption.shareData ? {
        contactCd: selectedOption.shareData.contactCd,
        shareCapitalIdx: selectedOption.shareData.shareCapitalIdx,
        shareholderIdx: selectedOption.shareData.shareholderIdx
      } : null
    } : null
  });
  
  // From Shareholder가 선택되면 To (Transferee) 목록을 반드시 재조회 (excludeContactCd 포함)
  // entityId 우선순위: props.entityId > entity.value
  const targetEntityId = props.entityId || entity.value;
  
  console.log('🔄 [watch fromShareholder] To (Transferee) 재조회 준비:', {
    propsEntityId: props.entityId,
    entityValue: entity.value,
    targetEntityId,
    finalContactCd,
    hasEntityId: !!targetEntityId
  });
  
  if (targetEntityId) {
    if (finalContactCd) {
      const trimmedContactCd = String(finalContactCd).trim();
      if (trimmedContactCd) {
        console.log('🚀 [watch fromShareholder] To (Transferee) 목록 재조회 시작 (excludeContactCd 포함):', {
          entityId: targetEntityId,
          excludeContactCd: trimmedContactCd,
          originalContactCd: contactCd,
          finalContactCd: finalContactCd,
          contactCdType: typeof finalContactCd,
          contactCdLength: trimmedContactCd.length,
          willForceRefresh: true,
          timestamp: Date.now()
        });
        // 반드시 재조회하도록 호출
        try {
          await loadShareholderContacts(targetEntityId, trimmedContactCd);
          console.log('✅ [watch fromShareholder] To (Transferee) 목록 재조회 완료');
        } catch (error) {
          console.error('❌ [watch fromShareholder] To (Transferee) 목록 재조회 실패:', error);
        }
      } else {
        console.warn('⚠️ [watch fromShareholder] CONTACT_CD가 빈 문자열이어서 To (Transferee) 목록을 제외 없이 재조회합니다.');
        try {
          await loadShareholderContacts(targetEntityId);
        } catch (error) {
          console.error('❌ [watch fromShareholder] To (Transferee) 목록 재조회 실패:', error);
        }
      }
    } else {
      console.warn('⚠️ [watch fromShareholder] CONTACT_CD가 없어 To (Transferee) 목록을 제외 없이 재조회합니다.', {
        contactCd,
        finalContactCd,
        selectedOption: selectedOption?.shareData,
        hasSelectedOption: !!selectedOption
      });
      try {
        await loadShareholderContacts(targetEntityId);
      } catch (error) {
        console.error('❌ [watch fromShareholder] To (Transferee) 목록 재조회 실패:', error);
      }
    }
  } else {
    console.warn('⚠️ [watch fromShareholder] entityId가 없어 To (Transferee) 목록을 재조회할 수 없습니다.', {
      propsEntityId: props.entityId,
      entityValue: entity.value
    });
  }
});

// 각 섹션별 From Shareholder 선택 시 자동 바인딩 및 Contact 정보 로드
watch(
  () => Array.isArray(shareholderTransferSections.value)
    ? shareholderTransferSections.value.map(s => s.fromShareholder)
    : [],
  async (newValues, oldValues) => {
    if (!Array.isArray(shareholderTransferSections.value)) return;
    for (let i = 0; i < shareholderTransferSections.value.length; i++) {
      const section = shareholderTransferSections.value[i];
      const newValue = newValues[i];
      const oldValue = oldValues?.[i];
      
      if (newValue === oldValue) continue;
      
      if (!newValue) {
        // 선택 해제
        section.fromNumberOfShares = '';
        section.fromPaidUpShareCapital = '';
        section.fromShareholderID = '';
        section.fromShareholderAddress = '';
        section.currency = '';
        section.shareType = '';
        section.hasSubClass = false;
        section.subClassErrorMessage = '';
        section.enteredMember = '';
        
        // Contact 데이터 제거
        const contactData = shareholderTransferContactData.value.get(section.id) || {};
        delete contactData.from;
        if (Object.keys(contactData).length === 0) {
          shareholderTransferContactData.value.delete(section.id);
        } else {
          shareholderTransferContactData.value.set(section.id, contactData);
        }
        
        // To (Transferee) 목록 재로드
        const targetEntityId = props.entityId || entity.value;
        if (targetEntityId) {
          await loadShareholderContacts(targetEntityId);
        }
        continue;
      }
      
      // 선택된 주주 데이터 찾기
      const selectedOption = fromShareholderOptions.value.find(opt => opt.value === newValue);
      if (!selectedOption?.shareData) continue;
      
      const shareData = selectedOption.shareData;
      section.fromShareholderID = shareData.idNumber || '';
      
      // Currency 설정
      const currencies = shareData.currencies || [];
      if (currencies.length > 0) {
        const firstCurrency = currencies[0];
        section.currency = `${firstCurrency.currency || firstCurrency.currencyCode || ''}_${firstCurrency.shareCapitalIdx || 0}`;
      } else {
        section.currency = shareData.currency || shareData.currencyCode || '';
      }
      
      // Share Type 초기화
      section.shareType = '';
      section.fromNumberOfShares = '';
      section.fromPaidUpShareCapital = '';
      
      // Contact 정보 로드
      let contactCd: string | undefined = shareData.contactCd;
      if (!contactCd && shareData.shareCapitalIdx && shareData.shareholderIdx) {
        const matchingEntityShare = entitySharesData.value.find(
          (es: any) => es.shareCapitalIdx === shareData.shareCapitalIdx && 
                       es.shareholderIdx === shareData.shareholderIdx
        );
        if (matchingEntityShare && (matchingEntityShare as any).contactCd) {
          contactCd = (matchingEntityShare as any).contactCd;
        }
      }
      
      if (contactCd) {
        await loadFromShareholderContactDataForSection(i, contactCd);
      }
      
      // Entity Shareholder Detail에서 추가 정보 가져오기
      // entityId 우선순위: props.entityId > entity.value
      const entityIdForDetail = props.entityId || entity.value;
      
      console.log('🔍 [Section', i, '] ENTERED_MEMBER 로드 시작:', {
        shareCapitalIdx: shareData.shareCapitalIdx,
        shareholderIdx: shareData.shareholderIdx,
        propsEntityId: props.entityId,
        entityValue: entity.value,
        entityIdForDetail: entityIdForDetail,
        shareData: shareData
      });
      
      if (shareData.shareCapitalIdx && shareData.shareholderIdx && entityIdForDetail) {
        try {
          console.log('📞 [Section', i, '] getEntityShareholderDetail API 호출:', {
            entityId: entityIdForDetail,
            shareCapitalIdx: shareData.shareCapitalIdx,
            shareholderIdx: shareData.shareholderIdx
          });
          
          const detailResponse = await entityApi.getEntityShareholderDetail(
            entityIdForDetail,
            shareData.shareCapitalIdx,
            shareData.shareholderIdx
          );
          
          console.log('📥 [Section', i, '] getEntityShareholderDetail API 응답:', {
            fullResponse: detailResponse,
            data: detailResponse.data,
            detailData: detailResponse.data?.data || detailResponse.data
          });
          
          const detailData = detailResponse.data?.data || detailResponse.data || {};
          
          console.log('🔍 [Section', i, '] detailData 전체 내용:', {
            detailData: detailData,
            enteredMember: detailData.enteredMember,
            ENTERED_MEMBER: detailData.ENTERED_MEMBER,
            allKeys: Object.keys(detailData)
          });
          
          // ENTERED_MEMBER 로드 및 포맷팅
          const enteredMember = detailData.enteredMember || detailData.ENTERED_MEMBER || null;
          
          console.log('📊 [Section', i, '] ENTERED_MEMBER 추출 결과:', {
            enteredMemberRaw: detailData.enteredMember,
            ENTERED_MEMBERRaw: detailData.ENTERED_MEMBER,
            enteredMember: enteredMember,
            type: typeof enteredMember,
            isNull: enteredMember === null,
            isEmpty: enteredMember === '',
            isUndefined: enteredMember === undefined
          });
          
          if (enteredMember) {
            section.enteredMember = enteredMember;
            console.log('✅ [Section', i, '] ENTERED_MEMBER 로드 성공:', {
              shareCapitalIdx: shareData.shareCapitalIdx,
              shareholderIdx: shareData.shareholderIdx,
              enteredMember: enteredMember,
              sectionEnteredMember: section.enteredMember
            });
          } else {
            section.enteredMember = '';
            console.log('⚠️ [Section', i, '] ENTERED_MEMBER 값 없음:', {
              shareCapitalIdx: shareData.shareCapitalIdx,
              shareholderIdx: shareData.shareholderIdx,
              detailData: detailData,
              sectionEnteredMember: section.enteredMember
            });
          }
          
          // Detail에서 contactCd가 있으면 Contact 정보 로드
          const detailContactCd = detailData.contactCd || detailData.CONTACT_CD;
          if (detailContactCd && !contactCd) {
            contactCd = detailContactCd;
            if (contactCd) {
              await loadFromShareholderContactDataForSection(i, contactCd);
            }
          }
        } catch (error: any) {
          console.error('❌ [Section', i, '] Failed to load shareholder detail:', {
            error: error,
            message: error?.message || String(error),
            stack: error?.stack,
            shareCapitalIdx: shareData.shareCapitalIdx,
            shareholderIdx: shareData.shareholderIdx,
            entityId: entityIdForDetail,
            propsEntityId: props.entityId,
            entityValue: entity.value
          });
          section.enteredMember = '';
        }
      } else {
        // shareCapitalIdx나 shareholderIdx가 없으면 기본 정보만 바인딩
        console.log('⚠️ [Section', i, '] ENTERED_MEMBER 로드 스킵 - 필수 값 없음:', {
          shareCapitalIdx: shareData.shareCapitalIdx,
          shareholderIdx: shareData.shareholderIdx,
          propsEntityId: props.entityId,
          entityValue: entity.value,
          entityIdForDetail: entityIdForDetail,
          hasShareCapitalIdx: !!shareData.shareCapitalIdx,
          hasShareholderIdx: !!shareData.shareholderIdx,
          hasEntityId: !!entityIdForDetail
        });
        section.enteredMember = '';
      }
      
      // To (Transferee) 목록 재로드 (excludeContactCd 포함)
      const targetEntityId = props.entityId || entity.value;
      if (targetEntityId && contactCd) {
        const trimmedContactCd = String(contactCd).trim();
        if (trimmedContactCd) {
          await loadShareholderContacts(targetEntityId, trimmedContactCd);
        } else {
          await loadShareholderContacts(targetEntityId);
        }
      }
    }
  },
  { deep: true }
);

// 각 섹션별 Currency와 Share Type 변경 시 자동 바인딩 및 validation 에러 제거
watch(
  () => Array.isArray(shareholderTransferSections.value) 
    ? shareholderTransferSections.value.map(s => ({ currency: s.currency, shareType: s.shareType, fromShareholder: s.fromShareholder }))
    : [],
  async (newValues, oldValues) => {
    // 데이터 복원 중이면 toShareholder 초기화만 스킵 (fromNumberOfShares 바인딩은 계속 실행)
    const skipToShareholderReset = isRestoringData.value;
    
    if (skipToShareholderReset) {
      console.log('🔍 [watch currency/shareType] isRestoringData가 true이므로 toShareholder 초기화만 스킵');
    }
    
    if (!Array.isArray(shareholderTransferSections.value)) return;
    for (let i = 0; i < shareholderTransferSections.value.length; i++) {

      const section = shareholderTransferSections.value[i];
      const newValue = newValues[i];
      const oldValue = oldValues?.[i];
      
      // Currency 변경 시 validation 에러 제거
      if (newValue?.currency !== oldValue?.currency && newValue?.currency) {
        const sectionErrors = sectionValidationErrors.value.get(i) || {};
        if (sectionErrors.currency) {
          delete sectionErrors.currency;
          if (Object.keys(sectionErrors).length === 0) {
            sectionValidationErrors.value.delete(i);
          } else {
            sectionValidationErrors.value.set(i, sectionErrors);
          }
        }
      }
      
      // Share Type 변경 시 validation 에러 제거
      if (newValue?.shareType !== oldValue?.shareType && newValue?.shareType) {
        const sectionErrors = sectionValidationErrors.value.get(i) || {};
        if (sectionErrors.shareType) {
          delete sectionErrors.shareType;
          if (Object.keys(sectionErrors).length === 0) {
            sectionValidationErrors.value.delete(i);
          } else {
            sectionValidationErrors.value.set(i, sectionErrors);
          }
        }
      }
      
      // Currency나 Share Type이 변경되지 않았으면 스킵
      if (newValue?.currency === oldValue?.currency && newValue?.shareType === oldValue?.shareType) continue;
      
      // Currency 또는 Share Type이 변경되면 To (Transferee) 필드 초기화
      // 단, shareType이 빈 값에서 같은 값으로 복원되는 경우는 무시 (재설정 로직 때문)
      // ⚠️ isRestoringData가 true이면 toShareholder 초기화는 스킵
      if (oldValue && (newValue?.currency !== oldValue?.currency || newValue?.shareType !== oldValue?.shareType)) {
        // shareType이 빈 값('')에서 실제 값으로 복원되는 경우는 무시 (재설정 로직)
        const isShareTypeRestore = oldValue.shareType === '' && newValue?.shareType && newValue.shareType !== '';
        
        // currency가 변경되거나, shareType이 실제로 다른 값으로 변경된 경우에만 초기화
        const shouldReset = newValue?.currency !== oldValue?.currency || 
          (newValue?.shareType !== oldValue?.shareType && !isShareTypeRestore);
        
        // 추가 체크: toShareholder가 이미 설정되어 있고, currency와 shareType이 모두 동일하면 초기화하지 않음
        const hasToShareholder = section.toShareholder && section.toShareholder !== '';
        const currencyUnchanged = newValue?.currency === oldValue?.currency;
        const shareTypeUnchanged = newValue?.shareType === oldValue?.shareType;
        
        // currency나 shareType이 실제로 변경되었고, toShareholder가 설정되어 있지 않거나 
        // currency/shareType이 변경된 경우에만 초기화
        // 단, isRestoringData가 true이면 toShareholder 초기화는 스킵
        if (shouldReset && 
            ((oldValue.currency && oldValue.currency !== '') || (oldValue.shareType && oldValue.shareType !== '')) &&
            !(hasToShareholder && currencyUnchanged && shareTypeUnchanged) &&
            !skipToShareholderReset) {  // isRestoringData일 때는 toShareholder 초기화 스킵
          await nextTick();
          section.toShareholder = '';
          section.toNumberOfShares = '';
          section.toPaidUpShareCapital = '';
          section.toIssuedShareCapital = '';
          section.totalConsiderationPaid = '';
          section.toShareholderID = '';
          section.toShareholderAddress = '';
          section.toShareholderNationality = '';
          section.toShareholderBirth = '';
          section.showTrustSection = false;
          section.sharesHeldInTrust = null;
          section.trustName = '';
          
          // Contact 데이터도 초기화
          const contactData = shareholderTransferContactData.value.get(section.id) || {};
          delete contactData.to;
          if (Object.keys(contactData).length === 0) {
            shareholderTransferContactData.value.delete(section.id);
          } else {
            shareholderTransferContactData.value.set(section.id, contactData);
          }
          await nextTick();
        }
      }
      
      // Currency와 Share Type이 선택되었는지 확인
      if (!section.fromShareholder || !newValue?.currency || !newValue?.shareType) {
        if (!newValue?.currency || !newValue?.shareType) {
          section.fromNumberOfShares = '';
          section.fromPaidUpShareCapital = '';
        }
        continue;
      }
      
      // 선택된 From Shareholder의 shareData 찾기
      const selectedOption = fromShareholderOptions.value.find(opt => opt.value === section.fromShareholder);
      if (!selectedOption?.shareData) {
        section.fromNumberOfShares = '';
        section.fromPaidUpShareCapital = '';
        continue;
      }
      
      const shareData = selectedOption.shareData;
      const currencies = shareData.currencies || [];
      
      // 선택된 currency 찾기
      const [selectedCurrencyCode, shareCapitalIdxStr] = newValue.currency.split('_');
      const shareCapitalIdx = parseInt(shareCapitalIdxStr, 10);
      
      const selectedCurrency = currencies.find((curr: any) => 
        (curr.currency === selectedCurrencyCode || curr.currencyCode === selectedCurrencyCode) &&
        curr.shareCapitalIdx === shareCapitalIdx
      );
      
      if (!selectedCurrency) {
        section.fromNumberOfShares = '';
        section.fromPaidUpShareCapital = '';
        continue;
      }
      
      // 서브클래스 확인
      const shareholderIdx = selectedCurrency.shareholderIdx || shareData.shareholderIdx;
      if (shareholderIdx && newValue.shareType) {
        const hasSub = await checkSubClassForSection(i, shareholderIdx, newValue.shareType);
        
        if (hasSub) {
          section.fromNumberOfShares = '';
          section.fromPaidUpShareCapital = '';
          continue;
        }
      }
      
      // Share Type에 따라 값 바인딩
      let numberOfShares = 0;
      let paidUpCapital = 0;
      
      if (newValue.shareType === 'A20001' || newValue.shareType === 'A20001000' || newValue.shareType.startsWith('A20001')) {
        numberOfShares = selectedCurrency.sharesOrdinary || 0;
        paidUpCapital = selectedCurrency.paidUpOrdinary || 0;
      } else if (newValue.shareType === 'A20002' || newValue.shareType === 'A20002000' || newValue.shareType.startsWith('A20002')) {
        numberOfShares = selectedCurrency.sharesPreference || 0;
        paidUpCapital = selectedCurrency.paidUpPreference || 0;
      } else if (newValue.shareType === 'A20003' || newValue.shareType === 'A20003000' || newValue.shareType.startsWith('A20003')) {
        numberOfShares = selectedCurrency.sharesOthers || 0;
        paidUpCapital = selectedCurrency.paidUpOthers || 0;
      }
      
      section.fromNumberOfShares = String(numberOfShares);
      section.fromPaidUpShareCapital = String(paidUpCapital);
      
      // ENTERED_MEMBER는 From Shareholder 선택 시 이미 로드되었으므로 여기서는 스킵
      // (Currency나 Share Type 변경 시에는 이미 로드된 값을 유지)
      
      console.log('✅ [Section', i, '] Currency와 Share Type 선택 시 바인딩:', {
        currency: newValue.currency,
        shareType: newValue.shareType,
        numberOfShares,
        paidUpCapital,
        enteredMember: section.enteredMember,
        isRestoringData: isRestoringData.value
      });
    }
  },
  { deep: true }
);

// 섹션별 서브클래스 확인 함수
const checkSubClassForSection = async (shareholderIndex: number, shareholderIdx: number, shareTypeValue: string): Promise<boolean> => {
  if (!shareholderIdx || !shareTypeValue) {
    const section = shareholderTransferSections.value[shareholderIndex];
    if (section) {
      section.hasSubClass = false;
      section.subClassErrorMessage = '';
    }
    return false;
  }

  try {
    const response = await apiClient.get(`/startTask/shares-transfer/check-subclass/${shareholderIdx}`) as any;
    const responseData = response?.data || response;
    
    const subClassOrdinary = Number(responseData?.subClassOrdinary || 0);
    const subClassPreference = Number(responseData?.subClassPreference || 0);
    const subClassOthers = Number(responseData?.subClassOthers || 0);
    
    let hasSubForType = false;
    
    if (shareTypeValue === 'A20001' || shareTypeValue === 'A20001000' || shareTypeValue.startsWith('A20001')) {
      hasSubForType = subClassOrdinary > 0;
    } else if (shareTypeValue === 'A20002' || shareTypeValue === 'A20002000' || shareTypeValue.startsWith('A20002')) {
      hasSubForType = subClassPreference > 0;
    } else if (shareTypeValue === 'A20003' || shareTypeValue === 'A20003000' || shareTypeValue.startsWith('A20003')) {
      hasSubForType = subClassOthers > 0;
    }
    
    const section = shareholderTransferSections.value[shareholderIndex];
    if (section) {
      section.hasSubClass = hasSubForType;
      
      if (hasSubForType) {
        section.subClassErrorMessage = t('startTask.Subclass transfer is not supported') || 'Subclass transfer는 서비스는 제공하지않는다';
      } else {
        section.subClassErrorMessage = '';
      }
    }
    
    return hasSubForType;
  } catch (error) {
    console.error('❌ Failed to check subclass for section:', shareholderIndex, error);
    const section = shareholderTransferSections.value[shareholderIndex];
    if (section) {
      section.hasSubClass = false;
      section.subClassErrorMessage = '';
    }
    return false;
  }
};

// 서브클래스 확인 함수 (Share Type별로 확인) - 기존 호환성
const checkSubClass = async (shareholderIdx: number, shareTypeValue: string): Promise<boolean> => {
  if (!shareholderIdx || !shareTypeValue) {
    hasSubClass.value = false;
    subClassErrorMessage.value = '';
    return false;
  }

  try {
    const response = await apiClient.get(`/startTask/shares-transfer/check-subclass/${shareholderIdx}`) as any;
    const responseData = response?.data || response;
    
    // 각 Share Type별 SUB_CLASS 값
    const subClassOrdinary = Number(responseData?.subClassOrdinary || 0);
    const subClassPreference = Number(responseData?.subClassPreference || 0);
    const subClassOthers = Number(responseData?.subClassOthers || 0);
    
    // 선택한 Share Type에 해당하는 SUB_CLASS 값이 0보다 큰지 확인
    let hasSubForType = false;
    
    // A20001 또는 A20001000 = Ordinary
    if (shareTypeValue === 'A20001' || shareTypeValue === 'A20001000' || shareTypeValue.startsWith('A20001')) {
      hasSubForType = subClassOrdinary > 0;
    } 
    // A20002 또는 A20002000 = Preference
    else if (shareTypeValue === 'A20002' || shareTypeValue === 'A20002000' || shareTypeValue.startsWith('A20002')) {
      hasSubForType = subClassPreference > 0;
    } 
    // A20003 또는 A20003000 = Others
    else if (shareTypeValue === 'A20003' || shareTypeValue === 'A20003000' || shareTypeValue.startsWith('A20003')) {
      hasSubForType = subClassOthers > 0;
    }
    
    hasSubClass.value = hasSubForType;
    
    if (hasSubForType) {
      subClassErrorMessage.value = t('startTask.Subclass transfer is not supported') || 'Subclass transfer는 서비스는 제공하지않는다';
    } else {
      subClassErrorMessage.value = '';
    }
    
    console.log('✅ 서브클래스 확인 완료:', {
      shareholderIdx,
      shareTypeValue,
      subClassOrdinary,
      subClassPreference,
      subClassOthers,
      hasSubClass: hasSubForType
    });
    
    return hasSubForType;
  } catch (error: any) {
    console.error('❌ 서브클래스 확인 실패:', error);
    hasSubClass.value = false;
    subClassErrorMessage.value = '';
    return false;
  }
};

// Currency와 Share Type 선택 시 Number of Shares와 Paid up share capital 바인딩
watch([currency, shareType], async ([currencyValue, shareTypeValue], [oldCurrencyValue, oldShareTypeValue]) => {
  // 데이터 복원 중이면 watch 실행 안 함 (toShareholder 초기화 방지)
  if (isRestoringData.value) {
    return;
  }
  
  // 초기화
  hasSubClass.value = false;
  subClassErrorMessage.value = '';
  
  // Currency 또는 Share Type이 변경되면 To (Transferee) 필드 초기화
  if ((oldCurrencyValue !== undefined && currencyValue !== oldCurrencyValue) || 
      (oldShareTypeValue !== undefined && shareTypeValue !== oldShareTypeValue)) {
    // 이전 값이 있었고 새 값이 다르면 To (Transferee) 필드 초기화
    if ((oldCurrencyValue && oldCurrencyValue !== '') || (oldShareTypeValue && oldShareTypeValue !== '')) {
      // 초기화 직전에 다시 한 번 체크
      if (isRestoringData.value) {
        return;
      }
      
      // 초기화 중 플래그 설정하여 validation 비활성화
      isResettingToFields.value = true;
      await nextTick();
      
      // nextTick 후에도 다시 체크
      if (isRestoringData.value) {
        isResettingToFields.value = false;
        return;
      }
      
      toShareholder.value = '';
      toNumberOfShares.value = '';
      toPaidUpShareCapital.value = '';
      toIssuedShareCapital.value = '';
      totalConsiderationPaid.value = '';
      toShareholderID.value = '';
      toShareholderAddress.value = '';
      toShareholderNationality.value = '';
      toShareholderBirth.value = '';
      // Trust 관련 필드도 초기화
      showTrustSection.value = false;
      sharesHeldInTrust.value = null;
      trustName.value = '';
      // Contact 데이터도 초기화 (첫 번째 섹션)
      if (shareholderTransferSections.value[0]) {
        const contactData = shareholderTransferContactData.value.get(shareholderTransferSections.value[0].id) || {};
        delete contactData.to;
        if (Object.keys(contactData).length === 0) {
          shareholderTransferContactData.value.delete(shareholderTransferSections.value[0].id);
        } else {
          shareholderTransferContactData.value.set(shareholderTransferSections.value[0].id, contactData);
        }
      }
      
      // validation 활성화
      await nextTick();
      isResettingToFields.value = false;
    }
  }
  
  if (!fromShareholder.value || !currencyValue || !shareTypeValue) {
    // Currency 또는 Share Type이 선택되지 않으면 초기화
    if (!currencyValue || !shareTypeValue) {
      fromNumberOfShares.value = '';
      fromPaidUpShareCapital.value = '';
    }
    return;
  }
  
  // 선택된 From Shareholder의 shareData 찾기
  const selectedOption = fromShareholderOptions.value.find(opt => opt.value === fromShareholder.value);
  if (!selectedOption?.shareData) {
    fromNumberOfShares.value = '';
    fromPaidUpShareCapital.value = '';
    return;
  }
  
  const shareData = selectedOption.shareData;
  const currencies = shareData.currencies || [];
  
  // 선택된 currency 찾기 (currencyValue 형식: "USD_123")
  const [selectedCurrencyCode, shareCapitalIdxStr] = currencyValue.split('_');
  const shareCapitalIdx = parseInt(shareCapitalIdxStr, 10);
  
  const selectedCurrency = currencies.find((curr: any) => 
    (curr.currency === selectedCurrencyCode || curr.currencyCode === selectedCurrencyCode) &&
    curr.shareCapitalIdx === shareCapitalIdx
  );
  
  if (!selectedCurrency) {
    fromNumberOfShares.value = '';
    fromPaidUpShareCapital.value = '';
    return;
  }
  
  // SHAREHOLDER_IDX 확인 (selectedCurrency에서 가져오기)
  const shareholderIdx = selectedCurrency.shareholderIdx || shareData.shareholderIdx;
  
  // 서브클래스 확인 (선택한 Share Type에 해당하는 SUB_CLASS 값 확인)
  if (shareholderIdx) {
    const hasSub = await checkSubClass(shareholderIdx, shareTypeValue);
    
    // 선택한 Share Type에 서브클래스가 있으면 자동 바인딩 하지 않음
    if (hasSub) {
      fromNumberOfShares.value = '';
      fromPaidUpShareCapital.value = '';
      return;
    }
  }
  
  // Share Type에 따라 해당하는 값 바인딩
  // A20001 또는 A20001000 = Ordinary
  // A20002 또는 A20002000 = Preference
  // A20003 또는 A20003000 = Others
  let numberOfShares = 0;
  let paidUpCapital = 0;
  
  if (shareTypeValue === 'A20001' || shareTypeValue === 'A20001000' || shareTypeValue.startsWith('A20001')) {
    numberOfShares = selectedCurrency.sharesOrdinary || 0;
    paidUpCapital = selectedCurrency.paidUpOrdinary || 0;
  } else if (shareTypeValue === 'A20002' || shareTypeValue === 'A20002000' || shareTypeValue.startsWith('A20002')) {
    numberOfShares = selectedCurrency.sharesPreference || 0;
    paidUpCapital = selectedCurrency.paidUpPreference || 0;
  } else if (shareTypeValue === 'A20003' || shareTypeValue === 'A20003000' || shareTypeValue.startsWith('A20003')) {
    numberOfShares = selectedCurrency.sharesOthers || 0;
    paidUpCapital = selectedCurrency.paidUpOthers || 0;
  }
  
  fromNumberOfShares.value = String(numberOfShares);
  fromPaidUpShareCapital.value = String(paidUpCapital);
});

// Load share type (Class of shares) - 전체 옵션 로드 (필터링은 computed에서 수행)
const loadShareType = async () => {
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'A20', undefined);
    const codes = (response as any).codes || response.data?.codes || [];
    const unique = new Map<string, { title: string; value: string }>();
    codes.forEach((code: any) => {
      const total = String(code.totalCode || code.TOTAL_CODE || '').trim();
      const name = String(code.codeName || code.CODE_NAME || code.defaultCodeName || code.DEFAULT_CODE_NAME || '').trim();
      // 빈 값이나 placeholder 라벨 필터링
      if (total && name && !unique.has(total) && 
          total !== '' && name !== '' && 
          !name.toLowerCase().includes('please select') &&
          !name.toLowerCase().includes('select') &&
          total.slice(-3) !== '000') {
        unique.set(total, { title: name, value: total });
      }
    });
    allShareTypeOptions.value = Array.from(unique.values()).filter(item => item.value && item.value !== '');
  } catch (error) {
    console.error('Failed to load share type', error);
    allShareTypeOptions.value = [];
  }
};

// Certificate Type Options 로드 (EntitySharesSection.vue와 동일하게 getEntityShares API 사용)
const loadCertificateTypes = async (entityId?: string) => {
  const targetEntityId = entityId || props.entityId;
  if (!targetEntityId) {
    certificateTypeOptions.value = [];
    return;
  }
  
  try {
    // EntitySharesSection.vue와 동일하게 getEntityShares API 사용
    const response = await entityApi.getEntityShares(targetEntityId);
    const data = (response as any).data?.data || (response as any).data || {};
    const certificateTypeData = data.certificateTypeOptions || [];
    
    certificateTypeOptions.value = Array.isArray(certificateTypeData)
      ? certificateTypeData.map((item: any) => ({
          title: item.title || item.codeName || item.defaultCodeName || item.totalCode || item.TOTAL_CODE || '',
          value: item.value || item.totalCode || item.TOTAL_CODE || ''
        })).filter((item: any) => item.title)
      : [];
  } catch (error) {
    console.error('Failed to load certificate types', error);
    certificateTypeOptions.value = [];
  }
};

// Certificate Type Options 로드 (A19) - 백업용 (loadCertificateTypes 사용 시 필요 없을 수 있음)
const loadCertificateTypeOptions = async () => {
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'A19', undefined);
    const codes = (response as any).codes || response.data?.codes || [];
    const unique = new Map<string, { title: string; value: string }>();
    codes.forEach((code: any) => {
      const total = String(code.totalCode || code.TOTAL_CODE || '').trim();
      if (!total || unique.has(total) || total.slice(-3) === '000') {
        return;
      }
      const codeName = String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim();
      if (codeName) {
        unique.set(total, {
          title: codeName,
          value: total
        });
      }
    });
    certificateTypeOptions.value = Array.from(unique.values());
    console.log('✅ Certificate Type options loaded:', certificateTypeOptions.value.length);
  } catch (error) {
    console.error('❌ Failed to load certificate type options:', error);
    certificateTypeOptions.value = [];
  }
};

// Chair Person 옵션 로드 (Entity의 Shareholder Contacts)
const loadChairPersonOptions = async (entityId?: string) => {
  const targetEntityId = entityId || props.entityId;
  if (!targetEntityId) {
    chairPersonOptions.value = [];
    return;
  }
  try {
    const response = await entityApi.getEntityShareContacts(targetEntityId);
    const contacts = response.data?.data?.contacts || response.data?.contacts || [];
    // 배열 체크 추가
    const options = Array.isArray(contacts)
      ? contacts.map((contact: any) => {
          const code = contact.CONTACT_CD || contact.contactCd || '';
          const name = contact.CONTACT_NAME || contact.contactName || '';
          return {
            title: name || code,
            value: code
          };
        })
      : [];
    chairPersonOptions.value = options;
  } catch (error) {
    console.error('Failed to load chair person options', error);
    chairPersonOptions.value = [];
  }
};

// Representative Director 옵션 로드 (Entity의 Director 목록)
const loadRepresentativeDirectorOptions = async (entityId?: string) => {
  const targetEntityId = entityId || props.entityId;
  if (!targetEntityId) {
    representativeDirectorOptions.value = [];
    return;
  }
  try {
    // Director positionCd는 'B30027' (일반 Director)
    const response = await getOfficersByEntity(targetEntityId, 'B30027');
    const officers = (response as any).data?.officers || (response as any).officers || [];
    // 배열 체크 추가
    const options = Array.isArray(officers)
      ? officers.map((officer: any) => {
          const code = officer.contactCd || officer.CONTACT_CD || '';
          const name = officer.contactName || officer.CONTACT_NAME || '';
          return {
            title: name || code,
            value: code
          };
        }).filter((opt: any) => opt.title && opt.value)
      : [];
    representativeDirectorOptions.value = options;
  } catch (error) {
    console.error('Failed to load representative director options', error);
    representativeDirectorOptions.value = [];
  }
};

// RORC 옵션 로드 (Entity의 Controller 목록)
const loadRorcOptions = async (entityId?: string) => {
  const targetEntityId = entityId || props.entityId;
  if (!targetEntityId) {
    rorcOptions.value = [];
    return;
  }
  try {
    const response = await getControllersByEntity(targetEntityId);
    const responseData = (response as any).data || response;
    const controllers = responseData?.controllers || responseData?.data?.controllers || [];
    const options = Array.isArray(controllers) ? controllers.map((controller: any) => {
      const id = controller.REGISTRABLE_CONTROLLER_ID || controller.registrableControllerId || '';
      const name = controller.FULL_NAME || controller.fullName || '';
      return {
        title: name || id,
        value: id
      };
    }).filter((opt: any) => opt.title && opt.value) : [];
    rorcOptions.value = options;
  } catch (error) {
    console.error('Failed to load RORC options', error);
    rorcOptions.value = [];
  }
};

// REPRESENTATIVE 옵션 로드 (RORC 선택 시 해당 Controller가 법인인 경우 해당 법인의 Director와 Shareholder)
const loadRepresentativeOptions = async (rorcId?: string) => {
  if (!rorcId || !props.entityId) {
    representativeOptions.value = [];
    return;
  }
  
  try {
    // Controller 상세 정보 조회
    const controllerResponse = await getControllerDetail(props.entityId, rorcId);
    const controllerData = (controllerResponse as any).data?.controller || 
                          (controllerResponse as any).data?.data?.controller || 
                          (controllerResponse as any).data || {};
    
    const categoryCode = controllerData.categoryCode || controllerData.CATEGORY_CD || '';
    const contactCd = controllerData.contactCd || controllerData.CONTACT_CD || '';
    
    // 법인인 경우만 (B09001 = Corporate)
    if (categoryCode === 'B09001' && contactCd) {
      // Director와 Shareholder 목록 병렬 조회
      const [directorResponse, shareholderResponse] = await Promise.all([
        getOfficersByEntity(contactCd, 'B30027'),
        entityApi.getEntityShareContacts(contactCd)
      ]);
      
      const directors = (directorResponse as any).data?.officers || 
                       (directorResponse as any).officers || [];
      const shareholders = (shareholderResponse as any).data?.data?.contacts || 
                          (shareholderResponse as any).data?.contacts || 
                          (shareholderResponse as any).contacts || [];
      
      // Director 옵션 생성 (배열 체크 추가)
      const directorOptions = Array.isArray(directors)
        ? directors.map((director: any) => ({
            title: director.contactName || director.CONTACT_NAME || '',
            value: director.contactCd || director.CONTACT_CD || ''
          })).filter((opt: any) => opt.title && opt.value)
        : [];
      
      // Shareholder 옵션 생성 (배열 체크 추가)
      const shareholderOptions = Array.isArray(shareholders)
        ? shareholders.map((shareholder: any) => ({
            title: shareholder.CONTACT_NAME || shareholder.contactName || '',
            value: shareholder.CONTACT_CD || shareholder.contactCd || ''
          })).filter((opt: any) => opt.title && opt.value)
        : [];
      
      // 두 목록 합치기 (중복 제거)
      const allOptions = [...directorOptions, ...shareholderOptions];
      const uniqueOptions = Array.from(
        new Map(allOptions.map(opt => [opt.value, opt])).values()
      );
      
      representativeOptions.value = uniqueOptions;
    } else {
      // 법인이 아니면 빈 목록
      representativeOptions.value = [];
    }
  } catch (error) {
    console.error('Failed to load REPRESENTATIVE options', error);
    representativeOptions.value = [];
  }
};

// RORC 변경 시 REPRESENTATIVE 옵션 로드
watch(rorc, async (newRorcId) => {
  if (newRorcId) {
    await loadRepresentativeOptions(newRorcId);
  } else {
    representativeOptions.value = [];
    representative.value = '';
  }
});

// Load Entity Shares (From Shareholder용 - 등록된 주주 목록)
// 새로운 API 사용: /startTask/shares-transfer/shareholders/:entityCd
const loadEntityShares = async (entityId?: string) => {
  const targetEntityId = entityId || props.entityId || entity.value;
  if (!targetEntityId) {
    entitySharesData.value = [];
    fromShareholderOptions.value = [];
    return;
  }
  
  try {
    // 새로운 Shares Transfer 전용 API 호출
    const response = await apiClient.get(`/startTask/shares-transfer/shareholders/${targetEntityId}`) as any;
    
    console.log('🔄 [loadEntityShares] Shares Transfer Shareholders API 응답:', {
      entityId: targetEntityId,
      response
    });
    
    // 응답 구조: { success: true, data: { shareholders: [...] } }
    const responseData = response?.data || response;
    const shareholders = Array.isArray(responseData?.shareholders) 
      ? responseData.shareholders 
      : [];
    
    console.log('🔄 [loadEntityShares] Shareholders 추출:', {
      entityId: targetEntityId,
      shareholdersCount: shareholders.length,
      shareholders
    });
    
    if (!shareholders || shareholders.length === 0) {
      entitySharesData.value = [];
      fromShareholderOptions.value = [];
      console.log('⚠️ [loadEntityShares] Shareholders가 없습니다');
      return;
    }
    
    // 주주 데이터 저장 (API에서 이미 필터링 및 중복 제거 완료)
    entitySharesData.value = shareholders.map((shareholder: any) => {
      const shareData = shareholder.shareData || {};
      return {
        name: shareData.name || shareholder.title || '',
        idNumber: shareData.idNumber || shareData.idOfRegistrationNumber || shareData.contactRegNo || '',
        numberOfShares: shareData.numberOfShares || 0,
        paidUpCapital: shareData.paidUpCapital || 0,
        shareType: shareData.shareType || 'Ordinary',
        shareholderIdx: shareData.shareholderIdx || null,
        shareCapitalIdx: shareData.shareCapitalIdx || null,
        currencyCode: shareData.currencyCode || '',
        contactCd: shareholder.contactCd || shareholder.value || null,
        registerType: 'A06001', // API에서 이미 필터링되므로 항상 A06001
        categoryCd: shareData.categoryCd || ''
      };
    });
    
    // From Shareholder Options 생성 (API에서 이미 title과 value가 설정됨)
    fromShareholderOptions.value = shareholders.map((shareholder: any, index: number) => {
      const shareData = shareholder.shareData || {};
      const uniqueKey = shareData.shareCapitalIdx && shareData.shareholderIdx 
        ? `${shareData.shareCapitalIdx}_${shareData.shareholderIdx}` 
        : shareholder.value || `share_${index}`;
      
      // contactCd 우선순위: shareData.contactCd > shareholder.contactCd > shareholder.value
      // 백엔드 응답 구조: { contactCd, value, shareData: { contactCd, ... } }
      const contactCd = shareData.contactCd || shareholder.contactCd || shareholder.value || '';
      
      console.log('🔍 [loadEntityShares] Shareholder 옵션 생성:', {
        index,
        shareholderContactCd: shareholder.contactCd,
        shareholderValue: shareholder.value,
        shareDataContactCd: shareData.contactCd,
        finalContactCd: contactCd,
        uniqueKey,
        shareholder: {
          title: shareholder.title,
          contactCd: shareholder.contactCd,
          value: shareholder.value
        }
      });
      
      return {
        title: shareholder.title || shareData.name || `Shareholder ${index + 1}`,
        value: contactCd || uniqueKey, // contactCd를 우선 사용, 없으면 uniqueKey 사용 (toShareholderOptions와 동일한 패턴)
        contactCd: contactCd, // 옵션 자체에도 contactCd 추가
        shareData: {
          ...shareData,
          contactCd: contactCd, // shareData에도 확실히 설정
          name: shareholder.title || shareData.name || '',
          idNumber: shareData.idNumber || shareData.contactRegNo || '',
          // uniqueKey 정보도 shareData에 보관 (필요시 사용)
          uniqueKey: uniqueKey
        }
      };
    });
    
    console.log('✅ [loadEntityShares] Entity Shares loaded:', {
      count: entitySharesData.value.length,
      options: fromShareholderOptions.value.length,
      firstOption: fromShareholderOptions.value[0]
    });
  } catch (error) {
    console.error('❌ [loadEntityShares] Failed to load entity shares:', error);
    entitySharesData.value = [];
    fromShareholderOptions.value = [];
  }
};

// Load shareholder contacts (To Shareholder용 - 연락처 목록)
// 새로운 API를 사용하여 우선순위별로 바인딩:
// 1. From (Transferor) 쿼리로 조회한 사람들 (excludeContactCd 제외)
// 2. L_OFFICER에서 REGISTER_TYPE='A06001', BIZFILE_POSITIONHELD='LC_DIR' 조회
// 3. 구분선 "-----------"
// 4. L_GLOBAL_CONTACT에서 L_CLIENT_CONTACT_MAP으로 필터링
const loadShareholderContacts = async (entityId?: string, excludeContactCd?: string) => {
  console.log('🚀 [loadShareholderContacts] 함수 호출됨:', {
    entityId,
    excludeContactCd,
    propsEntityId: props.entityId,
    timestamp: Date.now()
  });
  
  // 로딩 상태 설정 (UI에 로딩바 표시)
  isLoadingShareholders.value = true;
  console.log('⏳ [loadShareholderContacts] isLoadingShareholders = true 설정됨');
  
  try {
    const targetEntityId = entityId || props.entityId || entity.value;
    if (!targetEntityId) {
      // Allotment와 동일하게 조용히 반환 (에러 메시지 없음)
      toShareholderOptions.value = [];
      originalToShareholderOptions.value = [];
      isLoadingShareholders.value = false;
      return;
    }
    
    console.log('✅ [loadShareholderContacts] Entity ID 확인됨, API 호출 시작:', {
      targetEntityId,
      excludeContactCd
    });

    // 새로운 To (Transferee) API 호출
    // 캐시 무시를 위해 타임스탬프 파라미터 추가 (재조회 보장)
    const trimmedExcludeContactCd = excludeContactCd ? String(excludeContactCd).trim() : null;
    const timestamp = Date.now();
    const excludeParam = trimmedExcludeContactCd ? `excludeContactCd=${encodeURIComponent(trimmedExcludeContactCd)}` : '';
    const timestampParam = `_t=${timestamp}`;
    const queryParams = [excludeParam, timestampParam].filter(p => p).join('&');
    const apiUrl = `/startTask/shares-transfer/transferees/${targetEntityId}${queryParams ? `?${queryParams}` : ''}`;
    
    console.log('🔄 [loadShareholderContacts] API 호출 준비:', {
      entityId: targetEntityId,
      excludeContactCd: trimmedExcludeContactCd,
      timestamp,
      apiUrl,
      willForceRefresh: !!trimmedExcludeContactCd
    });
    
    const response = await apiClient.get(apiUrl) as any;
    
    console.log('🔄 [loadShareholderContacts] To (Transferee) Shareholder API 호출 완료:', {
      entityId: targetEntityId,
      excludeContactCd: trimmedExcludeContactCd,
      originalExcludeContactCd: excludeContactCd,
      excludeContactCdType: typeof excludeContactCd,
      excludeContactCdLength: trimmedExcludeContactCd ? trimmedExcludeContactCd.length : 0,
      apiUrl,
      timestamp,
      responseStatus: response?.status,
      responseData: response?.data
    });
    
    // 응답 구조: { success: true, data: { transferees: [...] } }
    const responseData = response?.data || response;
    const transferees = Array.isArray(responseData?.transferees)
      ? responseData.transferees
      : [];
    
    // excludeContactCd가 포함된 항목이 있는지 확인 (대소문자 구분 없이 비교)
    const excludedItems = trimmedExcludeContactCd ? transferees.filter((t: any) => {
      const tContactCd = (t.contactCd || '').toString().trim().toUpperCase();
      const excludeCd = trimmedExcludeContactCd.toUpperCase();
      return tContactCd === excludeCd;
    }) : [];
    
    if (excludedItems.length > 0) {
      console.warn('⚠️ [loadShareholderContacts] excludeContactCd가 결과에 포함됨:', {
        excludeContactCd: trimmedExcludeContactCd,
        originalExcludeContactCd: excludeContactCd,
        excludedItems,
        excludedItemsCount: excludedItems.length,
        allTransferees: transferees
      });
    } else if (trimmedExcludeContactCd) {
      console.log('✅ [loadShareholderContacts] excludeContactCd가 결과에서 제외됨:', {
        excludeContactCd: trimmedExcludeContactCd,
        originalExcludeContactCd: excludeContactCd
      });
    }
    
    console.log('🔄 [loadShareholderContacts] Transferees 추출:', {
      entityId: targetEntityId,
      excludeContactCd: trimmedExcludeContactCd,
      originalExcludeContactCd: excludeContactCd,
      transfereesCount: transferees.length,
      excludedItemsCount: excludedItems.length,
      transferees: transferees.slice(0, 10) // 처음 10개만 로그 (너무 길면 생략)
    });
    
    if (!transferees || transferees.length === 0) {
      toShareholderOptions.value = [];
      originalToShareholderOptions.value = [];
      console.log('⚠️ [loadShareholderContacts] Transferees가 없습니다');
      return;
    }
    
    // excludeContactCd가 있으면 프론트엔드에서도 추가 필터링 (이중 안전장치)
    let filteredTransferees = transferees;
    if (trimmedExcludeContactCd) {
      const excludeCdUpper = trimmedExcludeContactCd.toUpperCase();
      let excludedCount = 0;
      
      filteredTransferees = transferees.filter((t: any) => {
        // 구분선은 유지
        if (t.isDivider) return true;
        
        // 모든 가능한 필드에서 contactCd 확인
        const possibleContactCds = [
          t.contactCd,
          t.value,
          t.CONTACT_CD,
          t.contact_cd,
          t.contactCode,
          t.contact_code
        ].filter(cd => cd != null && cd !== '');
        
        // 각 contactCd와 비교 (대소문자 구분 없이)
        const shouldExclude = possibleContactCds.some(cd => {
          const cdUpper = String(cd).trim().toUpperCase();
          return cdUpper === excludeCdUpper;
        });
        
        if (shouldExclude) {
          excludedCount++;
          console.warn('🚫 [loadShareholderContacts] 프론트엔드 필터링으로 제외된 CONTACT_CD:', {
            contactCd: possibleContactCds[0],
            excludeContactCd: trimmedExcludeContactCd,
            excludeCdUpper,
            possibleContactCds,
            transferee: {
              title: t.title,
              value: t.value,
              contactCd: t.contactCd,
              contactName: t.contactName
            }
          });
        }
        
        return !shouldExclude;
      });
      
      if (excludedCount > 0) {
        console.log(`✅ [loadShareholderContacts] 프론트엔드 필터링으로 ${excludedCount}건 제외됨`, {
          excludeContactCd: trimmedExcludeContactCd,
          beforeCount: transferees.length,
          afterCount: filteredTransferees.length
        });
      } else {
        // 경고 로그 제거 (디버깅용이므로 불필요)
        // console.warn('⚠️ [loadShareholderContacts] 프론트엔드 필터링에서 제외된 항목이 없습니다...');
      }
    }
    
    // 원본 목록 저장 (구분선 제외)
    originalToShareholderOptions.value = filteredTransferees.filter((t: any) => !t.isDivider);
    
    // 옵션 목록 생성 (구분선 포함)
    toShareholderOptions.value = filteredTransferees.map((transferee: any) => ({
      title: transferee.title || transferee.contactName || transferee.contactCd || '',
      value: transferee.value || transferee.contactCd || '',
      contactCd: transferee.contactCd || '',
      contactName: transferee.contactName || '',
      contactRegNo: transferee.contactRegNo || '',
      categoryCd: transferee.categoryCd || '',
      priority: transferee.priority || 0,
      isDivider: transferee.isDivider || false
    }));
    
    console.log('✅ [loadShareholderContacts] To (Transferee) Shareholder loaded:', {
      entityId: targetEntityId,
      excludeContactCd: trimmedExcludeContactCd,
      originalExcludeContactCd: excludeContactCd,
      totalCount: toShareholderOptions.value.length,
      dividerCount: toShareholderOptions.value.filter((opt: any) => opt.isDivider).length,
      excludedInFinalResult: excludedItems.length,
      beforeFilterCount: transferees.length,
      afterFilterCount: filteredTransferees.length,
      optionsUpdated: true,
      timestamp
    });
    
    // 재조회 완료 확인 로그
    if (trimmedExcludeContactCd) {
      const stillContainsExcluded = toShareholderOptions.value.some((opt: any) => {
        if (opt.isDivider) return false;
        const optContactCd = (opt.contactCd || opt.value || '').toString().trim().toUpperCase();
        return optContactCd === trimmedExcludeContactCd.toUpperCase();
      });
      
      if (stillContainsExcluded) {
        console.error('❌ [loadShareholderContacts] 재조회 후에도 제외 대상이 여전히 포함되어 있습니다!', {
          excludeContactCd: trimmedExcludeContactCd,
          options: toShareholderOptions.value.filter((opt: any) => {
            if (opt.isDivider) return false;
            const optContactCd = (opt.contactCd || opt.value || '').toString().trim().toUpperCase();
            return optContactCd === trimmedExcludeContactCd.toUpperCase();
          })
        });
      } else {
        console.log('✅ [loadShareholderContacts] 재조회 완료 - 제외 대상이 목록에서 제거되었습니다.', {
          excludeContactCd: trimmedExcludeContactCd,
          totalOptions: toShareholderOptions.value.length
        });
      }
    }
  } catch (error) {
    console.error('❌ [loadShareholderContacts] Failed to load To (Transferee) Shareholder list:', error);
    toShareholderOptions.value = [];
    originalToShareholderOptions.value = [];
  } finally {
    isLoadingShareholders.value = false;
    console.log('✅ [loadShareholderContacts] isLoadingShareholders = false 설정됨 (로딩 완료)');
  }
};

// 초기 데이터 로드
onMounted(async () => {
  try {
    await Promise.all([
      officerAppointmentStore.fetchEntities(),
      loadShareType(),
      loadCertificateTypeOptions(),
      loadBaseCodeOptions(),
      loadShareholderContacts() // To Shareholder용 (연락처 - 엔티티 선택 여부와 관계없이 로드)
    ]);
    if (props.entityId) {
      entity.value = props.entityId;
      await Promise.all([
        loadEntityShares(props.entityId), // From Shareholder용 (Entity Shares)
        loadShareholderContacts(props.entityId), // To Shareholder용 (연락처 - 엔티티 우선 정렬)
        loadChairPersonOptions(props.entityId),
        loadCertificateTypes(props.entityId),
        loadRepresentativeDirectorOptions(props.entityId),
        loadRorcOptions(props.entityId)
      ]);
      // 첫 번째 Shareholder 섹션 펼치기
      if (shareholderTransferSections.value.length > 0) {
        allExpandedPanels.value = [shareholderTransferSections.value[0].id];
      }
    }
  } catch (error) {
    console.error('Failed to fetch initial data:', error);
  }
});

// From Shareholder Contact 정보 로드 (첫 번째 섹션용)
const loadFromShareholderContactData = async (contactCd: string) => {
  if (!contactCd) {
    // Contact 데이터 제거 (첫 번째 섹션)
    if (shareholderTransferSections.value[0]) {
      const contactData = shareholderTransferContactData.value.get(shareholderTransferSections.value[0].id) || {};
      delete contactData.from;
      if (Object.keys(contactData).length === 0) {
        shareholderTransferContactData.value.delete(shareholderTransferSections.value[0].id);
      } else {
        shareholderTransferContactData.value.set(shareholderTransferSections.value[0].id, contactData);
      }
    }
    return;
  }

  try {
    const response = await contactsApi.getContactById(contactCd);
    const contact = (response as any).contact || response.data?.contact;

    if (contact && shareholderTransferSections.value[0]) {
      const contactData = shareholderTransferContactData.value.get(shareholderTransferSections.value[0].id) || {};
      contactData.from = contact;
      shareholderTransferContactData.value.set(shareholderTransferSections.value[0].id, contactData);
    }
  } catch (error: any) {
    console.error('❌ Failed to fetch from shareholder contact details:', error);
    // Contact 데이터 제거 (첫 번째 섹션)
    if (shareholderTransferSections.value[0]) {
      const contactData = shareholderTransferContactData.value.get(shareholderTransferSections.value[0].id) || {};
      delete contactData.from;
      if (Object.keys(contactData).length === 0) {
        shareholderTransferContactData.value.delete(shareholderTransferSections.value[0].id);
      } else {
        shareholderTransferContactData.value.set(shareholderTransferSections.value[0].id, contactData);
      }
    }
  }
};

// To Shareholder Contact 정보 로드 (첫 번째 섹션용)
const loadToShareholderContactData = async (contactCd: string) => {
  if (!contactCd) {
    // Contact 데이터 제거 (첫 번째 섹션)
    if (shareholderTransferSections.value[0]) {
      const contactData = shareholderTransferContactData.value.get(shareholderTransferSections.value[0].id) || {};
      delete contactData.to;
      if (Object.keys(contactData).length === 0) {
        shareholderTransferContactData.value.delete(shareholderTransferSections.value[0].id);
      } else {
        shareholderTransferContactData.value.set(shareholderTransferSections.value[0].id, contactData);
      }
    }
    return;
  }

  try {
    const response = await contactsApi.getContactById(contactCd);
    const contact = (response as any).contact || response.data?.contact;

    if (contact && shareholderTransferSections.value[0]) {
      const contactData = shareholderTransferContactData.value.get(shareholderTransferSections.value[0].id) || {};
      contactData.to = contact;
      shareholderTransferContactData.value.set(shareholderTransferSections.value[0].id, contactData);
    }
  } catch (error: any) {
    console.error('❌ Failed to fetch to shareholder contact details:', error);
    // Contact 데이터 제거 (첫 번째 섹션)
    if (shareholderTransferSections.value[0]) {
      const contactData = shareholderTransferContactData.value.get(shareholderTransferSections.value[0].id) || {};
      delete contactData.to;
      if (Object.keys(contactData).length === 0) {
        shareholderTransferContactData.value.delete(shareholderTransferSections.value[0].id);
      } else {
        shareholderTransferContactData.value.set(shareholderTransferSections.value[0].id, contactData);
      }
    }
  }
};

// To Shareholder 선택 시 Contact 정보 로드 (첫 번째 섹션 - 기존 호환성)
watch(toShareholder, async (selectedValue) => {
  if (selectedValue) {
    await loadToShareholderContactData(selectedValue);
  } else {
    // Contact 데이터 제거 (첫 번째 섹션)
    if (shareholderTransferSections.value[0]) {
      const contactData = shareholderTransferContactData.value.get(shareholderTransferSections.value[0].id) || {};
      delete contactData.to;
      if (Object.keys(contactData).length === 0) {
        shareholderTransferContactData.value.delete(shareholderTransferSections.value[0].id);
      } else {
        shareholderTransferContactData.value.set(shareholderTransferSections.value[0].id, contactData);
      }
    }
  }
});

// 각 섹션별 To Shareholder 선택 시 Contact 정보 로드 및 문서 자동 선택
watch(
  () => Array.isArray(shareholderTransferSections.value)
    ? shareholderTransferSections.value.map(s => s.toShareholder)
    : [],
  async (newValues, oldValues) => {
    if (!Array.isArray(shareholderTransferSections.value)) return;
    for (let i = 0; i < shareholderTransferSections.value.length; i++) {
      const section = shareholderTransferSections.value[i];
      const newValue = newValues[i];
      const oldValue = oldValues?.[i];
      
      if (newValue === oldValue) continue;
      
      if (newValue) {
        // Contact 정보 로드
        await loadToShareholderContactDataForSection(i, newValue);
      } else {
        // Contact 데이터 제거
        const contactData = shareholderTransferContactData.value.get(section.id) || {};
        delete contactData.to;
        if (Object.keys(contactData).length === 0) {
          shareholderTransferContactData.value.delete(section.id);
        } else {
          shareholderTransferContactData.value.set(section.id, contactData);
        }
      }
    }
    
    // 약간의 지연을 두어 모든 Contact 정보가 로드될 시간 확보
    await new Promise(resolve => setTimeout(resolve, 100));
    await autoSelectTemplatesByShareholders();
  },
  { deep: true }
);

// 주소 표시 함수 (공통 함수 사용)
const formatShareholderAddress = (contactData: any): string => {
  if (!contactData) return '';
  const address = formatAddress(contactData, { useComma: true });
  return typeof address === 'string' ? address : '';
};

const handleNewContact = (type: 'from' | 'to', shareholderIndex: number = 0) => {
  contactListType.value = type;
  currentContactDetailIndex.value = shareholderIndex;
  const section = shareholderTransferSections.value[shareholderIndex];
  if (!section) return;
  
  if (type === 'from') {
    const selectedFromOption = fromShareholderOptions.value.find(opt => opt.value === section.fromShareholder);
    contactListInitialSearch.value = selectedFromOption?.title || '';
  } else {
    const selectedToOption = toShareholderOptions.value.find(opt => opt.value === section.toShareholder);
    contactListInitialSearch.value = selectedToOption?.title || '';
  }
  showContactListModal.value = true;
};

const handleContactSelect = async (contact: any) => {
  const contactName = contact.CONTACT_NAME || `${contact.FIRST_NAME || ''} ${contact.SURNAME || ''}`.trim() || '';
  const contactCd = contact.CONTACT_CD || '';
  const section = shareholderTransferSections.value[currentContactDetailIndex.value];
  if (!section) return;
  
  if (contactListType.value === 'from') {
    // From Shareholder는 Entity Shares에서만 선택 가능하므로 여기서는 스킵
    // 대신 Contact 상세 화면으로 이동
    showContactListModal.value = false;
    currentContactDetailType.value = 'from';
    showContactDetailModal.value = true;
  } else {
    section.toShareholder = contactCd;
    const existingOption = toShareholderOptions.value.find(opt => opt.value === contactCd);
    if (!existingOption && contactName) {
      toShareholderOptions.value.push({
        title: contactName,
        value: contactCd
      });
    }
    await loadToShareholderContactDataForSection(currentContactDetailIndex.value, contactCd);
    showContactListModal.value = false;
  }
};

// Contact 상세 화면에서 선택 시 처리
const handleContactDetailSelect = async (contact: any) => {
  if (contact && (contact.CONTACT_CD || contact.contactCd)) {
    const contactCd = contact.CONTACT_CD || contact.contactCd;
    const contactName = contact.CONTACT_NAME || contact.contactName || `${contact.FIRST_NAME || ''} ${contact.SURNAME || ''}`.trim() || '';
    const section = shareholderTransferSections.value[currentContactDetailIndex.value];
    if (!section) return;
    
    if (currentContactDetailType.value === 'from') {
      // From Shareholder는 Entity Shares에서만 선택 가능
      showContactDetailModal.value = false;
    } else {
      section.toShareholder = contactCd;
      const existingOption = toShareholderOptions.value.find(opt => opt.value === contactCd);
      if (!existingOption && contactName) {
        toShareholderOptions.value.push({
          title: contactName,
          value: contactCd
        });
      }
      await loadToShareholderContactDataForSection(currentContactDetailIndex.value, contactCd);
      showContactDetailModal.value = false;
    }
  }
};

// Contact 상세 화면에서 수정 완료 후 데이터 업데이트
const handleContactUpdated = async (contact: any) => {
  if (!contact) return;
  
  const contactCd = contact.CONTACT_CD || contact.contactCd;
  if (!contactCd) return;
  const section = shareholderTransferSections.value[currentContactDetailIndex.value];
  if (!section) return;
  
  if (currentContactDetailType.value === 'from') {
    await loadFromShareholderContactDataForSection(currentContactDetailIndex.value, contactCd);
  } else {
    await loadToShareholderContactDataForSection(currentContactDetailIndex.value, contactCd);
  }
  
  console.log('✅ [SharesTransferForm] Contact 정보 업데이트 완료:', contact);
};

// 섹션별 Contact 데이터 로드 함수
const loadFromShareholderContactDataForSection = async (shareholderIndex: number, contactCd: string) => {
  if (!contactCd) {
    const section = shareholderTransferSections.value[shareholderIndex];
    if (section) {
      const contactData = shareholderTransferContactData.value.get(section.id) || {};
      delete contactData.from;
      if (Object.keys(contactData).length === 0) {
        shareholderTransferContactData.value.delete(section.id);
      } else {
        shareholderTransferContactData.value.set(section.id, contactData);
      }
    }
    return;
  }

  try {
    const response = await contactsApi.getContactById(contactCd);
    const contact = (response as any).contact || response.data?.contact;
    const section = shareholderTransferSections.value[shareholderIndex];
    if (section && contact) {
      const contactData = shareholderTransferContactData.value.get(section.id) || {};
      contactData.from = contact;
      shareholderTransferContactData.value.set(section.id, contactData);
    }
  } catch (error: any) {
    console.error('❌ Failed to fetch from shareholder contact details:', error);
    const section = shareholderTransferSections.value[shareholderIndex];
    if (section) {
      const contactData = shareholderTransferContactData.value.get(section.id) || {};
      delete contactData.from;
      if (Object.keys(contactData).length === 0) {
        shareholderTransferContactData.value.delete(section.id);
      } else {
        shareholderTransferContactData.value.set(section.id, contactData);
      }
    }
  }
};

const loadToShareholderContactDataForSection = async (shareholderIndex: number, contactCd: string) => {
  if (!contactCd) {
    const section = shareholderTransferSections.value[shareholderIndex];
    if (section) {
      const contactData = shareholderTransferContactData.value.get(section.id) || {};
      delete contactData.to;
      if (Object.keys(contactData).length === 0) {
        shareholderTransferContactData.value.delete(section.id);
      } else {
        shareholderTransferContactData.value.set(section.id, contactData);
      }
    }
    return;
  }

  try {
    const response = await contactsApi.getContactById(contactCd);
    const contact = (response as any).contact || response.data?.contact;
    const section = shareholderTransferSections.value[shareholderIndex];
    if (section && contact) {
      const contactData = shareholderTransferContactData.value.get(section.id) || {};
      contactData.to = contact;
      shareholderTransferContactData.value.set(section.id, contactData);
    }
  } catch (error: any) {
    console.error('❌ Failed to fetch to shareholder contact details:', error);
    const section = shareholderTransferSections.value[shareholderIndex];
    if (section) {
      const contactData = shareholderTransferContactData.value.get(section.id) || {};
      delete contactData.to;
      if (Object.keys(contactData).length === 0) {
        shareholderTransferContactData.value.delete(section.id);
      } else {
        shareholderTransferContactData.value.set(section.id, contactData);
      }
    }
  }
};

// 필수 검증 규칙 (toShareholderRules는 제거하고 :error-messages 사용)

// 섹션별 validation 함수
// 동일한 From Shareholder (같은 currency, shareType)에 대해 모든 섹션의 transfer 주식 수 합산 검증
const getToNumberOfSharesRules = (shareholderIndex: number) => {
  return computed(() => {
    // 초기화 중일 때는 validation 비활성화
    if (isResettingToFields.value) {
      return [];
    }
    
    const section = shareholderTransferSections.value[shareholderIndex];
    if (!section) return [];
    
    return [
      (v: string) => {
        if (!v || v.trim() === '') {
          return t('startTask.Number of Shares is required') || 'Number of Shares is required';
        }
        
        // 동일한 From Shareholder (같은 fromShareholder, currency, shareType)를 가진 모든 섹션 찾기
        const sameFromShareholderSections = Array.isArray(shareholderTransferSections.value)
          ? shareholderTransferSections.value.filter(s =>
              s.fromShareholder === section.fromShareholder &&
              s.currency === section.currency &&
              s.shareType === section.shareType &&
              s.fromShareholder !== '' &&
              s.currency !== '' &&
              s.shareType !== ''
            )
          : [];
        
        // 해당 From Shareholder가 가진 총 주식 수
        const fromShares = Number(section.fromNumberOfShares || 0);
        
        if (fromShares === 0) {
          const toShares = Number(v || 0);
          if (toShares > 0) {
            return t('startTask.Number of shares transferred cannot exceed the number of shares held by the shareholder/transferor') || 
                   'Number of shares *transferred* *cannot exceed* the *number of shares* *held by the shareholder/transferor*.';
          }
          return true;
        }
        
        // 동일한 From Shareholder를 가진 모든 섹션의 transfer 주식 수 합산
        // 현재 섹션의 값은 v로 사용 (입력 중인 값)
        const totalTransferredShares = sameFromShareholderSections.reduce((sum, s) => {
          if (s.id === section.id) {
            // 현재 검증 중인 섹션은 입력 중인 값(v) 사용
            const transferred = Number(v || 0);
            return sum + (isNaN(transferred) ? 0 : transferred);
          } else {
            // 다른 섹션은 이미 입력된 값 사용
            const transferred = Number(s.toNumberOfShares || 0);
            return sum + (isNaN(transferred) ? 0 : transferred);
          }
        }, 0);
        
        // 합산된 총 transfer 주식 수가 From Shareholder가 가진 총 주식 수를 초과하면 에러
        if (totalTransferredShares > fromShares) {
          return t('startTask.Number of shares transferred cannot exceed the number of shares held by the shareholder/transferor') || 
                 'Number of shares *transferred* *cannot exceed* the *number of shares* *held by the shareholder/transferor*.';
        }
        
        return true;
      }
    ];
  });
};

// 섹션별 To Number of Shares 에러 메시지 계산 및 업데이트 함수
const updateToNumberOfSharesErrors = async () => {
  // 초기화 중일 때는 에러 표시하지 않음
  if (isResettingToFields.value) {
    shareholderTransferSections.value.forEach((section, index) => {
      section.toNumberOfSharesError = '';
      const errors = sectionValidationErrors.value.get(index);
      if (errors) {
        const updatedErrors = { ...errors };
        delete updatedErrors.toNumberOfShares;
        if (Object.keys(updatedErrors).length > 0) {
          sectionValidationErrors.value.set(index, updatedErrors);
        } else {
          sectionValidationErrors.value.delete(index);
        }
      }
    });
    return;
  }
  
  // 먼저 모든 섹션의 에러 메시지 초기화
  shareholderTransferSections.value.forEach((section, index) => {
    section.toNumberOfSharesError = '';
    const errors = sectionValidationErrors.value.get(index);
    if (errors && errors.toNumberOfShares) {
      const updatedErrors = { ...errors };
      delete updatedErrors.toNumberOfShares;
      if (Object.keys(updatedErrors).length > 0) {
        sectionValidationErrors.value.set(index, updatedErrors);
      } else {
        sectionValidationErrors.value.delete(index);
      }
    }
  });
  
  // nextTick을 사용하여 DOM 업데이트 보장
  await nextTick();
  
  // 각 섹션에 대해 에러 메시지 계산
  shareholderTransferSections.value.forEach((section, index) => {
    // 값이 없으면 에러 표시하지 않음
    if (!section.toNumberOfShares || section.toNumberOfShares.trim() === '') {
      return;
    }
    
    // 동일한 From Shareholder (같은 fromShareholder, currency, shareType)를 가진 모든 섹션 찾기
    const sameFromShareholderSections = Array.isArray(shareholderTransferSections.value)
      ? shareholderTransferSections.value.filter(s => 
          s.fromShareholder === section.fromShareholder &&
          s.currency === section.currency &&
          s.shareType === section.shareType &&
          s.fromShareholder !== '' &&
          s.currency !== '' &&
          s.shareType !== ''
        )
      : [];
    
    // 해당 From Shareholder가 가진 총 주식 수
    const fromShares = Number(section.fromNumberOfShares || 0);
    
    const errorMessage = t('startTask.Number of shares transferred cannot exceed the number of shares held by the shareholder/transferor') || 
      'Number of shares transferred cannot exceed the number of shares held by the shareholder/transferor.';
    
    if (fromShares === 0) {
      if (Number(section.toNumberOfShares || 0) > 0) {
        // sectionValidationErrors에 추가
        const errors = sectionValidationErrors.value.get(index) || {};
        errors.toNumberOfShares = errorMessage;
        sectionValidationErrors.value.set(index, errors);
      }
      return;
    }
    
    // 동일한 From Shareholder를 가진 모든 섹션의 transfer 주식 수 합산
    const totalTransferredShares = sameFromShareholderSections.reduce((sum, s) => {
      const transferred = Number(s.toNumberOfShares || 0);
      return sum + (isNaN(transferred) ? 0 : transferred);
    }, 0);
    
    // 합산된 총 transfer 주식 수가 From Shareholder가 가진 총 주식 수를 초과하면 에러
    if (totalTransferredShares > fromShares) {
      // 동일한 From Shareholder를 가진 모든 섹션에 에러 표시
      sameFromShareholderSections.forEach(s => {
        const sectionIndex = shareholderTransferSections.value.findIndex(sec => sec.id === s.id);
        if (sectionIndex !== -1) {
          const errors = sectionValidationErrors.value.get(sectionIndex) || {};
          errors.toNumberOfShares = errorMessage;
          sectionValidationErrors.value.set(sectionIndex, errors);
        }
      });
    }
  });
  
  // 모든 필드의 validation을 강제로 다시 실행
  await nextTick();
  toNumberOfSharesFieldRefs.value.forEach((fieldRef) => {
    if (fieldRef && typeof fieldRef.validate === 'function') {
      fieldRef.validate();
    }
  });
};

// 섹션별 To Number of Shares 에러 메시지 가져오기 (섹션 객체에서 직접 가져옴)
const getToNumberOfSharesError = (shareholderIndex: number) => {
  return shareholderTransferSections.value[shareholderIndex]?.toNumberOfSharesError || '';
};

// 모든 섹션의 toNumberOfShares 변경 감지하여 에러 업데이트
// watchEffect를 사용하여 모든 의존성을 자동으로 추적
watchEffect(async () => {
  // 모든 관련 필드를 참조하여 반응성 보장
  shareholderTransferSections.value.forEach(s => {
    // 각 필드를 명시적으로 참조
    const _ = s.toNumberOfShares;
    const __ = s.fromShareholder;
    const ___ = s.currency;
    const ____ = s.shareType;
    const _____ = s.fromNumberOfShares;
  });
  
  // nextTick 후 에러 업데이트
  await nextTick();
  await updateToNumberOfSharesErrors();
}, { flush: 'post' });

// To Number of Shares 검증 규칙 (From의 Number of Shares와 비교) - 기존 호환성
const toNumberOfSharesRules = computed(() => {
  return [
    (v: string) => {
      if (!v || v.trim() === '') {
        return t('startTask.Number of Shares is required') || 'Number of Shares is required';
      }
      
      // From의 Number of Shares와 비교
      const fromShares = Number(fromNumberOfShares.value) || 0;
      const toShares = Number(v) || 0;
      
      // From이 0이거나 To가 From보다 크면 에러
      if ((fromShares === 0 && toShares > 0) || (toShares > fromShares && fromShares > 0)) {
        // 에러는 error-messages로 표시
        return false;
      }
      
      return true;
    }
  ];
});

// To Number of Shares 에러 메시지 (computed)
const toNumberOfSharesError = computed(() => {
  if (!toNumberOfShares.value || toNumberOfShares.value.trim() === '') {
    return '';
  }
  
  const fromShares = Number(fromNumberOfShares.value) || 0;
  const toShares = Number(toNumberOfShares.value) || 0;
  
  // From이 0이거나 To가 From보다 크면 경고 표시
  if (fromShares === 0 && toShares > 0) {
    return t('startTask.Number of shares transferred cannot exceed the number of shares held by the shareholder/transferor') || 
           'Number of shares *transferred* *cannot exceed* the *number of shares* *held by the shareholder/transferor*.';
  }
  
  if (toShares > fromShares && fromShares > 0) {
    return t('startTask.Number of shares transferred cannot exceed the number of shares held by the shareholder/transferor') || 
           'Number of shares *transferred* *cannot exceed* the *number of shares* *held by the shareholder/transferor*.';
  }
  
  return '';
});

// 섹션별 Currency 에러 메시지 가져오기
const getCurrencyErrorMessages = (shareholderIndex: number): string[] => {
  const errors = sectionValidationErrors.value.get(shareholderIndex);
  if (errors?.currency) {
    return [errors.currency];
  }
  return [];
};

// 섹션별 Share Type 에러 메시지 가져오기
const getShareTypeErrorMessages = (shareholderIndex: number): string[] => {
  const errors = sectionValidationErrors.value.get(shareholderIndex);
  if (errors?.shareType) {
    return [errors.shareType];
  }
  return [];
};

// 섹션별 To Shareholder 에러 메시지 가져오기
const getToShareholderErrorMessages = (shareholderIndex: number): string[] => {
  const errors = sectionValidationErrors.value.get(shareholderIndex);
  if (errors?.toShareholder) {
    return [errors.toShareholder];
  }
  return [];
};

// 섹션별 To Number of Shares 에러 메시지 가져오기 (다른 필드들과 동일한 방식)
const getToNumberOfSharesErrorMessages = (shareholderIndex: number): string[] => {
  const errors = sectionValidationErrors.value.get(shareholderIndex);
  if (errors?.toNumberOfShares) {
    return [errors.toNumberOfShares];
  }
  return [];
};

// 섹션별 Issued share capital 에러 메시지 가져오기
const getToIssuedShareCapitalErrorMessages = (shareholderIndex: number): string[] => {
  const errors = sectionValidationErrors.value.get(shareholderIndex);
  if (errors?.toIssuedShareCapital) {
    return [errors.toIssuedShareCapital];
  }
  return [];
};

// 섹션별 Total Consideration Paid 에러 메시지 가져오기
const getTotalConsiderationPaidErrorMessages = (shareholderIndex: number): string[] => {
  const errors = sectionValidationErrors.value.get(shareholderIndex);
  if (errors?.totalConsiderationPaid) {
    return [errors.totalConsiderationPaid];
  }
  return [];
};

// 섹션별 Paid up share capital 에러 메시지 가져오기
const getToPaidUpShareCapitalErrorMessages = (shareholderIndex: number): string[] => {
  const errors = sectionValidationErrors.value.get(shareholderIndex);
  if (errors?.toPaidUpShareCapital) {
    return [errors.toPaidUpShareCapital];
  }
  return [];
};

const shareTypeRules = [
  (v: string) => !!v || 'Share Type is required'
];

// 문서 변수 매핑
const docVariableMapping = {
  entityName: 'CompanyName',
  entityRegNo: 'RegistrationNo',
  transactionDate: 'ResolutionDate',
  executionDate: 'ExecutionDate',
  noticeDate: 'NoticeDate',
  companyAddress: 'CompanyAddress',
  incorporatedDate: 'IncorporatedDate',
  shareType: 'ShareType',
  shareTypeName: 'ShareType',
  currency: 'Currency',
  amount: 'Amount',
  // From Shareholder
  fromShareholderName: 'TransferFromShareholder',
  fromShareholderID: 'TransferFromShareholderID',
  fromShareholderAddress: 'TransferFromShareholderAddress',
  fromShareholderCertNo: 'TransferFromShareholderCertNo',
  // To Shareholder
  toShareholderName: 'TransferToShareholder',
  toShareholderID: 'TransferToShareholderID',
  toShareholderAddress: 'TransferToShareholderAddress',
  toShareholderNationality: 'TransferToShareholderNationality',
  toShareholderBirth: 'TransferToShareholderBirth',
  toShareholderCertNo: 'TransferToShareholderCertNo',
  numberOfShares: 'NumberOfShares',
  // 기타
  egmPlace: 'EGMPlace',
  egmDate: 'EGMDate',
  egmTime: 'EGMTime',
  chairPersonName: 'ChairpersonName',
  representativeDirector: 'RepresentativeDirector',
  rorc: 'RORC',
  representative: 'REPRESENTATIVE'
};

// Chair Person 이름 가져오기
const getChairPersonName = (): string => {
  if (!chairPerson.value) return '';
  const option = chairPersonOptions.value.find(opt => opt.value === chairPerson.value);
  return option?.title || '';
};

// Helper 함수들
const getFromShareholderNameHelper = (shareholderValue: string): string => {
  const option = fromShareholderOptions.value.find(opt => opt.value === shareholderValue);
  return option?.title || '';
};

const getToShareholderNameHelper = (shareholderValue: string): string => {
  const option = toShareholderOptions.value.find(opt => opt.value === shareholderValue);
  return option?.title || '';
};

const getShareTypeNameHelper = (shareTypeValue: string): string => {
  const option = allShareTypeOptions.value.find(opt => opt.value === shareTypeValue);
  return option?.title || '';
};

const getCurrencyNameHelper = (currencyValue: string): string => {
  if (!currencyValue) return '';
  const [currencyCode] = currencyValue.split('_');
  return currencyCode || '';
};

const getToShareholderAddressHelper = (shareholderIndex: number): string => {
  const contactData = getToShareholderContactData(shareholderIndex);
  if (!contactData) return '';
  return formatShareholderAddress(contactData);
};

const getToShareholderIDHelper = (shareholderIndex: number): string => {
  const contactData = getToShareholderContactData(shareholderIndex);
  if (!contactData) return '';
  return contactData.CONTACT_REG_NO || '';
};

const getToShareholderNationalityHelper = (shareholderIndex: number): string => {
  const contactData = getToShareholderContactData(shareholderIndex);
  if (!contactData || !contactData.NATIONALITY_CD) return '';
  // 공통코드에서 bigo1 값 가져오기
  const option = nationalityOptions.value.find(opt => opt.value === contactData.NATIONALITY_CD);
  return option?.bigo1 || option?.title || '';
};

const getToShareholderBirthHelper = (shareholderIndex: number): string => {
  const contactData = getToShareholderContactData(shareholderIndex);
  if (!contactData || !contactData.DATE_OF_BIRTH) return '';
  // formatDocDate로 포맷팅
  return formatDocDate(contactData.DATE_OF_BIRTH) || '';
};

const getFromShareholderAddressHelper = (shareholderIndex: number): string => {
  const contactData = getFromShareholderContactData(shareholderIndex);
  if (!contactData) return '';
  return formatShareholderAddress(contactData);
};

const getFromShareholderIDHelper = (shareholderIndex: number): string => {
  const contactData = getFromShareholderContactData(shareholderIndex);
  if (!contactData) return '';
  return contactData.CONTACT_REG_NO || '';
};

// Entity 정보 조회 (companyAddress, incorporatedDate, entityRegNo)
const getEntityInfo = async () => {
  if (!props.entityId) {
    return {
      entityName: '',
      entityRegNo: '',
      companyAddress: '',
      incorporatedDate: ''
    };
  }

  try {
    // Entity 이름은 옵션에서 가져오기
    const selectedEntityOption = officerAppointmentStore.entityOptions.find(opt => opt.value === props.entityId);
    const entityNameVal = selectedEntityOption?.title || '';

    // Entity Profile에서 상세 정보 가져오기
    const response = await entityApi.getEntityProfile(props.entityId);
    const entityData = (response as any).data || response;
    
    // REG_NO
    const entityRegNoVal = entityData?.regNo || entityData?.REG_NO || '';
    
    // 주소 만들기
    const registeredAddress = entityData?.registeredAddress || {};
    const addressParts = [];
    if (registeredAddress.blockNo || registeredAddress.blockHouseNo) {
      addressParts.push(registeredAddress.blockNo || registeredAddress.blockHouseNo);
    }
    if (registeredAddress.streetName) {
      addressParts.push(registeredAddress.streetName);
    }
    if (registeredAddress.buildingEstateName || registeredAddress.buildingName) {
      const buildingName = registeredAddress.buildingEstateName || registeredAddress.buildingName;
      if (registeredAddress.level || registeredAddress.addrLevel) {
        const level = registeredAddress.level || registeredAddress.addrLevel;
        if (registeredAddress.unit || registeredAddress.addrUnit) {
          const unit = registeredAddress.unit || registeredAddress.addrUnit;
          addressParts.push(`${buildingName}, #${level}-${unit}`);
        } else {
          addressParts.push(`${buildingName}, #${level}`);
        }
      } else {
        addressParts.push(buildingName);
      }
    } else if (registeredAddress.level || registeredAddress.addrLevel) {
      const level = registeredAddress.level || registeredAddress.addrLevel;
      if (registeredAddress.unit || registeredAddress.addrUnit) {
        const unit = registeredAddress.unit || registeredAddress.addrUnit;
        addressParts.push(`#${level}-${unit}`);
      } else {
        addressParts.push(`#${level}`);
      }
    }
    if (registeredAddress.postalCode) {
      addressParts.push(`SINGAPORE ${registeredAddress.postalCode}`);
    }
    const companyAddressVal = addressParts.join(', ') || '';
    
    // DATE_OF_INCORPORATION
    const incorporatedDateVal = entityData?.dateOfIncorporation || entityData?.DATE_OF_INCORPORATION || '';
    
    return {
      entityName: entityNameVal,
      entityRegNo: entityRegNoVal,
      companyAddress: companyAddressVal,
      incorporatedDate: incorporatedDateVal
    };
  } catch (error) {
    console.error('Failed to get entity info:', error);
    const selectedEntityOption = officerAppointmentStore.entityOptions.find(opt => opt.value === props.entityId);
    return {
      entityName: selectedEntityOption?.title || '',
      entityRegNo: '',
      companyAddress: '',
      incorporatedDate: ''
    };
  }
};

// formData 수집 (복수 구조 전체 지원)
const getFormData = async () => {
  // Entity 정보 조회
  const entityInfo = await getEntityInfo();

  // 복수 구조 전체 데이터 수집
  const shareholders = Array.isArray(shareholderTransferSections.value)
    ? shareholderTransferSections.value.map((section, index) => {
        const fromShareholderName = getFromShareholderNameHelper(section.fromShareholder);
        const toShareholderName = getToShareholderNameHelper(section.toShareholder);
        const shareTypeName = getShareTypeNameHelper(section.shareType);
        const currencyName = getCurrencyNameHelper(section.currency);
        
        // fromShareholder를 contactCd로 변환
        const fromShareholderContactCd = getFromShareholderContactCd(index);
        
        // currency를 코드값만 추출 (예: "SGD_0" -> "SGD", "B08143" -> "B08143")
        const currencyCode = section.currency ? section.currency.split('_')[0] : '';
        
        return {
          fromShareholder: fromShareholderContactCd || '', // contactCd로 저장
          fromShareholderName: fromShareholderName,
          fromShareholderID: getFromShareholderIDHelper(index),
          fromShareholderAddress: getFromShareholderAddressHelper(index),
          fromShareholderCertNo: section.fromShareholderCertNo || '',
          fromShareholderCertType: section.fromShareholderCertType || '',
          fromNumberOfShares: section.fromNumberOfShares || '',
          fromPaidUpShareCapital: section.fromPaidUpShareCapital || '',
          toShareholder: section.toShareholder || '', // 이미 contactCd로 저장됨
          toShareholderName: toShareholderName,
          toShareholderID: getToShareholderIDHelper(index),
          toShareholderAddress: getToShareholderAddressHelper(index),
          toShareholderNationality: getToShareholderNationalityHelper(index),
          toShareholderBirth: getToShareholderBirthHelper(index),
          toShareholderCertNo: section.toShareholderCertNo || '',
          toShareholderCertType: section.toShareholderCertType || '',
          toNumberOfShares: section.toNumberOfShares || '',
          toPaidUpShareCapital: section.toPaidUpShareCapital || '',
          toIssuedShareCapital: section.toIssuedShareCapital || '',
          totalConsiderationPaid: section.totalConsiderationPaid || '',
          shareType: section.shareType || '',
          shareTypeName: shareTypeName,
          currency: currencyCode || '', // 코드값만 저장 (예: "B08143", "SGD")
          currencyName: currencyName,
          sharesHeldInTrust: section.sharesHeldInTrust,
          trustName: section.trustName || '',
          enteredMember: section.enteredMember || ''
        };
      })
    : [];

  // 첫 번째 섹션 데이터 (백엔드 호환성용 - 필수 검증 필드)
  const firstSection = Array.isArray(shareholderTransferSections.value) && shareholderTransferSections.value.length > 0
    ? shareholderTransferSections.value[0]
    : null;
  
  return {
    // Entity 정보
    entity: entity.value || props.entityId || '',  // entity.value 우선 사용 (Allotment와 동일)
    entityName: entityInfo.entityName,
    entityRegNo: entityInfo.entityRegNo,
    transactionDate: transactionDateValue.value || '',
    executionDate: transactionDateValue.value || '',
    noticeDate: transactionDateValue.value || '',
    companyAddress: entityInfo.companyAddress,
    incorporatedDate: entityInfo.incorporatedDate,
    
    // 복수 구조 전체 데이터
    shareholders: shareholders,
    
    // 백엔드 호환성용 필수 필드 (첫 번째 섹션 값 사용 - 검증용)
    toShareholder: firstSection?.toShareholder || '',
    toNumberOfShares: firstSection?.toNumberOfShares || '',
    shareType: firstSection?.shareType || '',
    
    // 기타 필드
    transferorCertificateType: transferorCertificateType.value || '',
    fromShareCertificateNo: transferorCertificateNo.value || '',
    transfereeCertificateType: transfereeCertificateType.value || '',
    toShareCertificateNo: transfereeCertificateNo.value || '',
    egmPlace: egmPlace.value || '',
    egmDate: egmDate.value || '',
    egmTime: egmTime.value || '',
    chairPerson: chairPerson.value || '',
    chairPersonName: getChairPersonName(),
    representativeDirector: representativeDirector.value || '',
    rorc: rorc.value || '',
    representative: representative.value || ''
  };
};

// doc_variables 포함 formData (복수 구조 지원)
const getFormDataWithDocVariables = async () => {
  const formData = await getFormData() as Record<string, any>;
  const fieldMap = new Map<string, { value: any; docVariables: string[] }>();

  // 값이 없어도 공백(' ')으로 doc_variables 생성 (빈 문자열은 문서 바인딩에서 식별이 안 됨)
  const addField = (key: string, docKey: string, isDate: boolean = false) => {
    let value = formData[key] || ' ';
    // 날짜 필드는 formatDocDate로 포맷팅
    if (isDate && value && value !== ' ') {
      value = formatDocDate(value);
    }
    fieldMap.set(key, { value: value || ' ', docVariables: [docKey] });
  };

  // 공통 필드
  addField('entityName', docVariableMapping.entityName);
  addField('entityRegNo', docVariableMapping.entityRegNo);
  addField('transactionDate', docVariableMapping.transactionDate, true); // 날짜 포맷팅
  addField('executionDate', docVariableMapping.executionDate, true); // 날짜 포맷팅
  addField('noticeDate', docVariableMapping.noticeDate, true); // 날짜 포맷팅
  addField('companyAddress', docVariableMapping.companyAddress);
  addField('incorporatedDate', docVariableMapping.incorporatedDate, true); // 날짜 포맷팅
  
  // EGM 필드
  addField('egmPlace', docVariableMapping.egmPlace);
  addField('egmTime', docVariableMapping.egmTime);
  addField('egmDate', docVariableMapping.egmDate, true); // 날짜 포맷팅
  
  // Chair Person Name 추가
  const chairPersonNameVal = getChairPersonName();
  fieldMap.set('chairPersonName', { 
    value: chairPersonNameVal || ' ', 
    docVariables: [docVariableMapping.chairPersonName] 
  });
  
  // Representative Director Name 추가
  if (representativeDirector.value) {
    const representativeDirectorOption = representativeDirectorOptions.value.find(opt => opt.value === representativeDirector.value);
    const representativeDirectorName = representativeDirectorOption?.title || '';
    fieldMap.set('representativeDirector', { 
      value: representativeDirectorName || ' ', 
      docVariables: [docVariableMapping.representativeDirector] 
    });
  }
  
  // RORC Name 추가
  if (rorc.value) {
    const rorcOption = rorcOptions.value.find(opt => opt.value === rorc.value);
    const rorcName = rorcOption?.title || '';
    fieldMap.set('rorc', { 
      value: rorcName || ' ', 
      docVariables: [docVariableMapping.rorc] 
    });
  }
  
  // REPRESENTATIVE Name 추가
  if (representative.value) {
    const representativeOption = representativeOptions.value.find(opt => opt.value === representative.value);
    const representativeName = representativeOption?.title || '';
    fieldMap.set('representative', { 
      value: representativeName || ' ', 
      docVariables: [docVariableMapping.representative] 
    });
  }
  
  // 공통 필드: TransferFromShareholderCertNo, TransferToShareholderCertNo
  fieldMap.set('fromShareholderCertNo', { 
    value: formData.fromShareCertificateNo || ' ', 
    docVariables: [docVariableMapping.fromShareholderCertNo] 
  });
  fieldMap.set('toShareholderCertNo', { 
    value: formData.toShareCertificateNo || ' ', 
    docVariables: [docVariableMapping.toShareholderCertNo] 
  });
  
  // Total Consideration Paid를 Amount doc_variable에 바인딩 (top-level)
  const firstSection = shareholderTransferSections.value[0];
  if (firstSection?.totalConsiderationPaid) {
    fieldMap.set('totalConsiderationPaid', { 
      value: firstSection.totalConsiderationPaid || ' ', 
      docVariables: [docVariableMapping.amount] 
    });
  }

  const result: Record<string, any> = {};
  fieldMap.forEach((item, fieldName) => {
    result[fieldName] = {
      value: item.value,
      doc_variables: item.docVariables.map(v => `#${v}`)
    };
  });
  
  // shareholders 배열의 각 항목을 doc_variables 형태로 변환
  if (formData.shareholders && Array.isArray(formData.shareholders)) {
    result.shareholders = formData.shareholders.map((shareholder: any, index: number) => {
      const shareholderWithDocVars: Record<string, any> = {};
      
      // 원본 데이터는 그대로 유지 (doc_variables 없이)
      shareholderWithDocVars.fromShareholder = shareholder.fromShareholder || '';
      shareholderWithDocVars.toShareholder = shareholder.toShareholder || '';
      shareholderWithDocVars.shareType = shareholder.shareType || '';
      shareholderWithDocVars.currency = shareholder.currency || '';
      shareholderWithDocVars.fromNumberOfShares = shareholder.fromNumberOfShares || '';
      shareholderWithDocVars.fromPaidUpShareCapital = shareholder.fromPaidUpShareCapital || '';
      shareholderWithDocVars.toPaidUpShareCapital = shareholder.toPaidUpShareCapital || '';
      shareholderWithDocVars.toIssuedShareCapital = shareholder.toIssuedShareCapital || '';
      shareholderWithDocVars.sharesHeldInTrust = shareholder.sharesHeldInTrust;
      shareholderWithDocVars.trustName = shareholder.trustName || '';
      
      // From Shareholder 필드 (doc_variables 포함) - 항상 생성
      shareholderWithDocVars.fromShareholderName = {
        value: shareholder.fromShareholderName || ' ',
        doc_variables: ['#TransferFromShareholder']
      };
      shareholderWithDocVars.fromShareholderID = {
        value: shareholder.fromShareholderID || ' ',
        doc_variables: ['#TransferFromShareholderID']
      };
      shareholderWithDocVars.fromShareholderAddress = {
        value: shareholder.fromShareholderAddress || ' ',
        doc_variables: ['#TransferFromShareholderAddress']
      };
      // fromShareholderCertNo는 shareholders 배열 밖으로 빼서 공통 필드로 처리
      
      // To Shareholder 필드 (doc_variables 포함) - 항상 생성
      shareholderWithDocVars.toShareholderName = {
        value: shareholder.toShareholderName || ' ',
        doc_variables: ['#TransferToShareholder']
      };
      shareholderWithDocVars.toShareholderID = {
        value: shareholder.toShareholderID || ' ',
        doc_variables: ['#TransferToShareholderID']
      };
      shareholderWithDocVars.toShareholderAddress = {
        value: shareholder.toShareholderAddress || ' ',
        doc_variables: ['#TransferToShareholderAddress']
      };
      
      // To Shareholder가 개인인 경우에만 Nationality와 Birth 포함 (Corporate인 경우 제외)
      // CATEGORY_CD가 B09001이 아닐 때만 포함 (B09001 = Corporate, B09002 = Individual)
      const section = shareholderTransferSections.value[index];
      if (section) {
        const toContactData = getToShareholderContactData(index);
        const categoryCd = toContactData?.CATEGORY_CD || toContactData?.categoryCd || '';
        // B09001이 아닐 때만 (즉, 개인일 때만) Nationality와 Birth 포함
        if (categoryCd !== 'B09001') {
          shareholderWithDocVars.toShareholderNationality = {
            value: shareholder.toShareholderNationality || ' ',
            doc_variables: ['#TransferToShareholderNationality']
          };
          // 날짜 포맷팅 적용
          const birthValue = shareholder.toShareholderBirth || ' ';
          shareholderWithDocVars.toShareholderBirth = {
            value: birthValue && birthValue !== ' ' ? formatDocDate(birthValue) : ' ',
            doc_variables: ['#TransferToShareholderBirth']
          };
        }
      } else {
        // 섹션 정보가 없으면 기본적으로 포함 (하위 호환성)
        shareholderWithDocVars.toShareholderNationality = {
          value: shareholder.toShareholderNationality || ' ',
          doc_variables: ['#TransferToShareholderNationality']
        };
        // 날짜 포맷팅 적용
        const birthValue = shareholder.toShareholderBirth || ' ';
        shareholderWithDocVars.toShareholderBirth = {
          value: birthValue && birthValue !== ' ' ? formatDocDate(birthValue) : ' ',
          doc_variables: ['#TransferToShareholderBirth']
        };
      }
      // toShareholderCertNo는 shareholders 배열 밖으로 빼서 공통 필드로 처리
      
      // 공통 필드 (doc_variables 포함) - 항상 생성
      shareholderWithDocVars.toNumberOfShares = {
        value: shareholder.toNumberOfShares || ' ',
        doc_variables: ['#NumberOfShares']
      };
      shareholderWithDocVars.shareTypeName = {
        value: shareholder.shareTypeName || ' ',
        doc_variables: ['#ShareType']
      };
      shareholderWithDocVars.currencyName = {
        value: shareholder.currencyName || ' ',
        doc_variables: ['#Currency']
      };
      shareholderWithDocVars.totalConsiderationPaid = {
        value: shareholder.totalConsiderationPaid || ' ',
        doc_variables: ['#Amount']
      };
      
      return shareholderWithDocVars;
    });
  }
  
  // entity 필드 명시적으로 추가 (문자열로 저장)
  result.entity = formData.entity || '';
  
  return result;
};

// Trust 관련 필드 초기화 watch (첫 번째 섹션)
watch(
  () => shareholderTransferSections.value[0]?.sharesHeldInTrust,
  (newValue) => {
    if (newValue === null || newValue === false) {
      if (shareholderTransferSections.value[0]) {
        shareholderTransferSections.value[0].trustName = '';
      }
    }
  }
);

// 섹션별 필드 변경 시 validation 에러 클리어
watch(
  () => shareholderTransferSections.value.map(s => ({
    currency: s.currency,
    shareType: s.shareType,
    toShareholder: s.toShareholder,
    toNumberOfShares: s.toNumberOfShares,
    toIssuedShareCapital: s.toIssuedShareCapital,
    totalConsiderationPaid: s.totalConsiderationPaid,
    toPaidUpShareCapital: s.toPaidUpShareCapital
  })),
  () => {
    // 각 섹션의 필드 값이 변경되면 해당 섹션의 validation 에러 클리어
    shareholderTransferSections.value.forEach((section, index) => {
      const errors = sectionValidationErrors.value.get(index);
      if (errors) {
        // 필드 값이 있으면 해당 필드의 에러만 클리어
        const updatedErrors = { ...errors };
        if (section.currency && updatedErrors.currency) {
          delete updatedErrors.currency;
        }
        if (section.shareType && updatedErrors.shareType) {
          delete updatedErrors.shareType;
        }
        if (section.toShareholder && updatedErrors.toShareholder) {
          delete updatedErrors.toShareholder;
        }
        if (section.toNumberOfShares && updatedErrors.toNumberOfShares) {
          delete updatedErrors.toNumberOfShares;
        }
        if (section.toIssuedShareCapital && updatedErrors.toIssuedShareCapital) {
          delete updatedErrors.toIssuedShareCapital;
        }
        if (section.totalConsiderationPaid && updatedErrors.totalConsiderationPaid) {
          delete updatedErrors.totalConsiderationPaid;
        }
        if (section.toPaidUpShareCapital && updatedErrors.toPaidUpShareCapital) {
          delete updatedErrors.toPaidUpShareCapital;
        }
        
        // 에러가 남아있으면 업데이트, 없으면 삭제
        if (Object.keys(updatedErrors).length > 0) {
          sectionValidationErrors.value.set(index, updatedErrors);
        } else {
          sectionValidationErrors.value.delete(index);
        }
      }
    });
  },
  { deep: true }
);

// Validation 함수 (CreateTaskForm에서 호출)
const validateAll = async (): Promise<boolean> => {
  await nextTick();
  
  // 모든 섹션의 validation 에러 초기화
  sectionValidationErrors.value.clear();
  
  let hasError = false;
  let firstErrorField: string | null = null;
  let firstErrorSectionIndex: number | null = null;
  
  // 모든 섹션에 대해 검증
  for (let i = 0; i < shareholderTransferSections.value.length; i++) {
    const section = shareholderTransferSections.value[i];
    const sectionErrors: Record<string, string> = {};
    
    // Currency 검증
    if (!section.currency || section.currency.trim() === '') {
      hasError = true;
      sectionErrors.currency = t('startTask.This field is required') || 'This field is required';
      if (!firstErrorField) {
        firstErrorField = 'currency';
        firstErrorSectionIndex = i;
      }
    }
    
    // Share Type 검증
    if (!section.shareType || section.shareType.trim() === '') {
      hasError = true;
      sectionErrors.shareType = t('startTask.This field is required') || 'This field is required';
      if (!firstErrorField) {
        firstErrorField = 'shareType';
        firstErrorSectionIndex = i;
      }
    }
    
    // To Shareholder 검증
    if (!section.toShareholder || section.toShareholder.trim() === '') {
      hasError = true;
      sectionErrors.toShareholder = (t('startTask.Transferee') || 'Transferee') + ' ' + (t('startTask.is required') || 'is required');
      if (!firstErrorField) {
        firstErrorField = 'toShareholder';
        firstErrorSectionIndex = i;
      }
    }
    
    // To Number of Shares 검증
    if (!section.toNumberOfShares || section.toNumberOfShares.trim() === '') {
      hasError = true;
      sectionErrors.toNumberOfShares = (t('startTask.Number of Shares') || 'Number of Shares') + ' ' + (t('startTask.is required') || 'is required');
      if (!firstErrorField) {
        firstErrorField = 'toNumberOfShares';
        firstErrorSectionIndex = i;
      }
    }
    
    // Issued share capital 검증
    if (!section.toIssuedShareCapital || section.toIssuedShareCapital.trim() === '') {
      hasError = true;
      sectionErrors.toIssuedShareCapital = (t('startTask.Issued share capital') || 'Issued share capital') + ' ' + (t('startTask.is required') || 'is required');
      if (!firstErrorField) {
        firstErrorField = 'toIssuedShareCapital';
        firstErrorSectionIndex = i;
      }
    }
    
    // Total Consideration Paid 검증
    if (!section.totalConsiderationPaid || section.totalConsiderationPaid.trim() === '') {
      hasError = true;
      sectionErrors.totalConsiderationPaid = (t('startTask.Total Consideration Paid') || 'Total Consideration Paid') + ' ' + (t('startTask.is required') || 'is required');
      if (!firstErrorField) {
        firstErrorField = 'totalConsiderationPaid';
        firstErrorSectionIndex = i;
      }
    }
    
    // Paid up share capital 검증
    if (!section.toPaidUpShareCapital || section.toPaidUpShareCapital.trim() === '') {
      hasError = true;
      sectionErrors.toPaidUpShareCapital = (t('startTask.Paid up share capital') || 'Paid up share capital') + ' ' + (t('startTask.is required') || 'is required');
      if (!firstErrorField) {
        firstErrorField = 'toPaidUpShareCapital';
        firstErrorSectionIndex = i;
      }
    }
    
    // 에러가 있으면 섹션별 에러 저장
    if (Object.keys(sectionErrors).length > 0) {
      sectionValidationErrors.value.set(i, sectionErrors);
    }
    
    // Number of Shares validation: 동일한 From Shareholder (같은 currency, shareType)의 모든 섹션 합산 검증
    if (section.toNumberOfShares && section.toNumberOfShares.trim() !== '' &&
        section.fromShareholder && section.fromShareholder.trim() !== '' &&
        section.currency && section.currency.trim() !== '' &&
        section.shareType && section.shareType.trim() !== '') {
      
      // 동일한 From Shareholder (같은 fromShareholder, currency, shareType)를 가진 모든 섹션 찾기
      const sameFromShareholderSections = Array.isArray(shareholderTransferSections.value)
        ? shareholderTransferSections.value.filter(s => 
            s.fromShareholder === section.fromShareholder &&
            s.currency === section.currency &&
            s.shareType === section.shareType &&
            s.fromShareholder !== '' &&
            s.currency !== '' &&
            s.shareType !== ''
          )
        : [];
      
      const fromShares = Number(section.fromNumberOfShares || 0);
      
      if (fromShares > 0) {
        // 동일한 From Shareholder를 가진 모든 섹션의 transfer 주식 수 합산
        const totalTransferredShares = sameFromShareholderSections.reduce((sum, s) => {
          const transferred = Number(s.toNumberOfShares || 0);
          return sum + (isNaN(transferred) ? 0 : transferred);
        }, 0);
        
        // 합산된 총 transfer 주식 수가 From Shareholder가 가진 총 주식 수를 초과하면 에러
        if (totalTransferredShares > fromShares) {
          hasError = true;
          // 에러 메시지는 이미 getToNumberOfSharesError computed로 표시됨
          if (!firstErrorField) {
            firstErrorField = 'toNumberOfShares';
            firstErrorSectionIndex = i;
          }
        }
      } else if (Number(section.toNumberOfShares || 0) > 0) {
        // From Shares가 0인데 To Shares가 0보다 크면 에러
        hasError = true;
        if (!firstErrorField) {
          firstErrorField = 'toNumberOfShares';
          firstErrorSectionIndex = i;
        }
      }
    }
  }
  
  // 공통 필드 검증
  if (!transactionDateValue.value) {
    hasError = true;
    if (!firstErrorField) {
      firstErrorField = 'transactionDate';
    }
  }
  
  // 필드 ref를 사용하여 validation 강제 실행
  for (let i = 0; i < shareholderTransferSections.value.length; i++) {
    // Currency 필드 validation 강제 실행
    const currencyRef = currencyFieldRefs.value.get(i);
    if (currencyRef && typeof currencyRef.validate === 'function') {
      currencyRef.validate();
    }
    
    // Share Type 필드 validation 강제 실행
    const shareTypeRef = shareTypeFieldRefs.value.get(i);
    if (shareTypeRef && typeof shareTypeRef.validate === 'function') {
      shareTypeRef.validate();
    }
    
    // To Shareholder 필드 validation 강제 실행
    const toShareholderRef = toShareholderFieldRefs.value.get(i);
    if (toShareholderRef && typeof toShareholderRef.validate === 'function') {
      toShareholderRef.validate();
    }
    
    // To Number of Shares 필드 validation 강제 실행
    const toNumberOfSharesRef = toNumberOfSharesFieldRefs.value.get(i);
    if (toNumberOfSharesRef && typeof toNumberOfSharesRef.validate === 'function') {
      toNumberOfSharesRef.validate();
    }
    
    // To Issued Share Capital 필드 validation 강제 실행
    const toIssuedShareCapitalRef = toIssuedShareCapitalFieldRefs.value.get(i);
    if (toIssuedShareCapitalRef && typeof toIssuedShareCapitalRef.validate === 'function') {
      toIssuedShareCapitalRef.validate();
    }
    
    // Total Consideration Paid 필드 validation 강제 실행
    const totalConsiderationPaidRef = totalConsiderationPaidFieldRefs.value.get(i);
    if (totalConsiderationPaidRef && typeof totalConsiderationPaidRef.validate === 'function') {
      totalConsiderationPaidRef.validate();
    }
    
    // To Paid Up Share Capital 필드 validation 강제 실행
    const toPaidUpShareCapitalRef = toPaidUpShareCapitalFieldRefs.value.get(i);
    if (toPaidUpShareCapitalRef && typeof toPaidUpShareCapitalRef.validate === 'function') {
      toPaidUpShareCapitalRef.validate();
    }
  }
  
  // 첫 번째 에러 필드로 포커스 이동
  if (hasError && firstErrorField) {
    await nextTick();
    setTimeout(async () => {
      let fieldElement: HTMLElement | null = null;
      
      // 섹션별 필드인 경우 (toShareholder, toNumberOfShares, currency, shareType, toIssuedShareCapital, totalConsiderationPaid, toPaidUpShareCapital)
      if (firstErrorSectionIndex !== null && (firstErrorField === 'toShareholder' || firstErrorField === 'toNumberOfShares' || firstErrorField === 'currency' || firstErrorField === 'shareType' || firstErrorField === 'toIssuedShareCapital' || firstErrorField === 'totalConsiderationPaid' || firstErrorField === 'toPaidUpShareCapital')) {
        // 해당 섹션의 패널이 닫혀있으면 열기
        const section = shareholderTransferSections.value[firstErrorSectionIndex];
        if (section && !allExpandedPanels.value.includes(section.id)) {
          allExpandedPanels.value.push(section.id);
          await nextTick();
        }
        
        // 섹션별 필드 찾기 (여러 개가 있을 수 있으므로 인덱스로 구분)
        const fields = Array.from(document.querySelectorAll(`[data-field="${firstErrorField}"]`));
        if (fields[firstErrorSectionIndex]) {
          fieldElement = fields[firstErrorSectionIndex].closest('.v-text-field, .v-autocomplete') as HTMLElement;
        }
      } else {
        // 공통 필드 (transactionDate)
        const field = document.querySelector(`[data-field="${firstErrorField}"]`);
        if (field) {
          fieldElement = field.closest('.v-text-field, .v-autocomplete') as HTMLElement;
        }
      }
      
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = fieldElement.querySelector('input') as HTMLInputElement;
        if (input && typeof input.focus === 'function') {
          setTimeout(() => {
            input.focus();
          }, 100);
        }
      }
    }, 100);
  }
  
  return !hasError;
};

// ========================================
// E-Filing JSON 생성 관련 코드
// ========================================

// 날짜를 "20 Jan 2026" 형식으로 변환
const formatDateForEFiling = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    // YYYY-MM-DD 형식 또는 YYYY-MM-DD HH:mm:ss 형식인 경우
    let datePart = dateString.trim();
    
    // "1982-03-12 00:00:00.0" 형식에서 날짜 부분만 추출
    if (datePart.includes(' ')) {
      datePart = datePart.split(' ')[0];
    }
    
    // YYYY-MM-DD 형식인지 확인
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const date = new Date(datePart + 'T00:00:00');
      if (isNaN(date.getTime())) return dateString;
      
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day} ${month} ${year}`;
    }
    
    // 이미 다른 형식인 경우 그대로 반환
    return dateString;
  } catch (e) {
    console.warn('날짜 포맷팅 실패:', e);
    return dateString;
  }
};

// 날짜를 "YYYY-MM-DD" 형식으로 변환
const formatDateToYYYYMMDD = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    // YYYY-MM-DD 형식 또는 YYYY-MM-DD HH:mm:ss 형식인 경우
    let datePart = dateString.trim();
    
    // "1982-03-12 00:00:00.0" 형식에서 날짜 부분만 추출
    if (datePart.includes(' ')) {
      datePart = datePart.split(' ')[0];
    }
    
    // 이미 YYYY-MM-DD 형식인 경우 그대로 반환
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
    
    // Date 객체로 파싱 시도
    const date = new Date(datePart);
    if (!isNaN(date.getTime())) {
      const year = String(date.getFullYear());
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // 파싱 실패 시 원본 반환
    return dateString;
  } catch (e) {
    console.warn('날짜 포맷팅 실패:', e);
    return dateString;
  }
};

// Country 코드에서 BIGO3 값 가져오기 (+ 제거)
const getCountryBigo3WithoutPlus = (countryCd: string): string => {
  const countryItem = countryOptions.value.find(opt => opt.value === countryCd);
  const bigo3 = countryItem?.bigo3 || '';
  return bigo3.replace(/^\+/, '');
};

// Class of Shares 코드에서 축약형 가져오기 (A20001 -> ORD, A20002 -> PREF, A20003 -> OTH)
const getClassOfSharesAs = (classOfSharesCd: string): string => {
  if (!classOfSharesCd) return '';
  const classOfSharesItem = allShareTypeOptions.value.find(opt => opt.value === classOfSharesCd);
  // 코드에서 직접 매핑
  if (classOfSharesCd === 'A20001') return 'ORD';
  if (classOfSharesCd === 'A20002') return 'PREF';
  if (classOfSharesCd === 'A20003') return 'OTH';
  // 옵션에서 찾기
  const title = classOfSharesItem?.title || '';
  if (title.toLowerCase().includes('ordinary')) return 'ORD';
  if (title.toLowerCase().includes('preference') || title.toLowerCase().includes('pref')) return 'PREF';
  if (title.toLowerCase().includes('other')) return 'OTH';
  return '';
};

// Currency 코드에서 축약형 가져오기 (BIGO3 사용)
const getCurrencyAs = (currencyCd: string): string => {
  if (!currencyCd) return '';
  // currencyCd에서 실제 currency 코드 추출 (currency_shareCapitalIdx 형식일 수 있음)
  const actualCurrencyCd = currencyCd.includes('_') ? currencyCd.split('_')[0] : currencyCd;
  const currencyItem = currencyOptions.value.find(opt => opt.value === actualCurrencyCd);
  return currencyItem?.bigo3 || '';
};

// { code, name } 객체를 name 값으로 변환하는 함수
const transformCodeNameToValue = (data: any): any => {
  if (data === null || data === undefined) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => transformCodeNameToValue(item));
  }

  if (typeof data === 'object') {
    const keys = Object.keys(data);
    if (keys.length === 2 && keys.includes('code') && keys.includes('name')) {
      return data.name || data.code || '';
    }

    const result: any = {};
    for (const key of keys) {
      result[key] = transformCodeNameToValue(data[key]);
    }
    return result;
  }

  return data;
};

// 외부에서 폼 데이터 설정 (E-Filing 페이지에서 TASK_DATA로 데이터 채우기용)
const setFormData = async (formData: any) => {
  if (!formData) {
    console.warn('⚠️ [SharesTransferForm] setFormData: formData가 null 또는 undefined입니다');
    return;
  }

  console.log('📋 [SharesTransferForm] setFormData:', formData);

  // 데이터 복원 모드 시작 (Allotment와 동일)
  isRestoringData.value = true;

  try {
    // allShareTypeOptions가 로드되지 않았으면 먼저 로드 (E-Filing 팝업에서 shareType 바인딩을 위해 필요)
    if (allShareTypeOptions.value.length === 0) {
      await loadShareType();
    }
    
    // Entity 설정
    if (formData.entity) {
      entity.value = formData.entity; // Allotment와 동일하게 직접 할당
      // Entity 설정 후 Vue 반응성 시스템이 처리될 때까지 대기
      await nextTick();
      
      // loadEntityShares가 완료될 때까지 명시적으로 대기 (toShareholderOptions는 entity 없이도 로드되지만, fromShareholderOptions는 entity가 필요)
      await loadEntityShares(formData.entity);
      await nextTick();
      
      // Entity 관련 다른 데이터도 로드
      try {
        await Promise.all([
          loadShareholderContacts(formData.entity),
          loadChairPersonOptions(formData.entity),
          loadCertificateTypes(formData.entity),
          loadRepresentativeDirectorOptions(formData.entity),
          loadRorcOptions(formData.entity)
        ]);
      } catch (promiseError: any) {
        console.error('❌ [SharesTransferForm] Promise.all 실패:', promiseError);
        // Promise.all 실패해도 계속 진행
      }
    }

    // Transaction Date 설정
    if (formData.transactionDate?.value) {
      transactionDateValue.value = formData.transactionDate.value;
    } else if (formData.transactionDate) {
      transactionDateValue.value = formData.transactionDate;
    }

    // Shareholder Transfer Sections 설정
    if (formData.shareholderTransferSections && Array.isArray(formData.shareholderTransferSections)) {
      // 기존 섹션 초기화
      shareholderTransferSections.value = [];
      
      for (const sectionData of formData.shareholderTransferSections) {
        const newSection: ShareholderTransferSection = {
          id: String(shareholderTransferSections.value.length + 1),
          fromShareholder: sectionData.fromShareholder || '',
          fromNumberOfShares: sectionData.fromNumberOfShares || '',
          fromPaidUpShareCapital: sectionData.fromPaidUpShareCapital || '',
          fromShareholderID: sectionData.fromShareholderID || '',
          fromShareholderAddress: sectionData.fromShareholderAddress || '',
          toShareholder: '', // 먼저 빈 값으로 설정
          toNumberOfShares: sectionData.toNumberOfShares || '',
          toIssuedShareCapital: sectionData.toIssuedShareCapital || '',
          toPaidUpShareCapital: sectionData.toPaidUpShareCapital || '',
          totalConsiderationPaid: sectionData.totalConsiderationPaid || '',
          toShareholderID: sectionData.toShareholderID || '',
          toShareholderAddress: sectionData.toShareholderAddress || '',
          toShareholderNationality: sectionData.toShareholderNationality || '',
          toShareholderBirth: sectionData.toShareholderBirth || '',
          fromShareholderCertType:
            sectionData.fromShareholderCertType || sectionData.certiTypeFrom || '',
          fromShareholderCertNo:
            sectionData.fromShareholderCertNo ||
            sectionData.fromShareCertificateNo ||
            sectionData.certiNoFrom ||
            '',
          toShareholderCertType:
            sectionData.toShareholderCertType || sectionData.certiTypeTo || '',
          toShareholderCertNo:
            sectionData.toShareholderCertNo ||
            sectionData.toShareCertificateNo ||
            sectionData.certiNoTo ||
            '',
          shareType: '', // 먼저 빈 값으로 설정
          currency: sectionData.currency || '',
          showTrustSection: sectionData.showTrustSection || false,
          sharesHeldInTrust: sectionData.sharesHeldInTrust || null,
          trustName: sectionData.trustName || '',
          hasSubClass: sectionData.hasSubClass || false,
          subClassErrorMessage: sectionData.subClassErrorMessage || '',
          toNumberOfSharesError: sectionData.toNumberOfSharesError || '',
          enteredMember: sectionData.enteredMember || ''
        };
        
        shareholderTransferSections.value.push(newSection);
        
        // fromShareholder와 currency가 설정된 후 nextTick 대기
        await nextTick();
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Contact 데이터 로드 (fromShareholder 먼저)
        if (sectionData.fromShareholder) {
          await loadFromShareholderContactDataForSection(shareholderTransferSections.value.length - 1, sectionData.fromShareholder);
        }
        
        // toShareholderOptions가 준비될 때까지 대기 (최대 1초)
        let retryCount = 0;
        while (retryCount < 10 && (!Array.isArray(toShareholderOptions.value) || toShareholderOptions.value.length === 0)) {
          await nextTick();
          await new Promise(resolve => setTimeout(resolve, 100));
          retryCount++;
        }
        
        // 이제 toShareholder 설정 (옵션이 준비된 후)
        if (sectionData.toShareholder) {
          const toShareholderStr = String(sectionData.toShareholder).trim();
          const toShareholderOptionsValue = toShareholderOptions.value;
          const existsInOptions = Array.isArray(toShareholderOptionsValue) && toShareholderOptionsValue.some(opt => String(opt.value).trim() === toShareholderStr);
          
          if (existsInOptions) {
            newSection.toShareholder = sectionData.toShareholder;
            await nextTick();
            await loadToShareholderContactDataForSection(shareholderTransferSections.value.length - 1, sectionData.toShareholder);
            await nextTick();
            await new Promise(resolve => setTimeout(resolve, 100));
            
            console.log(`🔍 [setFormData] 섹션 ${shareholderTransferSections.value.length - 1}의 toShareholder 설정 완료:`, {
              toShareholder: sectionData.toShareholder,
              existsInOptions,
              toShareholderOptions: toShareholderOptionsValue.map(opt => ({ value: opt.value, title: opt.title }))
            });
          } else {
            console.warn(`🔍 [setFormData] 섹션 ${shareholderTransferSections.value.length - 1}의 toShareholder가 옵션에 없음:`, {
              toShareholder: sectionData.toShareholder,
              toShareholderStr,
              toShareholderOptions: Array.isArray(toShareholderOptionsValue) ? toShareholderOptionsValue.map(opt => ({ value: opt.value, title: opt.title })) : []
            });
          }
        }
      }
    } else if (formData.shareholders && Array.isArray(formData.shareholders)) {
      // shareholders 배열 형식 지원 (하위 호환성)
      shareholderTransferSections.value = [];
      
      for (const shareholderData of formData.shareholders) {
        const newSection: ShareholderTransferSection = {
          id: String(shareholderTransferSections.value.length + 1),
          fromShareholder: shareholderData.fromShareholder || '',
          fromNumberOfShares: shareholderData.fromNumberOfShares || '',
          fromPaidUpShareCapital: shareholderData.fromPaidUpShareCapital || '',
          fromShareholderID: shareholderData.fromShareholderID || '',
          fromShareholderAddress: shareholderData.fromShareholderAddress || '',
          toShareholder: shareholderData.toShareholder || '',
          toNumberOfShares: shareholderData.toNumberOfShares || '',
          toIssuedShareCapital: shareholderData.toIssuedShareCapital || '',
          toPaidUpShareCapital: shareholderData.toPaidUpShareCapital || '',
          totalConsiderationPaid: shareholderData.totalConsiderationPaid || '',
          toShareholderID: shareholderData.toShareholderID || '',
          toShareholderAddress: shareholderData.toShareholderAddress || '',
          toShareholderNationality: shareholderData.toShareholderNationality || '',
          toShareholderBirth: shareholderData.toShareholderBirth || '',
          fromShareholderCertType:
            shareholderData.fromShareholderCertType || shareholderData.certiTypeFrom || '',
          fromShareholderCertNo:
            shareholderData.fromShareholderCertNo ||
            shareholderData.fromShareCertificateNo ||
            shareholderData.certiNoFrom ||
            '',
          toShareholderCertType:
            shareholderData.toShareholderCertType || shareholderData.certiTypeTo || '',
          toShareholderCertNo:
            shareholderData.toShareholderCertNo ||
            shareholderData.toShareCertificateNo ||
            shareholderData.certiNoTo ||
            '',
          shareType: shareholderData.shareType || '',
          currency: shareholderData.currency || '',
          showTrustSection: false,
          sharesHeldInTrust: null,
          trustName: '',
          hasSubClass: false,
          subClassErrorMessage: '',
          toNumberOfSharesError: '',
          enteredMember: ''
        };
        
        shareholderTransferSections.value.push(newSection);
        
        // shareType이 설정된 후 nextTick으로 Vue 반응성 처리 대기
        if (shareholderData.shareType) {
          await nextTick();
        }
        
        // Contact 데이터 로드
        if (shareholderData.fromShareholder) {
          await loadFromShareholderContactDataForSection(shareholderTransferSections.value.length - 1, shareholderData.fromShareholder);
        }
        if (shareholderData.toShareholder) {
          await loadToShareholderContactDataForSection(shareholderTransferSections.value.length - 1, shareholderData.toShareholder);
        }
      }
    }

    // Certificate 정보 설정 (top-level → 섹션 0 fallback; 섹션별 값은 위에서 이미 반영)
    const firstSec = shareholderTransferSections.value[0];
    if (firstSec) {
      if (formData.transferorCertificateType && !String(firstSec.fromShareholderCertType || '').trim()) {
        firstSec.fromShareholderCertType = formData.transferorCertificateType;
      }
      const topFromNo = formData.transferorCertificateNo || formData.fromShareCertificateNo || '';
      if (topFromNo && !String(firstSec.fromShareholderCertNo || '').trim()) {
        firstSec.fromShareholderCertNo = topFromNo;
      }
      if (formData.transfereeCertificateType && !String(firstSec.toShareholderCertType || '').trim()) {
        firstSec.toShareholderCertType = formData.transfereeCertificateType;
      }
      const topToNo = formData.transfereeCertificateNo || formData.toShareCertificateNo || '';
      if (topToNo && !String(firstSec.toShareholderCertNo || '').trim()) {
        firstSec.toShareholderCertNo = topToNo;
      }
    }

    // Task Flow 설정
    if (formData.taskFlow) {
      taskFlow.value = { ...taskFlow.value, ...formData.taskFlow };
    }

    console.log('✅ [SharesTransferForm] Form data set successfully');
  } catch (error: any) {
    console.error('❌ [SharesTransferForm] setFormData 실패:', error);
    console.error('❌ [SharesTransferForm] setFormData 에러 상세:', {
      message: error?.message,
      stack: error?.stack,
      name: error?.name,
      formData: formData
    });
  } finally {
    // 모든 watch가 완전히 처리될 때까지 약간 대기 (Allotment와 동일한 패턴)
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // shareTypeOptions가 준비될 때까지 대기 (최대 1초)
    let retryCount = 0;
    while (retryCount < 10 && (!Array.isArray(shareTypeOptions.value) || shareTypeOptions.value.length === 0)) {
      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));
      retryCount++;
    }
    
    // shareTypeOptions가 재계산되도록 강제 접근
    const _ = shareTypeOptions.value;
    await nextTick();
    
    // 각 섹션의 shareType이 옵션에 포함되어 있는지 확인하고 강제로 재설정
    if (Array.isArray(shareholderTransferSections.value)) {
      for (let i = 0; i < shareholderTransferSections.value.length; i++) {
        const section = shareholderTransferSections.value[i];
        // shareType이 빈 값이고 formData에 값이 있으면 설정
        const sectionData = formData.shareholderTransferSections?.[i] || formData.shareholders?.[i];
        if (sectionData?.shareType && (!section.shareType || section.shareType === '')) {
          const shareTypeStr = String(sectionData.shareType).trim();
          const shareTypeOptionsValue = shareTypeOptions.value;
          const existsInOptions = Array.isArray(shareTypeOptionsValue) && shareTypeOptionsValue.some(opt => String(opt.value).trim() === shareTypeStr);
          
          if (existsInOptions) {
            section.shareType = sectionData.shareType;
            await nextTick();
            
            console.log(`🔍 [setFormData finally] 섹션 ${i}의 shareType 설정 완료:`, {
              shareType: sectionData.shareType,
              existsInOptions,
              shareTypeOptions: shareTypeOptionsValue.map(opt => ({ value: opt.value, title: opt.title }))
            });
          } else {
            console.warn(`🔍 [setFormData finally] 섹션 ${i}의 shareType이 옵션에 없음:`, {
              shareType: sectionData.shareType,
              shareTypeStr,
              shareTypeOptions: Array.isArray(shareTypeOptionsValue) ? shareTypeOptionsValue.map(opt => ({ value: opt.value, title: opt.title })) : []
            });
          }
        } else if (section.shareType) {
          // 이미 shareType이 설정되어 있으면 재설정하여 v-autocomplete가 반응하도록
          const shareTypeStr = String(section.shareType).trim();
          const shareTypeOptionsValue = shareTypeOptions.value;
          const existsInOptions = Array.isArray(shareTypeOptionsValue) && shareTypeOptionsValue.some(opt => String(opt.value).trim() === shareTypeStr);
          
          if (existsInOptions) {
            // toShareholder를 임시로 저장 (watch에 의해 초기화되는 것을 방지)
            const savedToShareholder = section.toShareholder;
            const savedToShareholderContactData = shareholderTransferContactData.value.get(section.id)?.to;
            const savedToShareholderFields = {
              toNumberOfShares: section.toNumberOfShares,
              toPaidUpShareCapital: section.toPaidUpShareCapital,
              toIssuedShareCapital: section.toIssuedShareCapital,
              totalConsiderationPaid: section.totalConsiderationPaid,
              toShareholderID: section.toShareholderID,
              toShareholderAddress: section.toShareholderAddress,
              toShareholderNationality: section.toShareholderNationality,
              toShareholderBirth: section.toShareholderBirth,
              showTrustSection: section.showTrustSection,
              sharesHeldInTrust: section.sharesHeldInTrust,
              trustName: section.trustName
            };
            
            const temp = section.shareType;
            section.shareType = '';
            await nextTick();
            await new Promise(resolve => setTimeout(resolve, 100));
            section.shareType = temp;
            await nextTick();
            
            // watch가 완전히 실행될 때까지 대기
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // toShareholder 복원 (조건 없이 항상 복원)
            if (savedToShareholder) {
              section.toShareholder = savedToShareholder;
              section.toNumberOfShares = savedToShareholderFields.toNumberOfShares;
              section.toPaidUpShareCapital = savedToShareholderFields.toPaidUpShareCapital;
              section.toIssuedShareCapital = savedToShareholderFields.toIssuedShareCapital;
              section.totalConsiderationPaid = savedToShareholderFields.totalConsiderationPaid;
              section.toShareholderID = savedToShareholderFields.toShareholderID;
              section.toShareholderAddress = savedToShareholderFields.toShareholderAddress;
              section.toShareholderNationality = savedToShareholderFields.toShareholderNationality;
              section.toShareholderBirth = savedToShareholderFields.toShareholderBirth;
              section.showTrustSection = savedToShareholderFields.showTrustSection;
              section.sharesHeldInTrust = savedToShareholderFields.sharesHeldInTrust;
              section.trustName = savedToShareholderFields.trustName;
              
              if (savedToShareholderContactData) {
                const contactData = shareholderTransferContactData.value.get(section.id) || {};
                contactData.to = savedToShareholderContactData;
                shareholderTransferContactData.value.set(section.id, contactData);
              }
              await nextTick();
              
              // toShareholder 복원 후 추가 대기 (watch가 실행되지 않도록)
              await new Promise(resolve => setTimeout(resolve, 300));
            }
            
            console.log(`🔍 [setFormData finally] 섹션 ${i}의 shareType 강제 재설정 완료:`, {
              shareType: temp,
              sectionShareType: section.shareType,
              toShareholder: section.toShareholder,
              savedToShareholder,
              shareTypeOptions: shareTypeOptions.value.map(opt => ({ value: opt.value, title: opt.title }))
            });
          }
        }
        // watch가 From Shareholder API cert로 덮어쓴 경우 PreSet/저장된 cert 복원
        if (sectionData) {
          const fromNo =
            sectionData.fromShareholderCertNo ||
            sectionData.fromShareCertificateNo ||
            sectionData.certiNoFrom ||
            '';
          const fromType = sectionData.fromShareholderCertType || sectionData.certiTypeFrom || '';
          const toNo =
            sectionData.toShareholderCertNo ||
            sectionData.toShareCertificateNo ||
            sectionData.certiNoTo ||
            '';
          const toType = sectionData.toShareholderCertType || sectionData.certiTypeTo || '';
          if (fromNo) section.fromShareholderCertNo = String(fromNo);
          if (fromType) section.fromShareholderCertType = String(fromType);
          if (toNo) section.toShareholderCertNo = String(toNo);
          if (toType) section.toShareholderCertType = String(toType);
        }
        // toShareholder는 메인 루프에서 이미 설정되므로 finally 블록에서 재설정하지 않음
      }
      // top-level fallback → 섹션 0
      const s0 = shareholderTransferSections.value[0];
      if (s0) {
        const topFromNo = formData.transferorCertificateNo || formData.fromShareCertificateNo || '';
        const topToNo = formData.transfereeCertificateNo || formData.toShareCertificateNo || '';
        if (formData.transferorCertificateType && !String(s0.fromShareholderCertType || '').trim()) {
          s0.fromShareholderCertType = formData.transferorCertificateType;
        }
        if (topFromNo && !String(s0.fromShareholderCertNo || '').trim()) {
          s0.fromShareholderCertNo = topFromNo;
        }
        if (formData.transfereeCertificateType && !String(s0.toShareholderCertType || '').trim()) {
          s0.toShareholderCertType = formData.transfereeCertificateType;
        }
        if (topToNo && !String(s0.toShareholderCertNo || '').trim()) {
          s0.toShareholderCertNo = topToNo;
        }
      }
    }
    
    // 모든 섹션의 shareType 재설정 및 toShareholder 복원이 완료될 때까지 추가 대기
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // E-Filing 모드일 때 모든 패널 자동 펼치기
    if (props.eFilingMode) {
      if (Array.isArray(shareholderTransferSections.value)) {
        expandAll();
      }
    }
    
    // 데이터 복원 모드 종료 전 최종 대기 (watch가 실행되지 않도록)
    // 충분히 기다리고(flush) 마지막으로 watch들이 안정화될 시간을 확보
    await new Promise(resolve => setTimeout(resolve, 500));
    await nextTick();
    await nextTick();
    await nextTick();
    
    // 데이터 복원 모드 종료 (Allotment와 동일)
    isRestoringData.value = false;

    // 종료 직후에도 한 틱 대기 (동기적으로 연결된 watch 연쇄 방지)
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 100));
  }
};

// initialData props가 있으면 자동으로 데이터 설정
watch(() => props.initialData, async (newData) => {
  if (newData) {
    // newData.formData가 있으면 그것을 사용, 없으면 newData 자체를 formData로 사용
    const formDataToUse = newData.formData || newData;
    if (formDataToUse) {
      await setFormData(formDataToUse);
    }
  }
}, { immediate: true });

// E-Filing 모드일 때 모든 패널 자동 펼치기
watch(() => props.eFilingMode, (isEFilingMode) => {
  if (isEFilingMode) {
    // 약간의 지연을 두어 컴포넌트가 완전히 마운트된 후 실행
    nextTick(() => {
      // shareholderTransferSections가 배열인지 확인 후 expandAll 호출
      if (Array.isArray(shareholderTransferSections.value)) {
        expandAll();
      }
    });
  }
}, { immediate: true });

// E-Filing용 JSON 데이터 생성 함수 (async)
const getEFilingData = async () => {
  // 세션에서 Client 정보 가져오기
  const customerInfo = sessionStore.getCustomerInfo();
  const filingAgentUEN = customerInfo?.regNo || '';

  // Entity 정보 가져오기
  const selectedEntity = await getEntityInfo();
  const entityRegNoVal = selectedEntity?.entityRegNo || '';
  const entityNameVal = selectedEntity?.entityName || '';

  // ShareholderList 생성 (복수 Transfer 지원)
  const shareholderList: any[] = [];

  for (let i = 0; i < shareholderTransferSections.value.length; i++) {
    const section = shareholderTransferSections.value[i];
    if (!section.fromShareholder || !section.toShareholder) continue;

    const fromContactData = getFromShareholderContactData(i);
    const toContactData = getToShareholderContactData(i);
    if (!fromContactData || !toContactData) continue;

    // From Shareholder 정보
    // ID Type 코드명 (B03) - BIGO1에서 E-Filing용 name 가져오기
    const fromIdTypeItem = identificationTypeOptions.value.find(opt => opt.value === fromContactData.ID_TYPE_CD);
    let fromIdTypeName = fromIdTypeItem?.bigo1 || fromIdTypeItem?.title || '';
    if (!fromIdTypeName && fromContactData.ID_TYPE_CD) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', fromContactData.ID_TYPE_CD);
        fromIdTypeName = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
      } catch (e) { console.warn('From ID Type BIGO1 조회 실패:', e); }
    }

    // From Nationality 코드명 (B04) - BIGO1에서 E-Filing용 name 가져오기
    const fromNationalityItem = nationalityOptions.value.find(opt => opt.value === fromContactData.NATIONALITY_CD);
    let fromNationalityName = fromNationalityItem?.bigo1 || fromNationalityItem?.title || '';
    if (!fromNationalityName && fromContactData.NATIONALITY_CD) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', fromContactData.NATIONALITY_CD);
        fromNationalityName = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
      } catch (e) { console.warn('From Nationality BIGO1 조회 실패:', e); }
    }

    // From Category 코드명 (B09) - BIGO3에서 E-Filing용 name 가져오기
    let fromCategoryName = '';
    if (fromContactData.CATEGORY_CD) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', fromContactData.CATEGORY_CD);
        fromCategoryName = (response as any).code?.bigo3 || (response as any).data?.code?.bigo3 || '';
      } catch (e) { console.warn('From Category BIGO3 조회 실패:', e); }
    }

    // To Shareholder 정보
    // ID Type 코드명 (B03) - BIGO1에서 E-Filing용 name 가져오기
    const toIdTypeItem = identificationTypeOptions.value.find(opt => opt.value === toContactData.ID_TYPE_CD);
    let toIdTypeName = toIdTypeItem?.bigo1 || toIdTypeItem?.title || '';
    if (!toIdTypeName && toContactData.ID_TYPE_CD) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', toContactData.ID_TYPE_CD);
        toIdTypeName = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
      } catch (e) { console.warn('To ID Type BIGO1 조회 실패:', e); }
    }

    // To Nationality 코드명 (B04) - BIGO1에서 E-Filing용 name 가져오기
    const toNationalityItem = nationalityOptions.value.find(opt => opt.value === toContactData.NATIONALITY_CD);
    let toNationalityName = toNationalityItem?.bigo1 || toNationalityItem?.title || '';
    if (!toNationalityName && toContactData.NATIONALITY_CD) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', toContactData.NATIONALITY_CD);
        toNationalityName = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
      } catch (e) { console.warn('To Nationality BIGO1 조회 실패:', e); }
    }

    // To Category 코드명 (B09) - BIGO3에서 E-Filing용 name 가져오기
    let toCategoryName = '';
    if (toContactData.CATEGORY_CD) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', toContactData.CATEGORY_CD);
        toCategoryName = (response as any).code?.bigo3 || (response as any).data?.code?.bigo3 || '';
      } catch (e) { console.warn('To Category BIGO3 조회 실패:', e); }
    }

    // Currency 코드명 (B08)
    // - currencyName: 화면/EFiling용 (BIGO1만 사용, AllotmentForm과 동일)
    // - currencyNameForApi: BizFile용 CODE_NAME 값 (예: "United States of America dollar")
    // currencyOptions는 shareData에서 생성되므로 bigo1/bigo3가 없음
    // 따라서 항상 API로 조회해야 함
    const currencyValue = section.currency || '';
    const currencyCd = currencyValue?.includes('_') ? currencyValue.split('_')[0] : currencyValue;
    
    // API로 조회하여 totalCode, bigo1, bigo3, CODE_NAME 얻기
    let currencyCodeForCd = currencyCd; // currencyCd에 사용할 실제 코드 값 (totalCode)
    let currencyName = ''; // currency.name에 사용 (bigo1)
    let currencyAs = ''; // currencyAs에 사용 (bigo3)
    let currencyNameForApi = ''; // currencyName에 사용 (CODE_NAME)

    if (currencyCd) {
      try {
        // currencyCd가 bigo1 값일 수 있으므로, 모든 currency 코드를 조회해서 bigo1이 일치하는 것 찾기
        const allCurrencyResponse = await baseCodeApi.getCodesByMainCode('CM', 'B08', undefined);
        const allCurrencyCodes = (allCurrencyResponse as any).codes || allCurrencyResponse.data?.codes || [];
        const matchingCode = allCurrencyCodes.find((c: any) => 
          (c.bigo1 || c.BIGO1 || '').trim() === currencyCd
        );
        
        if (matchingCode) {
          const totalCode = (matchingCode.totalCode || matchingCode.TOTAL_CODE || '').trim();
          if (totalCode) {
            currencyCodeForCd = totalCode;
            // totalCode로 API 조회
            const response = await baseCodeApi.getCodeByTotalCode('CM', totalCode);
            const code = (response as any).code || (response as any).data?.code || {};
            
            const codeName = (code.codeName || code.CODE_NAME || '').trim();
            const bigo1 = (code.bigo1 || code.BIGO1 || '').trim();
            const bigo3 = (code.bigo3 || code.BIGO3 || '').trim();

            // currencyName은 bigo1만 사용 (AllotmentForm과 동일)
            currencyName = bigo1 || '';
            
            // currencyAs는 bigo3 사용
            currencyAs = bigo3 || '';
            
            // CurrencyName은 CODE_NAME만 사용
            currencyNameForApi = codeName;
          }
        } else {
          // bigo1로 찾지 못하면 currencyCd가 이미 totalCode일 수 있으므로 직접 조회
          const response = await baseCodeApi.getCodeByTotalCode('CM', currencyCd);
          const code = (response as any).code || (response as any).data?.code || {};
          
          if (code.totalCode || code.TOTAL_CODE) {
            currencyCodeForCd = (code.totalCode || code.TOTAL_CODE || currencyCd).trim();
          }
          
          const codeName = (code.codeName || code.CODE_NAME || '').trim();
          const bigo1 = (code.bigo1 || code.BIGO1 || '').trim();
          const bigo3 = (code.bigo3 || code.BIGO3 || '').trim();

          currencyName = bigo1 || '';
          currencyAs = bigo3 || '';
          currencyNameForApi = codeName;
        }
      } catch (e) {
        console.warn('Currency CODE_NAME/BIGO1 조회 실패:', e);
      }
    }

    // Class of Shares 코드명 (A20) - CODE_NAME에서 E-Filing용 name 가져오기
    const classOfSharesItem = allShareTypeOptions.value.find(opt => opt.value === section.shareType);
    let classOfSharesName = classOfSharesItem?.title || '';
    if (!classOfSharesName && section.shareType) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', section.shareType);
        classOfSharesName = (response as any).code?.codeName || (response as any).data?.code?.CODE_NAME || '';
      } catch (e) { console.warn('Class of Shares CODE_NAME 조회 실패:', e); }
    }

    // Mobile Country Code
    const mobileNoCountryCode = getCountryBigo3WithoutPlus(toContactData.COUNTRY_CD || '');

    // From CategoryType 결정 (B09001 = Corporate, B09002 = Individual)
    const fromCategoryType = fromContactData.CATEGORY_CD === 'B09001' ? 'LC' : 'IND';
    
    // To CategoryType 결정 (B09001 = Corporate, B09002 = Individual)
    const toCategoryType = toContactData.CATEGORY_CD === 'B09001' ? 'LC' : 'IND';

    // AddressType을 단일 문자로 변환 (B07001 = S, B07002 = F)
    const fromAddressTypeChar = (fromContactData.ADDRESS_TYPE_CD || 'B07001') === 'B07001' ? 'S' : 'F';
    const toAddressTypeChar = (toContactData.ADDRESS_TYPE_CD || 'B07001') === 'B07001' ? 'S' : 'F';
    const toContactAddressTypeChar = (toContactData.ALTERNATE_ADDRESS_CD || toContactData.ADDRESS_TYPE_CD || 'B07001') === 'B07001' ? 'S' : 'F';

    // Transaction Date 포맷팅
    const transactionDateFormatted = formatDateForEFiling(transactionDateValue.value || '');

    // SaveShareholderTransfer 구조 생성
    const saveShareholderTransfer = {
      // 기본 필드
      tranDetailIdx: 0, // 서버에서 생성
      
      // To Shareholder 정보
      certiNoTo: section.toShareholderCertNo || '',
      certiTypeTo: section.toShareholderCertType || '',
      category: {
        code: toContactData.CATEGORY_CD || '',
        name: toCategoryName
      },
      categoryType: toCategoryType,
      toUen: toContactData.CONTACT_REG_NO || '',
      transferTo: section.toShareholder,
      transferToName: toContactData.CONTACT_NAME || '',
      
      // To가 개인인 경우
      ...(toCategoryType === 'IND' ? {
        nationality: {
          code: toContactData.NATIONALITY_CD || '',
          name: toNationalityName
        },
        idType: {
          code: toContactData.ID_TYPE_CD || '',
          name: toIdTypeName
        },
        contactEmail: toContactData.CONTACT_EMAIL || '',
        countryCode: mobileNoCountryCode,
        mobileNo: toContactData.MOBILE_NO || '',
        birthDate: (toContactData.DATE_OF_BIRTH || '').split(' ')[0] || '',
        hasBirthDate: !!toContactData.DATE_OF_BIRTH
      } : {}),
      
      // To 주소 정보
      addressType: toAddressTypeChar,
      postalCode: toAddressTypeChar === 'S' ? (toContactData.POSTAL_CODE || '') : '',
      unitNo: toAddressTypeChar === 'S' ? (toContactData.ADDR_UNIT || '') : '',
      levelNo: toAddressTypeChar === 'S' ? (toContactData.ADDR_LEVEL || '') : '',
      blkhseNo: toAddressTypeChar === 'S' ? (toContactData.BLOCK_NO || '') : '',
      streetName: toAddressTypeChar === 'S' ? (toContactData.STREET_NAME || '') : '',
      buildingName: toAddressTypeChar === 'S' ? (toContactData.BUILDING_NAME || '') : '',
      foreignAddr1: toAddressTypeChar === 'F' ? (toContactData.FOREIGN_ADDRESS_LINE_1 || '') : '',
      foreignAddr2: toAddressTypeChar === 'F' ? (toContactData.FOREIGN_ADDRESS_LINE_2 || '') : '',
      
      // To Contact Address (Alternate Address) 정보
      contactAddressType: toContactAddressTypeChar,
      contactPostalCode: toContactAddressTypeChar === 'S' ? (toContactData.ALT_POSTAL_CODE || '') : '',
      contactUnitNo: toContactAddressTypeChar === 'S' ? (toContactData.ALT_ADDR_UNIT || '') : '',
      contactLevelNo: toContactAddressTypeChar === 'S' ? (toContactData.ALT_ADDR_LEVEL || '') : '',
      contactBlkhseNo: toContactAddressTypeChar === 'S' ? (toContactData.ALT_BLOCK_NO || '') : '',
      contactStreetName: toContactAddressTypeChar === 'S' ? (toContactData.ALT_STREET_NAME || '') : '',
      contactBuildingName: toContactAddressTypeChar === 'S' ? (toContactData.ALT_BUILDING_NAME || '') : '',
      contactForeignAddr1: toContactAddressTypeChar === 'F' ? (toContactData.ALT_FOREIGN_ADDRESS_LINE_1 || '') : '',
      contactForeignAddr2: toContactAddressTypeChar === 'F' ? (toContactData.ALT_FOREIGN_ADDRESS_LINE_2 || '') : '',
      
      // From Shareholder 정보
      certiNoFrom: section.fromShareholderCertNo || '',
      certiTypeFrom: section.fromShareholderCertType || '',
      fromCategory: {
        code: fromContactData.CATEGORY_CD || '',
        name: fromCategoryName
      },
      fromCategoryType: fromCategoryType,
      fromUen: fromContactData.CONTACT_REG_NO || '',
      fromIdType: fromCategoryType === 'IND' ? {
        code: fromContactData.ID_TYPE_CD || '',
        name: fromIdTypeName
      } : undefined,
      fromNationality: fromCategoryType === 'IND' ? {
        code: fromContactData.NATIONALITY_CD || '',
        name: fromNationalityName
      } : undefined,
      transferFrom: section.fromShareholder,
      transferFromName: fromContactData.CONTACT_NAME || '',
      
      // Currency 관련
      currency: {
        code: currencyCodeForCd || section.currency || '',
        name: currencyName
      },
      currencyCd: currencyCodeForCd || section.currency || '',
      currencyAs: currencyAs || getCurrencyAs(currencyCodeForCd || section.currency || ''),
      currencyName: currencyNameForApi || currencyName,
      
      // Class of Shares 관련
      classOfShares: {
        code: section.shareType || '',
        name: classOfSharesName
      },
      classOfSharesAs: getClassOfSharesAs(section.shareType || ''),
      
      // Transaction 관련
      transactionDate: transactionDateFormatted,
      
      // Shares 관련
      numberOfShares: section.toNumberOfShares || '',
      issuedShareCapital: section.toIssuedShareCapital || '',
      paidUpShareCapital: section.toPaidUpShareCapital || '',
      considerationPaid: section.totalConsiderationPaid || '',
      
      // From Shares 관련
      fromNumberOfShares: section.fromNumberOfShares || '',
      fromIssuedShareCapital:
        section.fromPaidUpShareCapital
        || section.totalConsiderationPaid
        || section.toIssuedShareCapital
        || section.toPaidUpShareCapital
        || '',
      fromPaidUpShareCapital:
        section.fromPaidUpShareCapital
        || section.totalConsiderationPaid
        || section.toPaidUpShareCapital
        || '',
      
      // Old Shares 관련 (필요시 추가)
      oldnumberOfShares: '',
      oldpaidUpShareCapital: '',
      
      // Shareholder Profile ID
      shareholderProfileIdFrom: fromContactData.PROFILE_ID || '',
      shareholderProfileId: toContactData.PROFILE_ID || '',
      
      // Effective Date (enteredMember 값 사용, 없으면 transactionDate 사용) - YYYY-MM-DD 형식
      effectiveDate: section.enteredMember 
        ? formatDateToYYYYMMDD(section.enteredMember) 
        : formatDateToYYYYMMDD(transactionDateValue.value || '')
    };

    shareholderList.push(saveShareholderTransfer);
  }

  // E-Filing Validation용 JSON 구조 ({ code, name } 형태)
  const eFilingDataValidation = {
    ShareholderList: shareholderList,
    EntityUEN: entityRegNoVal || '',
    FilingAgentUEN: filingAgentUEN,
    entityName: entityNameVal || ''
  };

  // E-Filing API용 JSON 구조 (name 값만)
  const eFilingDataForApi = transformCodeNameToValue(eFilingDataValidation);

  console.log('📋 [SharesTransferForm] getEFilingData:', {
    eFilingDataValidation,
    eFilingDataForApi,
    entityName: entityNameVal,
    shareholderListCount: shareholderList.length
  });

  return {
    eFilingDataValidation,
    eFilingDataForApi
  };
};

// 외부에서 접근 가능하도록 expose
defineExpose({
  entity,
  fromShareholder,
  fromNumberOfShares,
  fromPaidUpShareCapital,
  toShareholder,
  toNumberOfShares,
  toIssuedShareCapital,
  toPaidUpShareCapital,
  totalConsiderationPaid,
  shareType,
  transactionDate: transactionDateValue,
  transferorCertificateType,
  transferorCertificateNo,
  transfereeCertificateType,
  transfereeCertificateNo,
  // 추가 필드
  fromShareholderID,
  fromShareholderAddress,
  toShareholderID,
  toShareholderAddress,
  toShareholderNationality,
  toShareholderBirth,
  egmPlace,
  egmDate,
  egmTime,
  fromShareCertificateNo,
  toShareCertificateNo,
  chairPerson,
  representativeDirector,
  rorc,
  representative,
  sharesHeldInTrust,
  trustName,
  taskFlow,
  shareholderTransferSections,
  getFormData,
  getFormDataWithDocVariables,
  getEFilingData,
  transformCodeNameToValue,
  setFormData,
  validateAll
});
</script>

<template>
  <v-card
    v-bind="$attrs"
    variant="outlined"
    class="mb-6"
    style="border: 1px solid #e0e0e0"
  >
    <v-card-title class="text-h6 pa-4 pb-2">{{ t('startTask.Shares Transfer Details') || 'Shares Transfer Details' }}</v-card-title>
    <v-card-text class="pa-6 pt-2">
      <!-- Entity Field -->
      <div class="mb-6" style="max-width: 70%;">
        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
          {{ t('startTask.Entity') || 'Entity' }} <span class="text-error">*</span>
        </label>
        <v-autocomplete
          v-model="entity"
          :items="officerAppointmentStore.entityOptions"
          :placeholder="t('startTask.Search') || 'Search'"
          variant="outlined"
          density="compact"
          hide-details
          append-inner-icon="mdi-magnify"
          clearable
        ></v-autocomplete>
      </div>

      <!-- Transactions Date Field (공통) -->
      <div class="mb-6" style="max-width: 70%;">
        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
          {{ t('startTask.Transactions Date') || 'Transactions Date' }} <span class="text-error">*</span>
        </label>
        <v-menu
          v-model="transactionDateMenu"
          :close-on-content-click="false"
          location="bottom"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              v-bind="props"
              v-model="transactionDate"
              :placeholder="datePlaceholder"
              variant="outlined"
              density="compact"
              readonly
              prepend-inner-icon="mdi-calendar"
              data-field="transactionDate"
            ></v-text-field>
          </template>
          <v-locale-provider :locale="datePickerLocale">
            <v-date-picker
              v-model="transactionDatePickerValue"
              :first-day-of-week="firstDayOfWeek"
              @update:model-value="transactionDateMenu = false"
            ></v-date-picker>
          </v-locale-provider>
        </v-menu>
      </div>

      <!-- Task Flow Section (Entity 선택 후 표시, E-Filing 모드에서는 숨김) -->
      <TaskFlowSection
        v-if="entity && props.showTaskFlow"
        v-model="taskFlow"
        :is-all-supported="isAllSupported"
        :is-create-document-supported="isCreateDocumentSupported"
        :is-request-t-sign-supported="isRequestTSignSupported"
        :is-proceed-with-e-filing-supported="isProceedWithEFilingSupported"
        class="mb-6"
        style="max-width: 70%;"
      />

      <!-- Shareholder 및 Certificate Details, RORC Information Setting 섹션 (ControllerAppointmentForm과 동일한 패턴) -->
      <div v-if="entity" class="mb-6">
        <v-card variant="outlined" class="mb-4">
          <v-card-title class="d-flex align-center justify-space-between pa-4 pb-2">
            <div class="d-flex align-center">
              <h3 class="text-h6 ma-0">
                {{ t('startTask.Shares Transfer') || 'Shares Transfer' }}
              </h3>
            </div>
            <div class="d-flex" style="gap: 8px;">
              <a href="#" @click.prevent="expandAll" class="text-caption text-primary text-decoration-none">
                {{ t('startTask.Expand all') || 'Expand all' }}
              </a>
              <span class="text-caption">|</span>
              <a href="#" @click.prevent="collapseAll" class="text-caption text-primary text-decoration-none">
                {{ t('startTask.Collapse all') || 'Collapse all' }}
              </a>
            </div>
          </v-card-title>
          
          <v-card-text class="pa-6 pt-2">
            <v-expansion-panels
              v-model="allExpandedPanels"
              multiple
              variant="accordion"
            >
              <!-- 1. Shareholder 섹션들 (여러 개 가능) -->
              <v-expansion-panel
                v-for="(section, shareholderIndex) in shareholderTransferSections"
                :key="section.id"
                :value="section.id"
              >
                <v-expansion-panel-title class="controller-accordion-header">
                  <div class="d-flex align-center flex-wrap">
                    <span class="font-weight-bold text-white mr-2">
                      1. Shareholder {{ shareholderIndex + 1 }}
                    </span>
                    <span class="text-white text-caption" v-if="section.fromShareholder || section.toShareholder">
                      - {{ section.fromShareholder ? fromShareholderOptions.find(opt => opt.value === section.fromShareholder)?.title || '' : '' }}
                      {{ section.fromShareholder && section.toShareholder ? ' → ' : '' }}
                      {{ section.toShareholder ? toShareholderOptions.find(opt => opt.value === section.toShareholder)?.title || '' : '' }}
                    </span>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div class="pa-4">
                    <!-- Delete Shareholder Button -->
                    <div class="d-flex justify-end mb-4" v-if="shareholderTransferSections.length > 1">
                      <v-btn 
                        icon="mdi-delete" 
                        variant="text" 
                        size="small"
                        @click="deleteShareholderTransferSection(shareholderIndex)"
                      ></v-btn>
                    </div>
                    <!-- From (Transferor) Section -->
                    <div class="mb-8">
                      <h6 class="text-subtitle-1 font-weight-bold mb-4">{{ t('startTask.From') || 'From' }} (Transferor)</h6>
                      <div class="d-flex" style="gap: 12px;">
                        <v-autocomplete
                          v-model="section.fromShareholder"
                          :items="fromShareholderOptions"
                          item-title="title"
                          item-value="value"
                          :loading="isLoadingShareholders"
                          :placeholder="placeholderLabel"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                          clearable
                          style="flex: 1;"
                        ></v-autocomplete>
                        <v-btn
                          color="primary"
                          variant="outlined"
                          size="small"
                          @click="handleNewContact('from', shareholderIndex)"
                        >
                          {{ t('startTask.Contact') || 'Contact' }}
                        </v-btn>
                      </div>

                    <!-- ENTERED MEMBER -->
                    <div class="mb-6">
                      <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                        {{ t('startTask.ENTERED MEMBER') || 'ENTERED MEMBER' }}
                      </label>
                      <v-text-field
                        :model-value="section.enteredMember ? formatDocDate(section.enteredMember) : ''"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                        readonly
                        disabled
                      ></v-text-field>
                    </div>

                    <!-- Currency -->
                    <div class="mb-6">
                      <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                        {{ t('startTask.Currency') || 'Currency' }} <span class="text-error">*</span>
                      </label>
                      <v-autocomplete
                        v-model="section.currency"
                        :items="currencyOptions"
                        item-title="title"
                        item-value="value"
                        :placeholder="placeholderLabel"
                        variant="outlined"
                        density="comfortable"
                        :error-messages="getCurrencyErrorMessages(shareholderIndex)"
                        hide-details="auto"
                        clearable
                        :disabled="!section.fromShareholder"
                        data-field="currency"
                      ></v-autocomplete>
                    </div>

                    <!-- Share Type -->
                    <div class="mb-6">
                      <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                        {{ t('startTask.Share Type') || 'Share Type' }} <span class="text-error">*</span>
                      </label>
                      <v-autocomplete
                        :key="`share-type-${shareholderIndex}-${section.shareType || 'empty'}-${shareTypeOptions.length}`"
                        v-model="section.shareType"
                        :items="shareTypeOptions"
                        item-title="title"
                        item-value="value"
                        :placeholder="placeholderLabel"
                        variant="outlined"
                        density="comfortable"
                        :error-messages="getShareTypeErrorMessages(shareholderIndex).concat(section.subClassErrorMessage ? [section.subClassErrorMessage] : [])"
                        :error="!!getShareTypeErrorMessages(shareholderIndex).length || !!section.subClassErrorMessage"
                        hide-details="auto"
                        clearable
                        :disabled="!section.fromShareholder || !section.currency"
                        data-field="shareType"
                        @update:model-value="(value: any) => {
                          console.log('🔍 [Share Type v-autocomplete] update:model-value:', {
                            shareholderIndex,
                            value,
                            sectionShareType: section.shareType,
                            shareTypeOptions: shareTypeOptions.map(opt => ({ value: opt.value, title: opt.title })),
                            hasValueInOptions: shareTypeOptions.some(opt => String(opt.value).trim() === String(value).trim())
                          });
                        }"
                      ></v-autocomplete>
                    </div>

                    <!-- Number of Shares and Paid up share capital -->
                    <v-row class="ma-0 mb-6">
                      <v-col cols="12" sm="6" class="pa-0 pr-sm-3">
                        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                          {{ t('startTask.Number of Shares') || 'Number of Shares' }}
                        </label>
                        <v-text-field
                          v-model="section.fromNumberOfShares"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                          readonly
                          disabled
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" class="pa-0 pl-sm-3">
                        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                          {{ t('startTask.Paid up share capital') || 'Paid up share capital' }}
                        </label>
                        <v-text-field
                          v-model="section.fromPaidUpShareCapital"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                          readonly
                          disabled
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <!-- Contact Details (From Shareholder 선택 후 표시) -->
                    <template v-if="section.fromShareholder && getFromShareholderContactData(shareholderIndex)">
                      <div class="mb-6">
                        <!-- Corporate인 경우 -->
                        <template v-if="isCorporateContact(getFromShareholderContactData(shareholderIndex))">
                          <!-- Basic Corporate 섹션 -->
                          <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailType = 'from'; currentContactDetailIndex = shareholderIndex">
                            <v-card-title class="contact-section-title">
                              {{ t('contact.Basic') || 'Basic' }}
                            </v-card-title>
                            <v-card-text>
                              <v-row>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :model-value="getFromShareholderContactData(shareholderIndex)?.CONTACT_NAME"
                                    :label="t('contact.Entity Name') || 'Entity Name'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    class="readonly-field"
                                  />
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :model-value="getFromShareholderContactData(shareholderIndex)?.CONTACT_REG_NO"
                                    :label="t('contact.Registration Number') || 'Registration Number'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    class="readonly-field"
                                  />
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </template>

                        <!-- Individual인 경우 -->
                        <template v-else>
                          <!-- Basic 섹션 -->
                          <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailType = 'from'; currentContactDetailIndex = shareholderIndex">
                            <v-card-title class="contact-section-title">
                              {{ t('contact.Basic') || 'Basic' }}
                            </v-card-title>
                            <v-card-text>
                              <v-row>
                                <v-col cols="12">
                                  <v-text-field
                                    :model-value="getFromShareholderContactData(shareholderIndex)?.CONTACT_NAME"
                                    :label="t('contact.Name') || 'Name'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    class="readonly-field"
                                  />
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :model-value="getIdentificationTypeDisplay(getFromShareholderContactData(shareholderIndex)?.ID_TYPE_CD)"
                                    :label="t('contact.Identification Type') || 'Identification Type'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    class="readonly-field"
                                  />
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :model-value="getNationalityDisplay(getFromShareholderContactData(shareholderIndex)?.NATIONALITY_CD)"
                                    :label="t('contact.Nationality/Citizenship') || 'Nationality/Citizenship'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    class="readonly-field"
                                  />
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :model-value="getFromShareholderContactData(shareholderIndex)?.CONTACT_REG_NO"
                                    :label="t('contact.Identification No') || 'Identification No.'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    class="readonly-field"
                                  />
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :model-value="formatDateOfBirth(getFromShareholderContactData(shareholderIndex)?.DATE_OF_BIRTH)"
                                    :label="t('contact.Date of Birth') || 'Date of Birth'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    prepend-inner-icon="mdi-calendar"
                                    class="readonly-field"
                                  />
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </template>

                        <!-- Residential Address 섹션 -->
                        <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailType = 'from'; currentContactDetailIndex = shareholderIndex">
                          <v-card-title class="contact-section-title">
                            {{ t('contact.Residential Address') || 'Residential Address' }}
                          </v-card-title>
                          <v-card-text>
                            <v-row>
                              <v-col cols="12">
                                <v-text-field
                                  :model-value="getAddressTypeDisplay(getFromShareholderContactData(shareholderIndex)?.ADDRESS_TYPE_CD)"
                                  :label="t('contact.Address Type') || 'Address Type'"
                                  variant="outlined"
                                  density="comfortable"
                                  readonly
                                  hide-details
                                  class="readonly-field"
                                />
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col cols="12">
                                <v-textarea
                                  :model-value="formatShareholderAddress(getFromShareholderContactData(shareholderIndex))"
                                  :label="t('contact.Residential Address') || 'Residential Address'"
                                  variant="outlined"
                                  density="comfortable"
                                  readonly
                                  rows="3"
                                  hide-details
                                  class="readonly-field"
                                />
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>

                        <!-- Contact 섹션 -->
                        <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailType = 'from'; currentContactDetailIndex = shareholderIndex">
                          <v-card-title class="contact-section-title">
                            {{ t('contact.Contact') || 'Contact' }}
                          </v-card-title>
                          <v-card-text>
                            <v-row>
                              <v-col cols="12" md="4">
                                <v-text-field
                                  :model-value="getCountryCodeDisplay(getFromShareholderContactData(shareholderIndex)?.COUNTRY_CD)"
                                  :label="t('contact.Country Code') || 'Country Code'"
                                  variant="outlined"
                                  density="comfortable"
                                  readonly
                                  hide-details
                                  class="readonly-field"
                                />
                              </v-col>
                              <v-col cols="12" md="4">
                                <v-text-field
                                  :model-value="getFromShareholderContactData(shareholderIndex)?.AREA_CODE"
                                  :label="t('contact.Area Code') || 'Area Code'"
                                  variant="outlined"
                                  density="comfortable"
                                  readonly
                                  hide-details
                                  class="readonly-field"
                                />
                              </v-col>
                              <v-col cols="12" md="4">
                                <v-text-field
                                  :model-value="getFromShareholderContactData(shareholderIndex)?.MOBILE_NO"
                                  :label="t('contact.Mobile No') || 'Mobile No'"
                                  variant="outlined"
                                  density="comfortable"
                                  readonly
                                  hide-details
                                  class="readonly-field"
                                />
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col cols="12">
                                <v-text-field
                                  :model-value="getFromShareholderContactData(shareholderIndex)?.CONTACT_EMAIL"
                                  :label="t('contact.Email') || 'Email'"
                                  variant="outlined"
                                  density="comfortable"
                                  readonly
                                  hide-details
                                  class="readonly-field"
                                />
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </div>
                    </template>
                    </div>

                    <!-- 구분선 -->
                    <v-divider class="my-8"></v-divider>

                    <!-- To (Transferee) Section -->
                    <div class="mb-8">
                      <h6 class="text-subtitle-1 font-weight-bold mb-4">
                        {{ t('startTask.To') || 'To' }} (Transferee) <span class="text-error">*</span>
                      </h6>
                      <div class="mb-6">
                        <div class="d-flex" style="gap: 12px;">
                        <v-autocomplete
                          :ref="(el: any) => { if (el) toShareholderFieldRefs.set(shareholderIndex, el) }"
                          v-model="section.toShareholder"
                          :items="toShareholderOptions"
                          item-title="title"
                          item-value="value"
                          :loading="isLoadingShareholders"
                          :placeholder="placeholderLabel"
                          variant="outlined"
                          density="comfortable"
                          :error-messages="getToShareholderErrorMessages(shareholderIndex)"
                          :error="!!getToShareholderErrorMessages(shareholderIndex).length"
                          hide-details="auto"
                          style="flex: 1;"
                          clearable
                          :disabled="!section.fromNumberOfShares || section.fromNumberOfShares.trim() === ''"
                          data-field="toShareholder"
                        ></v-autocomplete>
                        <v-btn
                          color="primary"
                          variant="outlined"
                          size="small"
                          :disabled="!section.fromNumberOfShares || section.fromNumberOfShares.trim() === ''"
                          @click="handleNewContact('to', shareholderIndex)"
                        >
                          {{ t('startTask.Contact') || 'Contact' }}
                        </v-btn>
                        </div>
                      </div>

                    <!-- To Details - 2x2 Grid -->
                    <v-row class="ma-0 mb-6">
                      <!-- Top-left: Number of Shares -->
                      <v-col cols="12" sm="6" class="pa-0 pr-sm-3">
                        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                          {{ t('startTask.Number of Shares') || 'Number of Shares' }} <span class="text-error">*</span>
                        </label>
                        <v-text-field
                          ref="(el) => { if (el) toNumberOfSharesFieldRefs.set(shareholderIndex, el) }"
                          v-model="section.toNumberOfShares"
                          type="number"
                          variant="outlined"
                          density="comfortable"
                          :error-messages="getToNumberOfSharesErrorMessages(shareholderIndex)"
                          :error="!!getToNumberOfSharesErrorMessages(shareholderIndex).length"
                          hide-details="auto"
                          class="to-shares-input"
                          data-field="toNumberOfShares"
                        ></v-text-field>
                      </v-col>
                      <!-- Top-right: Issued share capital -->
                      <v-col cols="12" sm="6" class="pa-0 pl-sm-3">
                        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                          {{ t('startTask.Issued share capital') || 'Issued share capital' }} <span class="text-error">*</span>
                        </label>
                        <v-text-field
                          ref="(el) => { if (el) toIssuedShareCapitalFieldRefs.set(shareholderIndex, el) }"
                          v-model="section.toIssuedShareCapital"
                          type="number"
                          variant="outlined"
                          density="comfortable"
                          :error-messages="getToIssuedShareCapitalErrorMessages(shareholderIndex)"
                          :error="!!getToIssuedShareCapitalErrorMessages(shareholderIndex).length"
                          hide-details="auto"
                          data-field="toIssuedShareCapital"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row class="ma-0 mb-6">
                      <!-- Bottom-left: Paid up share capital -->
                      <v-col cols="12" sm="6" class="pa-0 pr-sm-3">
                        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                          {{ t('startTask.Paid up share capital') || 'Paid up share capital' }} <span class="text-error">*</span>
                        </label>
                        <v-text-field
                          ref="(el) => { if (el) toPaidUpShareCapitalFieldRefs.set(shareholderIndex, el) }"
                          v-model="section.toPaidUpShareCapital"
                          type="number"
                          variant="outlined"
                          density="comfortable"
                          :error-messages="getToPaidUpShareCapitalErrorMessages(shareholderIndex)"
                          :error="!!getToPaidUpShareCapitalErrorMessages(shareholderIndex).length"
                          hide-details="auto"
                          data-field="toPaidUpShareCapital"
                        ></v-text-field>
                      </v-col>
                      <!-- Bottom-right: Total Consideration Paid -->
                      <v-col cols="12" sm="6" class="pa-0 pl-sm-3">
                        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                          {{ t('startTask.Total Consideration Paid') || 'Total Consideration Paid' }} <span class="text-error">*</span>
                        </label>
                        <v-text-field
                          ref="(el) => { if (el) totalConsiderationPaidFieldRefs.set(shareholderIndex, el) }"
                          v-model="section.totalConsiderationPaid"
                          type="number"
                          variant="outlined"
                          density="comfortable"
                          :error-messages="getTotalConsiderationPaidErrorMessages(shareholderIndex)"
                          :error="!!getTotalConsiderationPaidErrorMessages(shareholderIndex).length"
                          hide-details="auto"
                          data-field="totalConsiderationPaid"
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <!-- Add Shares held in trust 버튼 -->
                    <div v-if="!section.showTrustSection" class="mb-6">
                      <v-btn
                        variant="text"
                        color="primary"
                        class="pa-0 text-none"
                        style="text-transform: none; font-weight: 400;"
                        @click="section.showTrustSection = true"
                      >
                        <v-icon start size="small">mdi-plus</v-icon>
                        {{ t('startTask.Add Shares held in trust') || 'Add Shares held in trust' }}
                      </v-btn>
                    </div>

                    <!-- Trust 관련 섹션 -->
                    <div v-if="section.showTrustSection" class="mb-6">
                      <label class="text-subtitle-2 font-weight-medium mb-3 d-block">
                        {{ t('startTask.Shares are held in Trust. Do you want to provide the name of the trust?') || 'Shares are held in Trust. Do you want to provide the name of the trust?' }}
                      </label>
                      <div class="d-flex align-center mb-4">
                        <v-radio-group
                          v-model="section.sharesHeldInTrust"
                          inline
                          hide-details
                          class="ma-0"
                        >
                          <v-radio
                            :label="t('startTask.Yes') || 'Yes'"
                            :value="true"
                          ></v-radio>
                          <v-radio
                            :label="t('startTask.No') || 'No'"
                            :value="false"
                            class="ml-4"
                          ></v-radio>
                        </v-radio-group>
                        <v-btn
                          icon="mdi-delete-outline"
                          variant="text"
                          size="small"
                          class="ml-2"
                          @click="section.showTrustSection = false; section.sharesHeldInTrust = null; section.trustName = ''"
                        ></v-btn>
                      </div>
                      <div v-if="section.sharesHeldInTrust === true" class="mt-4">
                        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                          {{ t('startTask.Trust name') || 'Trust name' }}
                        </label>
                        <v-text-field
                          v-model="section.trustName"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                        ></v-text-field>
                      </div>
                    </div>

                    <!-- Contact Details (To Shareholder 선택 후 표시) -->
                    <template v-if="section.toShareholder && getToShareholderContactData(shareholderIndex)">
                      <div class="mb-6">
                        <!-- Corporate인 경우 -->
                        <template v-if="isCorporateContact(getToShareholderContactData(shareholderIndex))">
                          <!-- Basic Corporate 섹션 -->
                          <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailType = 'to'; currentContactDetailIndex = shareholderIndex">
                            <v-card-title class="contact-section-title">
                              {{ t('contact.Basic') || 'Basic' }}
                            </v-card-title>
                            <v-card-text>
                              <v-row>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :model-value="getToShareholderContactData(shareholderIndex)?.CONTACT_NAME"
                                    :label="t('contact.Entity Name') || 'Entity Name'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    class="readonly-field"
                                  />
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :model-value="getToShareholderContactData(shareholderIndex)?.CONTACT_REG_NO"
                                    :label="t('contact.Registration Number') || 'Registration Number'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    class="readonly-field"
                                  />
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </template>

                        <!-- Individual인 경우 -->
                        <template v-else>
                          <!-- Basic 섹션 -->
                          <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailType = 'to'; currentContactDetailIndex = shareholderIndex">
                            <v-card-title class="contact-section-title">
                              {{ t('contact.Basic') || 'Basic' }}
                            </v-card-title>
                            <v-card-text>
                              <v-row>
                                <v-col cols="12">
                                  <v-text-field
                                    :model-value="getToShareholderContactData(shareholderIndex)?.CONTACT_NAME"
                                    :label="t('contact.Name') || 'Name'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    class="readonly-field"
                                  />
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :model-value="getIdentificationTypeDisplay(getToShareholderContactData(shareholderIndex)?.ID_TYPE_CD)"
                                    :label="t('contact.Identification Type') || 'Identification Type'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    class="readonly-field"
                                  />
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :model-value="getNationalityDisplay(getToShareholderContactData(shareholderIndex)?.NATIONALITY_CD)"
                                    :label="t('contact.Nationality/Citizenship') || 'Nationality/Citizenship'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    class="readonly-field"
                                  />
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :model-value="getToShareholderContactData(shareholderIndex)?.CONTACT_REG_NO"
                                    :label="t('contact.Identification No') || 'Identification No.'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    class="readonly-field"
                                  />
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    :model-value="formatDateOfBirth(getToShareholderContactData(shareholderIndex)?.DATE_OF_BIRTH)"
                                    :label="t('contact.Date of Birth') || 'Date of Birth'"
                                    variant="outlined"
                                    density="comfortable"
                                    readonly
                                    hide-details
                                    prepend-inner-icon="mdi-calendar"
                                    class="readonly-field"
                                  />
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </template>

                        <!-- Residential Address 섹션 -->
                        <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailType = 'to'; currentContactDetailIndex = shareholderIndex">
                          <v-card-title class="contact-section-title">
                            {{ t('contact.Residential Address') || 'Residential Address' }}
                          </v-card-title>
                          <v-card-text>
                            <v-row>
                              <v-col cols="12">
                                <v-text-field
                                  :model-value="getAddressTypeDisplay(getToShareholderContactData(shareholderIndex)?.ADDRESS_TYPE_CD)"
                                  :label="t('contact.Address Type') || 'Address Type'"
                                  variant="outlined"
                                  density="comfortable"
                                  readonly
                                  hide-details
                                  class="readonly-field"
                                />
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col cols="12">
                                <v-textarea
                                  :model-value="formatShareholderAddress(getToShareholderContactData(shareholderIndex))"
                                  :label="t('contact.Residential Address') || 'Residential Address'"
                                  variant="outlined"
                                  density="comfortable"
                                  readonly
                                  rows="3"
                                  hide-details
                                  class="readonly-field"
                                />
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>

                        <!-- Contact 섹션 -->
                        <v-card class="contact-section-card mb-4" @click="showContactDetailModal = true; currentContactDetailType = 'to'; currentContactDetailIndex = shareholderIndex">
                          <v-card-title class="contact-section-title">
                            {{ t('contact.Contact') || 'Contact' }}
                          </v-card-title>
                          <v-card-text>
                            <v-row>
                              <v-col cols="12" md="4">
                                <v-text-field
                                  :model-value="getCountryCodeDisplay(getToShareholderContactData(shareholderIndex)?.COUNTRY_CD)"
                                  :label="t('contact.Country Code') || 'Country Code'"
                                  variant="outlined"
                                  density="comfortable"
                                  readonly
                                  hide-details
                                  class="readonly-field"
                                />
                              </v-col>
                              <v-col cols="12" md="4">
                                <v-text-field
                                  :model-value="getToShareholderContactData(shareholderIndex)?.AREA_CODE"
                                  :label="t('contact.Area Code') || 'Area Code'"
                                  variant="outlined"
                                  density="comfortable"
                                  readonly
                                  hide-details
                                  class="readonly-field"
                                />
                              </v-col>
                              <v-col cols="12" md="4">
                                <v-text-field
                                  :model-value="getToShareholderContactData(shareholderIndex)?.MOBILE_NO"
                                  :label="t('contact.Mobile No') || 'Mobile No'"
                                  variant="outlined"
                                  density="comfortable"
                                  readonly
                                  hide-details
                                  class="readonly-field"
                                />
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col cols="12">
                                <v-text-field
                                  :model-value="getToShareholderContactData(shareholderIndex)?.CONTACT_EMAIL"
                                  :label="t('contact.Email') || 'Email'"
                                  variant="outlined"
                                  density="comfortable"
                                  readonly
                                  hide-details
                                  class="readonly-field"
                                />
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </div>
                    </template>
                    </div>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <!-- 2. Certificate Details 패널 (Transfer row별 Transferor / Transferee) -->
              <v-expansion-panel value="certificate-details">
                <v-expansion-panel-title class="controller-accordion-header">
                  <div class="d-flex align-center flex-wrap">
                    <span class="font-weight-bold text-white mr-2">
                      2. Certificate Details
                    </span>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div class="pa-4">
                    <div
                      v-for="(section, certIndex) in shareholderTransferSections"
                      :key="`cert-${section.id}`"
                      class="mb-6"
                    >
                      <div
                        v-if="shareholderTransferSections.length > 1"
                        class="text-subtitle-1 font-weight-bold mb-3"
                      >
                        Transfer #{{ certIndex + 1 }}
                        <span
                          v-if="section.fromShareholder || section.toShareholder"
                          class="text-medium-emphasis text-body-2 font-weight-regular"
                        >
                          —
                          {{ section.fromShareholder ? (fromShareholderOptions.find(opt => opt.value === section.fromShareholder)?.title || '') : '' }}
                          {{ section.fromShareholder && section.toShareholder ? ' → ' : '' }}
                          {{ section.toShareholder ? (toShareholderOptions.find(opt => opt.value === section.toShareholder)?.title || '') : '' }}
                        </span>
                      </div>

                      <!-- Transferor Section -->
                      <div class="mb-4">
                        <h6 class="text-subtitle-2 font-weight-medium mb-3">
                          {{ getSectionTransferorLabel(section, certIndex) }}
                        </h6>
                        <v-row class="ma-0 mb-6">
                          <v-col cols="12" md="6">
                            <v-autocomplete
                              v-model="section.fromShareholderCertType"
                              :items="certificateTypeOptionItems"
                              item-title="title"
                              item-value="value"
                              :label="t('entity.Certificate Type') || 'Certificate Type'"
                              :placeholder="placeholderLabel"
                              variant="outlined"
                              density="comfortable"
                              persistent-placeholder
                              hide-details
                              clearable
                            />
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="section.fromShareholderCertNo"
                              :label="t('entity.Certificate No') || 'Certificate No'"
                              type="text"
                              variant="outlined"
                              density="comfortable"
                              hide-details
                            />
                          </v-col>
                        </v-row>
                      </div>

                      <v-divider class="mb-6"></v-divider>

                      <!-- Transferee Section -->
                      <div class="mb-4">
                        <h6 class="text-subtitle-2 font-weight-medium mb-3">
                          {{ getSectionTransfereeLabel(section, certIndex) }}
                        </h6>
                        <v-row class="ma-0">
                          <v-col cols="12" md="6">
                            <v-autocomplete
                              v-model="section.toShareholderCertType"
                              :items="certificateTypeOptionItems"
                              item-title="title"
                              item-value="value"
                              :label="t('entity.Certificate Type') || 'Certificate Type'"
                              :placeholder="placeholderLabel"
                              variant="outlined"
                              density="comfortable"
                              persistent-placeholder
                              hide-details
                              clearable
                            />
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="section.toShareholderCertNo"
                              :label="t('entity.Certificate No') || 'Certificate No'"
                              type="text"
                              variant="outlined"
                              density="comfortable"
                              hide-details
                            />
                          </v-col>
                        </v-row>
                      </div>

                      <v-divider
                        v-if="certIndex < shareholderTransferSections.length - 1"
                        class="mt-6 mb-2"
                      ></v-divider>
                    </div>

                    <v-divider class="mb-4"></v-divider>
                    <v-row class="ma-0 mt-4">
                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="chairPerson"
                          :items="chairPersonOptions"
                          item-title="title"
                          item-value="value"
                          :label="t('entity.Chair Person') || 'Chair Person'"
                          variant="outlined"
                          density="comfortable"
                          :placeholder="placeholderLabel"
                          persistent-placeholder
                          hide-details
                          clearable
                        />
                      </v-col>
                    </v-row>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <!-- 3. RORC Information Setting 패널 -->
              <v-expansion-panel value="rorc-info">
                <v-expansion-panel-title class="controller-accordion-header">
                  <div class="d-flex align-center flex-wrap">
                    <span class="font-weight-bold text-white mr-2">
                      {{ t('startTask.RORC Information Setting') || '3. RORC Information Setting' }}
                    </span>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div class="pa-4">
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="representativeDirector"
                          :items="representativeDirectorOptions"
                          item-title="title"
                          item-value="value"
                          :label="t('startTask.Representative Director') || 'Representative Director'"
                          variant="outlined"
                          density="comfortable"
                          :placeholder="placeholderLabel"
                          persistent-placeholder
                          hide-details
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="rorc"
                          :items="rorcOptions"
                          item-title="title"
                          item-value="value"
                          :label="t('startTask.RORC') || 'RORC'"
                          variant="outlined"
                          density="comfortable"
                          :placeholder="placeholderLabel"
                          persistent-placeholder
                          hide-details
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="representative"
                          :items="representativeOptions"
                          item-title="title"
                          item-value="value"
                          :label="t('startTask.REPRESENTATIVE') || 'REPRESENTATIVE'"
                          variant="outlined"
                          density="comfortable"
                          :placeholder="placeholderLabel"
                          persistent-placeholder
                          hide-details
                          clearable
                          :disabled="!rorc"
                        />
                      </v-col>
                    </v-row>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>

            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </div>

      <!-- Bottom Actions: Add Shareholder Button -->
      <div class="d-flex justify-end mt-6" v-if="entity">
        <v-btn
          variant="outlined"
          prepend-icon="mdi-plus"
          @click="addShareholderTransferSection"
          style="border-style: dashed;"
        >
          + Add Shareholder
        </v-btn>
      </div>
    </v-card-text>
  </v-card>

  <!-- Contact List Modal -->
  <ContactListPage
    :is-modal="true"
    v-model="showContactListModal"
    :initial-search="contactListInitialSearch"
    :on-select="(contact) => handleContactSelect(contact)"
  />

  <!-- Contact Detail Modal -->
  <v-dialog
    v-model="showContactDetailModal"
    max-width="1400px"
    width="95%"
    persistent
  >
    <v-card rounded="md">
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <span class="text-h6">{{ t('contact.Contact Detail') || 'Contact Detail' }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="showContactDetailModal = false"
        />
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4" style="max-height: 80vh; overflow-y: auto;">
        <ContactDetailPage
          v-if="currentContactDetailType === 'from' && shareholderTransferSections[currentContactDetailIndex]?.fromShareholder"
          :contact-id="getFromShareholderContactCd(currentContactDetailIndex)"
          :is-modal="true"
          :on-select="handleContactDetailSelect"
          @select="handleContactDetailSelect"
          @updated="handleContactUpdated"
          @back="showContactDetailModal = false"
        />
        <ContactDetailPage
          v-else-if="currentContactDetailType === 'to' && shareholderTransferSections[currentContactDetailIndex]?.toShareholder"
          :contact-id="shareholderTransferSections[currentContactDetailIndex].toShareholder"
          :is-modal="true"
          :on-select="handleContactDetailSelect"
          @select="handleContactDetailSelect"
          @updated="handleContactUpdated"
          @back="showContactDetailModal = false"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
/* 폰트 패밀리 - Inter */
* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* 카드 타이틀 - 18px Bold */
:deep(.v-card-title) {
  font-size: 18px !important;
  font-weight: 700 !important;
  color: #3C4650;
}

/* 레이블 - 14px */
:deep(.v-label),
:deep(.v-field-label) {
  font-size: 14px !important;
  color: #3C4650;
}

/* 입력 필드 - 14px */
:deep(.v-field__input) {
  font-size: 14px !important;
  color: #3C4650;
}

/* placeholder - 14px #777E85 */
:deep(.v-field__input::placeholder),
:deep(input::placeholder) {
  font-size: 14px !important;
  color: #777E85 !important;
}

/* 버튼 - 14px */
:deep(.v-btn) {
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: none;
}

/* Primary 버튼 */
:deep(.v-btn--color-primary) {
  background-color: #1B84FF !important;
}

.text-error {
  color: rgb(var(--v-theme-error));
}

.required-asterisk {
  color: rgb(var(--v-theme-error)) !important;
  margin-left: 2px;
}

/* Controller Accordion Header 스타일 (SharesAllotmentForm과 동일) - 파란색 */
:deep(.controller-accordion-header) {
  background-color: #1B84FF !important;
  
  .v-expansion-panel-title {
    color: white !important;
  }
  
  .v-expansion-panel-title__icon {
    color: white !important;
  }
}

/* Contact Section Card 스타일 (SharesAllotmentForm과 동일) */
.contact-section-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e0e0e0;
}

.contact-section-card:hover {
  border-color: #1B84FF;
  box-shadow: 0 2px 8px rgba(27, 132, 255, 0.1);
}

.contact-section-title {
  background-color: #f5f5f5;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #3C4650 !important;
}

.contact-section-card :deep(.v-card-text) {
  padding: 16px !important;
}

.readonly-field {
  pointer-events: none;
}

.readonly-field :deep(.v-field) {
  background-color: #f9f9f9;
}

.readonly-field :deep(.v-field:hover) {
  background-color: #f5f5f5;
}

.readonly-field :deep(.v-field__input) {
  color: #3C4650;
  cursor: default;
}

.readonly-field :deep(.v-field--variant-outlined) {
  border-color: #e0e0e0;
}

.readonly-field :deep(.v-field--variant-outlined:hover) {
  border-color: #e0e0e0;
}

.readonly-field :deep(.v-field__overlay) {
  background-color: transparent;
}

.readonly-field :deep(input) {
  cursor: default;
  color: #3C4650;
}

.contact-section-card :deep(.v-field__overlay) {
  background-color: transparent;
}

.contact-section-card :deep(.v-field:hover .v-field__overlay) {
  background-color: rgba(27, 132, 255, 0.05);
}

/* To Number of Shares 에러 메시지 스타일 */
.to-shares-input :deep(.v-messages__message) {
  color: #f44336 !important;
  font-size: 14px !important;
  font-style: italic;
  line-height: 1.5;
}

.to-shares-input :deep(.v-field--error .v-field__outline) {
  border-color: #f44336 !important;
}

/* 에러 메시지 텍스트 강조 (이미지처럼) */
.to-shares-input :deep(.v-messages__message) {
  color: #ff6b35 !important; /* 오렌지색 계열 */
}
</style>
